export type ReleaseAudience = "admin" | "customer" | "system";
export type ReleaseConfidence = "verified" | "implemented" | "operational" | "reconstructed";

export type ReleaseEntry = {
  version: number;
  date: string;
  title: string;
  summary: string;
  confidence: ReleaseConfidence;
  audience: ReleaseAudience[];
  highlights: string[];
  details: Array<{
    title: string;
    items: string[];
  }>;
};

export const releaseNotes: ReleaseEntry[] = [
  {
    version: 191,
    date: "2026-04-24",
    title: "Optional Customer Billing Address Overrides Invoice PDF Buyer Address",
    summary: "Customer profiles now support a separate optional billing address, and invoice PDFs use that billing address automatically whenever it is fully filled. If the customer leaves it empty, the existing sender-address fallback continues unchanged.",
    confidence: "verified",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Customers can now save a dedicated billing street, postal code, city and country in their profile without affecting label sender settings.",
      "Invoice buyer snapshots and live invoice PDF rendering now prefer that billing address whenever it is complete, which keeps the PDF recipient block aligned with the saved invoice address.",
      "Admin customer support and manual invoice tools were updated to use the same billing-address fallback so invoice editing screens match the final PDF output."
    ],
    details: [
      {
        title: "Profile and Support Coverage",
        items: [
          "A new optional billing-address section was added to the customer profile and to the admin customer edit flow, with validation that requires all billing address fields together only when the customer chooses to use the override.",
          "When no billing address is saved, the existing sender address remains the single fallback so current customers do not need to change anything."
        ]
      },
      {
        title: "Invoice Resolution Consistency",
        items: [
          "Store-order invoices, admin-created invoices, credit-purchase invoices and invoice editor previews now share the same preferred-address logic, so the buyer block in the PDF and the admin preview use the same address resolution path.",
          "Legacy invoices still keep their saved buyer snapshots, while new invoice generation now persists the billing-address override whenever it exists on the customer profile."
        ]
      },
      {
        title: "Validation and Rollout",
        items: [
          "Targeted invoice buyer tests were expanded to cover billing-address precedence and sender fallback behavior.",
          "The change was prepared for production deployment in the same support thread so customers can start using the billing override immediately after rollout."
        ]
      }
    ]
  },
  {
    version: 190,
    date: "2026-04-22",
    title: "JTL eBay Orders Support Alternate Item Line Type",
    summary: "JTL writeback now handles the customer's eBay orders whose real product row is stored as `nType = 0` instead of the `nType = 1` shape seen in the first batch.",
    confidence: "verified",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Deliverable JTL order positions now include `nType = 0` and `nType = 1` product rows while still excluding service/shipping rows.",
      "This prevents valid eBay orders from stopping at `has no deliverable product positions` after Kenpost has already generated a label.",
      "The same-day affected labels were identified as eBay orders with no JTL package rows and no eBay shipping log yet."
    ],
    details: [
      {
        title: "Position Compatibility",
        items: [
          "The initial JTL dataset used `nType = 1` for shipped product rows, but later eBay orders from the same store used `nType = 0` for the real item and `nType = 2` for the service/shipping line.",
          "The writeback now accepts both product row variants and continues to filter out `nType = 2` rows such as `Sonstige` or shipping charges."
        ]
      }
    ]
  },
  {
    version: 189,
    date: "2026-04-22",
    title: "JTL Reprints Replace Voided Tracking",
    summary: "JTL tracking writeback now handles the void-and-reprint path for JTL orders. If a previous outbound shipment for the same order was voided in Kenpost, a new printed label can replace that old JTL package tracking number and queue the current tracking for eBay.",
    confidence: "verified",
    audience: ["customer", "admin", "system"],
    highlights: [
      "JTL orders with a voided previous Kenpost label can now replace the stale JTL package tracking number instead of failing with a different-tracking conflict.",
      "The replacement still only happens when the existing JTL package matches a voided sibling shipment for the same order, keeping normal duplicate-tracking protections intact.",
      "When the order is an eBay order, the replacement path queues the current tracking number in `ebay_queue_out` for the next JTL-eazyAuction sync."
    ],
    details: [
      {
        title: "Void and Reprint Safety",
        items: [
          "The sync now reads voided outbound sibling shipments, strips the local `VOID-...` marker, and allows replacement only when JTL has exactly that stale package tracking for the same order.",
          "The JTL package row and delivery-note shipping method are updated to match the new printed label before eBay queue rows are inserted or updated."
        ]
      },
      {
        title: "Live Data Correction",
        items: [
          "`AU-202604-46381` was queued for eBay because it was a pre-queue JTL eBay shipment.",
          "`AU-202604-46392` had a voided old tracking number in JTL; the JTL package was corrected to the current printed tracking number and then queued for eBay."
        ]
      }
    ]
  },
  {
    version: 188,
    date: "2026-04-22",
    title: "JTL Writeback Now Queues eBay Shipment Status",
    summary: "This follow-up closes the missing bridge between JTL package writeback and JTL's eBay outbound queue. When a JTL order is an eBay order, Kenpost now writes the tracking number to JTL and also queues the shipment status for the next JTL-eazyAuction/eBay sync.",
    confidence: "verified",
    audience: ["customer", "admin", "system"],
    highlights: [
      "JTL DB writeback now adds matching `ebay_queue_out` rows for JTL orders that originated from eBay.",
      "The eBay queue receives the eBay ItemID, TransactionID, seller user, tracking number and carrier code expected by the customer's JTL dataset.",
      "Carrier mapping now sends DHL labels as `DHL`, Warenpost labels as `Deutsche Post`, and DPD labels as `DPD` for the eBay outbound queue.",
      "The same-day customer data was inspected and showed that JTL package rows existed, but no eBay queue rows had been created for the affected orders."
    ],
    details: [
      {
        title: "JTL to eBay Queue",
        items: [
          "After creating or finding the JTL package row, the writeback now looks up `dbo.ebay_transaction` by `kBestellung` and inserts or updates `dbo.ebay_queue_out` with `nType = 1` for each eBay transaction tied to the JTL order.",
          "The queue operation runs in the same SQL transaction as the JTL writeback so newly created labels cannot be left in a half-synced state between JTL package data and eBay shipment-status handoff."
        ]
      },
      {
        title: "Operational Diagnosis",
        items: [
          "The reported JTL shipments were already fully written to `tLieferschein`/`tVersand` and marked as shipped in JTL, but their eBay transaction rows had no pending `ebay_queue_out` entries.",
          "The implementation keeps non-eBay JTL orders safe by treating missing `ebay_transaction` rows as a normal no-op while still completing the JTL writeback."
        ]
      },
      {
        title: "Release Tracking",
        items: [
          "The package version was advanced to `1.0.46`, and the change is recorded as `v188`.",
          "This release should be paired with a one-time backfill for already written same-day JTL shipments if the customer wants those existing orders queued for eBay as well."
        ]
      }
    ]
  },
  {
    version: 187,
    date: "2026-04-22",
    title: "JTL Writeback Uses the Kenpost Label Product",
    summary: "This follow-up tightens the new JTL tracking writeback so the package shipping method sent to JTL follows the Kenpost label product rather than blindly copying the order's older JTL shipping method.",
    confidence: "verified",
    audience: ["customer", "admin", "system"],
    highlights: [
      "JTL writeback now maps `Kleinpaket` labels to the customer's `DHL Kleinpaket` Versandart.",
      "DHL weight labels such as `2 kg` now map to `DHL Paket` in the JTL package row.",
      "The newly reported JTL shipments were written back and existing same-day package rows were corrected to match their Kenpost label products.",
      "The shipment screens continue to show the JTL source-platform sync badge once the refreshed frontend bundle is loaded."
    ],
    details: [
      {
        title: "Shipping Method Mapping",
        items: [
          "The automatic JTL DB writeback now reads the shipment's `labelProduct` relation and resolves the matching active JTL `tVersandart` before inserting the package row.",
          "If no label-product mapping can be resolved, the implementation still falls back to the JTL order's Versandart so unusual customer configurations continue to work."
        ]
      },
      {
        title: "Live Data Correction",
        items: [
          "The additional same-day orders `AU-202604-46390` and `AU-202604-46389` were manually written back to JTL, marked as synced in Kenpost, and verified as fully delivered in the JTL database.",
          "Same-day package rows whose Versandart was inherited from the order instead of the Kenpost label product were corrected so `Kleinpaket` rows show `DHL Kleinpaket` and `2 kg` rows show `DHL Paket`."
        ]
      },
      {
        title: "Release Tracking",
        items: [
          "This follow-up was verified with direct JTL SQL checks, a clean Gadget problem scan, a successful production build and a fresh production deployment.",
          "The package version was advanced to `1.0.45`, and the change is recorded as `v187`."
        ]
      }
    ]
  },
  {
    version: 186,
    date: "2026-04-22",
    title: "JTL Tracking Writeback Is Now Automated",
    summary: "This operational release adds JTL database tracking writeback to the normal label workflow. JTL shipments now create the matching delivery note, delivery-note positions and package tracking row directly in the customer JTL database, recalculate order delivery state, and surface the source-platform sync status on customer shipment screens.",
    confidence: "verified",
    audience: ["customer", "admin", "system"],
    highlights: [
      "JTL outbound labels now push tracking numbers back to the connected JTL database when the store-level source tracking sync setting is enabled.",
      "The JTL writeback path uses JTL's delivery-note stored procedures, then inserts the package tracking row and recalculates order eckdaten so Wawi sees the order as shipped.",
      "Manual source-platform sync now supports JTL alongside Amazon and eBay, with idempotent handling when the same tracking number already exists.",
      "Customer shipment list and detail screens now treat JTL as a source-platform tracking candidate and show the same sent, pending or failed sync badge pattern."
    ],
    details: [
      {
        title: "JTL Database Writeback",
        items: [
          "The implementation reuses the verified database flow: resolve the JTL order by `kAuftrag` and order number, guard against existing different package rows, create `tLieferschein` through `Versand.spLieferscheinErstellen`, create product positions through `Versand.spLieferscheinPosErstellen`, insert `tVersand`, and run `Verkauf.spAuftragEckdatenBerechnen`.",
          "The package status and weight are aligned with the live JTL dataset used during verification, and the path is safe to rerun for the same tracking number without creating duplicate packages."
        ]
      },
      {
        title: "Manual Repair and UI Status",
        items: [
          "The reported JTL shipments for orders `AU-202604-46386`, `AU-202604-46391`, `AU-202604-46380`, `AU-202604-46376`, `AU-202604-46372`, `AU-202604-46370`, `AU-202604-46369` and `AU-202604-46371` were written back and verified against the JTL database before deployment.",
          "Their Kenpost shipment records were marked as source-platform sync success so the customer shipment UI can show that the tracking has already been sent to JTL."
        ]
      },
      {
        title: "Release Tracking",
        items: [
          "This work was verified with direct production JTL SQL checks, targeted TypeScript checks for the changed API path, web/API typecheck scans with known unrelated existing errors, and a fresh Gadget problem check before deployment.",
          "The package version was advanced to `1.0.44`, and the change is recorded as `v186` before the requested production deployment."
        ]
      }
    ]
  },
  {
    version: 185,
    date: "2026-04-21",
    title: "Bulk Label Creation UI Polish Is Published in the Latest Version",
    summary: "This customer-facing release surfaces the completed bulk label creation UI polish in the latest version history: the selected-orders action, bulk review modal, localized labels, compact editable shipment rows and clearer confirm footer are now recorded together as the current customer-visible bulk label improvement.",
    confidence: "verified",
    audience: ["customer"],
    highlights: [
      "The customer orders page now presents the bulk label action with readable localized copy instead of raw translation keys.",
      "The bulk label review modal uses a wider, denser operational layout with clearer summary status, credit information and editable shipment rows.",
      "The missing label-product field translation and ready-shipment footer summary are included in the documented release so the polished workflow appears in the latest version notes.",
      "This release keeps the existing bulk label generation behavior unchanged while documenting the visual and copy improvements as the current visible product update."
    ],
    details: [
      {
        title: "Customer Bulk Label Workflow",
        items: [
          "The selected-orders bar, create-bulk-label action and review dialog copy now stay readable across English, Turkish and German customer sessions.",
          "The review step was tightened into a wider fixed-header modal with compact rows, smaller controls, green ready-state accents and a clearer bottom confirmation area."
        ]
      },
      {
        title: "Release Tracking",
        items: [
          "This entry keeps the bulk label UI polish visible as the latest customer-facing version after another parallel release advanced the history.",
          "The package version was advanced to `1.0.43`, and the change is recorded as `v185` before the requested production deployment."
        ]
      }
    ]
  },
  {
    version: 184,
    date: "2026-04-21",
    title: "Shipment Labels Now Print Platform SKU Lines Under Tracking Numbers",
    summary: "This customer-facing release adds product SKU visibility directly onto generated DHL shipment labels. Labels created for marketplace orders now resolve SKU data from the platform order cache, print it under the tracking number in the existing label information block, and keep the line visually aligned with the original DHL typography while safely handling multiple or long SKU values.",
    confidence: "verified",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Shipment labels now include a `SKU:` line below the tracking number when SKU data is available for the order, making warehouse checks easier without changing the existing address, reference number or barcode layout.",
      "The SKU resolver was expanded beyond Amazon and now reads known product SKU fields from the platform order JSON used by Shopify, Billbee, Amazon, eBay, WooCommerce, JTL, Kaufland and the other supported cached order shapes.",
      "Both first label creation and regenerate or download-repair flows pass the resolved SKU text into the internal label generator, so customer testing through `Yeniden Olustur` uses the same output path as newly created labels.",
      "The label overlay was refined to use the template's existing font resource, size and character spacing, align with the left shipment-info column, and keep multiple or long SKUs on one safe truncated line instead of spilling into the weight or quantity area."
    ],
    details: [
      {
        title: "Platform SKU Resolution",
        items: [
          "A shared shipment-SKU resolver now looks up the store relation, platform and cached S3 order JSON before building the label text, replacing the earlier Amazon-only lookup while preserving the existing order-cache path used by the customer order pages.",
          "Platform-specific SKU extraction covers common item arrays and fields such as Shopify `line_items[].sku`, Billbee `OrderItems[].Sku` or `Product.SKU`, Amazon and Kaufland `SellerSKU`, Otto `sellerSku`, AboutYou `variant_sku`, eBay line-item SKU, WooCommerce line-item SKU, JTL article numbers and OpenCart product SKU or model values."
        ]
      },
      {
        title: "Label Placement and Regeneration",
        items: [
          "The SKU line is drawn below `Sendungsnr.` in the shipment information block and now reuses the template text styling instead of embedding a visually separate Helvetica overlay, so the new row fits more naturally with `Abrechnungsnr.`, `Referenznr.` and `Sendungsnr.`.",
          "The same SKU text is passed through initial label creation, shipment regeneration, pending-label recovery and customer PDF download repair paths so old shipments can be retested quickly by regenerating the label without changing the rest of the shipment workflow."
        ]
      },
      {
        title: "Long SKU Safety, Verification and Release Tracking",
        items: [
          "When several SKU values exist, they are deduplicated and joined with commas; if the resulting line is too long, the label generator first reduces the font size and then truncates with `...` so the text remains on a single safe row.",
          "This work was verified with targeted manual-label and platform SKU tests, TypeScript `--noEmit`, clean Gadget problem checks and production deploys during the support pass, and it is now recorded as `v184` while the package version was advanced to `1.0.42` before the requested deployment."
        ]
      }
    ]
  },
  {
    version: 183,
    date: "2026-04-21",
    title: "Bulk Label Review Got a Denser Operational Polish",
    summary: "This customer-facing follow-up refines the bulk label creation review one more step after the first cleanup: the missing label-product translation is now filled in, the review modal opens wider, selected shipments are displayed in a denser row layout with green operational accents, and the final action area now clearly shows how many shipments are ready before confirming bulk label creation.",
    confidence: "verified",
    audience: ["customer"],
    highlights: [
      "The raw `customer.orders.labelProduct` fallback that appeared in the bulk label review form was replaced with localized English, Turkish and German copy.",
      "The bulk label review modal now uses a wider canvas, tighter summary blocks and more compact shipment rows so selected orders are easier to scan in larger batches.",
      "Each selected shipment now has a green left-side status accent, smaller form controls and cleaner spacing to make the workflow feel closer to a shipping operations screen.",
      "The sticky footer now includes a ready-shipment summary beside the confirm action, making the final bulk-label creation step clearer before labels are generated."
    ],
    details: [
      {
        title: "Review Layout",
        items: [
          "The modal width was increased and the header, summary cards, scrolling list and footer spacing were tightened so the customer can review more order information without the form feeling crowded.",
          "Shipment panels now use compact metadata, smaller inputs and select controls, plus a left green accent for ready rows and a red accent when row-level validation needs attention."
        ]
      },
      {
        title: "Localization and Action Copy",
        items: [
          "The missing bulk review `labelProduct` message was added to all supported customer locales so the live UI no longer exposes an internal i18n key.",
          "A localized footer summary was added to show the number of shipments ready for bulk label creation before the customer confirms the batch."
        ]
      },
      {
        title: "Scope and Verification",
        items: [
          "This release remains visual and copy-focused; the existing validation, credit checks, ZIP handling and bulk shipment creation behavior were intentionally left unchanged.",
          "The work was verified with a successful production build, a clean Gadget problem check and a fresh deployment, and the package version was advanced to `1.0.41` while this change is recorded as `v183`."
        ]
      }
    ]
  },
  {
    version: 182,
    date: "2026-04-21",
    title: "Customer Bulk Label Review Was Cleaned Up and Localized",
    summary: "This customer-facing release refreshes the bulk label creation surface on the orders page: the selected-orders action bar now shows proper localized copy instead of a raw translation key, the bulk label review dialog is wider and easier to scan, and each selected order is shown as a clean editable row so recipient details, address fields and label products no longer visually collide before labels are created.",
    confidence: "verified",
    audience: ["customer"],
    highlights: [
      "The bulk action button now reads as a real create-bulk-labels action in English, Turkish and German instead of falling back to `customer.orders.reviewCreateLabels`.",
      "The selected-orders bar was restyled into a clearer green action strip with count, hint text and explicit actions for clearing selection, retrying the last ZIP download and creating labels.",
      "The bulk review popup was expanded into a larger fixed-header workflow with summary metrics, credit status and separate editable order panels to reduce crowded text and overlapping fields.",
      "The progress dialog and bulk review labels were localized across the supported customer languages while keeping the existing label creation and ZIP download behavior unchanged."
    ],
    details: [
      {
        title: "Bulk Action Bar",
        items: [
          "Missing customer order translation keys were added for the bulk label controls so the app no longer exposes raw i18n paths in the live UI.",
          "The selected-order bar now uses the same operational language as the action it triggers, with a compact count, a helper line and a primary `Create bulk labels` action that stays legible in Turkish and German."
        ]
      },
      {
        title: "Bulk Review Dialog",
        items: [
          "The preview dialog now opens wider with a stable header, summary blocks and a dedicated scrolling order list so large batches can be reviewed without the controls squeezing into each other.",
          "Each order preview is now presented as its own editable panel with clearer metadata, full-width product selection and responsive address fields, matching the cleaner bulk-shipping review style requested during the support pass."
        ]
      },
      {
        title: "Scope and Verification",
        items: [
          "The change is intentionally visual and copy-focused; duplicate checks, credit validation, address blocking, bulk shipment creation and ZIP download behavior continue to use the existing actions.",
          "The package version was advanced to `1.0.40` and this change is recorded as `v182` before the requested production deployment."
        ]
      }
    ]
  },
  {
    version: 181,
    date: "2026-04-21",
    title: "Invoice Recipient Recovery, Credit-PDF Fresh Downloads and Legacy Zero-Price Repair Were Completed End to End",
    summary: "This support release records the full invoice troubleshooting thread completed across the current conversation: the repository was audited locally before changes began, order and credit invoice PDFs were repaired so they no longer fall back to a generic `Customer` recipient when real buyer data exists, stale cached credit-invoice downloads were replaced with fresh render output plus safer storage-key versioning, and legacy zero-priced manual credit rows with active customer-specific pricing are now repaired in production while true free bonuses remain explicitly locked free.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The support pass started with a full local project review plus baseline builds and targeted tests, then moved into repeated live invoice checks so each later PDF and pricing fix could be validated against real production records instead of only mocked assumptions.",
      "Order invoices, manual invoices and credit-purchase PDFs now ignore legacy placeholder buyer values such as `Customer`, recover recipient identity from live customer profiles and label-source relationships, and print the real recipient block with name, street, postal code, city, country and tax id whenever that data exists.",
      "Customer and admin credit-invoice downloads no longer depend on stale stored PDF artifacts alone, because the download action now serves freshly generated bytes and versioned storage keys while invoice tables also recover sparse product names more reliably.",
      "Legacy zero-priced manual bonus allocations that should have been billed with customer-specific pricing are now repriced automatically, while intentionally free future bonus allocations are protected by an explicit pricing-lock marker and a five-minute repair sweep keeps similar historical mistakes from lingering."
    ],
    details: [
      {
        title: "Repository Audit and Invoice Buyer Recovery",
        items: [
          "The conversation began with a local project audit covering the route structure, data model and build health so the invoice bugs could be traced from a known-good baseline instead of patching blindly inside a large codebase.",
          "From there, invoice buyer resolution was hardened across store-order invoices, admin-created invoices and credit-purchase invoices so legacy snapshot values like `Customer` are treated as placeholders rather than trusted recipient data when better live customer information is available.",
          "Buyer fallback now prioritizes real company or sender override data, full customer names and live profile fields recovered from user records or assigned label sources, which means existing invoices can recover real recipient names and addresses instead of staying stuck on generic placeholders."
        ]
      },
      {
        title: "Fresh PDF Delivery and Product Visibility",
        items: [
          "When production checks showed that customer-facing credit invoice downloads could still return an older broken artifact even after buyer-resolution fixes were deployed, the download flow was changed to return freshly rendered PDF bytes directly and to rotate the underlying storage keys so stale cached objects stop masking the new output.",
          "The same support thread also repaired the credit-invoice product display chain on both admin and customer-facing screens, adding safer fallbacks from sparse nested relations back to serialized product ids, names and carrier metadata so legacy rows no longer show a blank product column.",
          "Production verification during this thread confirmed that affected PDFs such as the reported `MAK2026-000133` path could be re-rendered with the real recipient block and that the invoice screens stayed aligned with the resolved product metadata instead of hiding which label product had been sold."
        ]
      },
      {
        title: "Legacy Zero-Price Repair, Scheduler and Release Tracking",
        items: [
          "The later pricing investigation confirmed that several historical manual credit rows were still marked as bonus allocations and therefore remained at `0.00` even after active customer-specific pricing existed, including the customer's reported rows `300` and `301` and a few other legacy production rows with the same pattern.",
          "To fix that safely, true free bonuses now carry an explicit `bonusPricingLocked:true` note marker, while legacy unlocked `bonusAllocation:true` rows with an active positive customer price are eligible for repricing; a dedicated repair action was also added on a five-minute scheduler so future historical cleanup happens automatically in the background.",
          "This work was verified with targeted invoice, repricing and repair tests, successful production builds, clean Gadget problem checks, a production backfill that updated the remaining affected rows, and it is now recorded as `v181` while the package version was advanced to `1.0.39` before the deploy requested at the end of this conversation."
        ]
      }
    ]
  },
  {
    version: 180,
    date: "2026-04-21",
    title: "Customer Label Handoffs Were Verified, Packaged and Published Through Permanent App Download Links",
    summary: "This support release records the full Oznatır label-handoff thread completed across the current conversation: historical and same-day customer label allocations were verified directly in production, the requested label PDFs were exported locally outside the repo and packaged into ZIP archives, and those archives were then uploaded back through Kenpost's S3-backed label-inventory history flow so the customer could receive stable `app.kenpost.de/dl/...` links instead of one-time raw storage URLs.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Production checks confirmed multiple real customer `128` handoff batches across the conversation, including `1000` labels on April 14, 2026, a later `79`-label follow-up window on the same day, and an additional `81`-label assignment on April 17, 2026.",
      "When the in-app ZIP generation flow kept restarting during a `1000`-label export, the labels were recovered safely through a manual fallback path that downloaded the PDFs locally under `~/Downloads/kenpost-label-exports` without writing export output into the project workspace.",
      "The exported archives were uploaded back into the same `label-inventory-exports/user-128/...` and `label-inventory-export-jobs/user-128/...` structure used by Kenpost's label inventory history, and the resulting `/dl/...` share links were verified with live `302` redirects.",
      "A later April 18, 2026 allocation for the same customer was verified as four separate `250`-label manual purchases, and each part was exported, zipped, uploaded and published as its own permanent application download link."
    ],
    details: [
      {
        title: "Production Verification of Customer Label Batches",
        items: [
          "Direct production checks confirmed that customer `128` (`info@oznatur.de`) received `1000` `Kleinpaket` labels on April 14, 2026 at `17:02:52 CEST`, matching purchase `302` and confirming that the original handoff request was grounded in one real assignment batch rather than several smaller unrelated allocations.",
          "The same support thread later isolated a five-minute April 14, 2026 follow-up window and confirmed exactly `79` newly assigned labels at `20:31:18 CEST`, so the requested incremental export could be separated cleanly from the earlier `1000`-label handoff.",
          "A later last-2-to-3-days review also confirmed an April 17, 2026 batch of `81` newly assigned labels at `18:54:57 CEST`, tied to purchase `331`, so the conversation kept a verified production trail of when fresh label stock had been assigned to the same customer."
        ]
      },
      {
        title: "Fallback ZIP Export and Permanent Archive Publishing",
        items: [
          "Because the customer-side export dialog was observed looping for roughly forty-five minutes instead of finishing the archive, the requested label PDFs were downloaded manually outside the repo, zipped locally and stored under `~/Downloads/kenpost-label-exports`, which kept the recovery output isolated from the project workspace while still producing the needed customer deliverables.",
          "Those locally built archives were then uploaded using the same S3-backed manifest pattern that the application's label inventory history already understands, including a stored ZIP under `label-inventory-exports/user-128/...` plus a matching manifest under `label-inventory-export-jobs/user-128/...`.",
          "The resulting customer-facing links were published as `https://app.kenpost.de/dl/...` URLs and verified live with `HTTP 302` responses, while the short-lived element remained only the internal S3 redirect generated at click time rather than the shared application link itself."
        ]
      },
      {
        title: "Four-Part April 18 Delivery, Release Tracking and Deploy",
        items: [
          "On April 18, 2026, production purchase checks verified four manual `Kleinpaket` allocations for customer `128`, purchases `335`, `336`, `337` and `338`, each quantity `250`, created between `12:23:39 CEST` and `12:25:19 CEST`, for a total of `1000` same-day labels.",
          "Those four purchases were exported as four separate local folders and four separate ZIP archives, after which each archive was uploaded with its own manifest, job id and permanent `/dl/...` link so the customer could download `part-1` through `part-4` independently instead of relying on one oversized combined archive.",
          "This operational support thread is now recorded as `v180`, the package version was advanced to `1.0.38`, and a fresh production deploy was requested after updating the documented version history so the live version badge stays aligned with the handled export workflow."
        ]
      }
    ]
  },
  {
    version: 179,
    date: "2026-04-21",
    title: "Amazon Shopify-Source Orders, Labels and Tracking Sync Were Reworked Across Customer Operations",
    summary: "This support release records the Amazon-focused work completed across the current conversation: Shopify-source Amazon orders were verified end to end, customer orders now show Amazon-facing identifiers and richer item metadata instead of Shopify-only values, the orders and shipments screens gained safer Amazon-specific filtering and relation fallbacks, and manual as well as automatic tracking handoff back to the Amazon source Shopify flow was repaired after live GraphQL and legacy-order matching issues surfaced during testing.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Amazon-source Shopify orders were confirmed as the active import path, and the customer order list was updated to prefer the real Amazon order id, show date plus time, expose SKU and ASIN when available, and support Amazon-specific sync ranges plus an `Only unshipped` filter.",
      "The customer orders page no longer loses created labels after refresh for legacy Amazon records, because current-page relation loading and shipment external-order resolution now fall back to `orderNumber` as well as `externalOrderId` when reconnecting shipments to visible order rows.",
      "Amazon shipment tracking handoff back to the source Shopify order gained a manual customer action, store-level enable or disable control, and later live repairs after legacy orders first reported `Order is not an Amazon order` and then Shopify GraphQL rejected the earlier fulfillment shape.",
      "Additional Amazon support follow-ups also covered order detail routing, Shopify-note based Amazon id extraction, SKU and ASIN enrichment from product detail, and Amazon order filtering or sorting improvements needed to make the customer portal workable with larger live order volumes."
    ],
    details: [
      {
        title: "Amazon Order Visibility and Customer List Improvements",
        items: [
          "The conversation first verified that Amazon orders are currently imported from a dedicated Shopify source store with separate credentials, filtered by Amazon store tags and cached into S3 as compressed JSON, rather than being pulled live from Amazon's own API on every customer page load.",
          "Customer order rows were then reworked so Amazon-facing order ids replace Shopify sequence numbers in the main list, detail routing was aligned to use the same identifier logic, dates were expanded to show time as well as day, and later Amazon-specific controls were added for `7 days`, `30 days` and `all` sync ranges plus a top-level `Only unshipped` checkbox.",
          "Item previews were also expanded so the popup and detail flows can surface SKU and ASIN when the source order itself does not carry those fields directly, including enrichment from Shopify product or variant detail where needed."
        ]
      },
      {
        title: "Shipment Relation Recovery and Amazon Tracking Sync",
        items: [
          "When live testing showed that created labels disappeared from the customer orders page after refresh and `Create Label` reappeared, the page-level relation loader was repaired so shipments can be reattached through store-level and `orderNumber`-based fallbacks instead of depending only on one exact `externalOrderId` path.",
          "The shipments screen then gained an Amazon-specific manual action to send tracking back to the source platform while still hiding Shopify implementation details from the customer, and the same work reused the store-level `shopifyFulfillmentSyncEnabled` control so Amazon stores default to automatic tracking pushback unless the customer disables it.",
          "Production-style testing surfaced two Amazon sync failures during the same thread, first `Order is not an Amazon order` on legacy records and later a Shopify GraphQL shape error around `Fulfillment.nodes`; both were repaired by resolving source-platform and store fallback data more defensively and by making the fulfillment query tolerate the actual Shopify response shape used by the connected Amazon source store."
        ]
      },
      {
        title: "Verification, Versioning and Deploy",
        items: [
          "Throughout the thread, every major Amazon support change was followed by local verification such as TypeScript `--noEmit`, production builds, Gadget problem scans and repeated deploys so live customer testing could continue immediately after each fix.",
          "This release note consolidates the full Amazon order and shipment support thread into one documented product-history entry instead of scattering it across ad hoc support replies, while keeping the existing release-note schema and audience structure unchanged.",
          "The version history was advanced to `v179`, the package version was bumped from `1.0.36` to `1.0.37`, and a fresh deploy was requested after updating the version file so the Versions screen stays aligned with the Amazon work completed in this conversation."
        ]
      }
    ]
  },
  {
    version: 177,
    date: "2026-04-21",
    title: "Admin Customer Label Allocation Timeouts Were Investigated, Audited and Hardened for Large Batches",
    summary: "This support release records the full admin customer label-allocation thread completed across the current conversation: a live `1000` label allocation on the customer credits screen was traced to a transaction timeout during per-row `labelSource` updates, production checks confirmed that failed run did not leave partial customer assignments behind, and the allocation plus unassign flows were then hardened to use chunked bulk updates so large manual stock transfers no longer depend on one thousand individual record mutations inside the same transaction.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Production logs for customer `128` showed that the failed `1000` label assignment stopped inside `adminAllocateLabelStock` when Gadget timed out on `internal.updateLabelSource`, so the issue was confirmed as a transaction-scale write problem rather than missing stock or a broken balance view.",
      "The failed `1000` attempt did not remain in an ambiguous state: the orphan candidate purchase id was not present afterwards, no labels were linked to that failed purchase, and the later four `250` allocations for `part-1` through `part-4` were all verified as completed with the customer's balance reaching `2368`.",
      "A seven-day production log audit found only this single `adminAllocateLabelStock` timeout signature in the live environment, while the many other timeout entries belonged to the separate Shopify billing sync action and were explicitly ruled out as unrelated to manual admin label assignment.",
      "The allocation and unassign code paths now share a dedicated bulk label-source update helper that uses Gadget `bulkUpdate` in safe chunks with a per-record fallback, which reduces transaction pressure for large admin stock movements."
    ],
    details: [
      {
        title: "Live Timeout Investigation and Data-State Verification",
        items: [
          "The reported error on `/admin/customers/128?tab=credits` was traced directly in production logs to `adminAllocateLabelStock`, where a `1000` quantity run created purchase `334` and then failed with `GGT_TRANSACTION_TIMEOUT` while executing `internal.updateLabelSource` inside the same transaction.",
          "Follow-up production checks confirmed that this failed attempt did not silently assign labels anyway: no surviving purchase row was visible for `334`, no label sources were linked to that purchase id, and the failed transaction therefore did not leave customer stock in an uncertain partially-written state.",
          "The later manual retries were verified separately in production, where purchases `335`, `336`, `337` and `338` each completed with quantity `250`, and their cumulative balance progression reached `1618`, `1868`, `2118` and finally `2368` for customer `128`."
        ]
      },
      {
        title: "Historical Audit of Similar Errors",
        items: [
          "To determine whether this was a repeat operational problem, the last seven days of production logs were exported and filtered specifically for `adminAllocateLabelStock`, transaction-timeout signatures and related GraphQL rollback failures.",
          "That audit found one matching admin manual-allocation timeout in the seven-day window, and it was the same live incident from this conversation rather than an older repeated failure of the same screen and action.",
          "The same audit also surfaced many timeout events for `adminSyncShopifyBillingPurchase`, but those were documented as a different action path so they were explicitly separated from the admin customer credits allocation issue to avoid conflating unrelated production failures."
        ]
      },
      {
        title: "Bulk Update Hardening, Verification and Release Tracking",
        items: [
          "A shared `labelSourceBulkUpdate` helper was added so both admin allocation and admin unassign flows can submit chunked Gadget `bulkUpdate` operations instead of issuing hundreds or thousands of individual `labelSource.update` calls inside one transaction.",
          "The new helper was covered with targeted Node tests for both the bulk-update path and the fallback per-record path, while direct module import checks confirmed the touched allocation files still load cleanly after the refactor.",
          "Repo-wide TypeScript `--noEmit` still reports unrelated pre-existing workspace errors outside this support change, but this release note records the scoped timeout repair as `v177`, the package version was advanced to `1.0.35`, and a fresh production deploy was requested after updating the documented version history."
        ]
      }
    ]
  },
  {
    version: 176,
    date: "2026-04-21",
    title: "Amazon Shopify Imports and Admin Customer Loading Were Repaired and Stabilized in Production",
    summary: "This support release records the full investigation and repair thread completed in the current conversation: Amazon orders coming through Shopify or Salestio now match stores more reliably during refresh, the `/admin/customers` page was rewritten to stop scanning unrelated customer relations across the whole dataset, and the later production follow-ups fixed the cursor and Gadget filter-depth failures that were still keeping the admin customer list intermittently empty after the first rollout.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Amazon-source Shopify refreshes no longer depend only on delayed order tags, because store matching now also falls back to Salestio note content and note attributes when identifying which Kenpost store should receive the imported order.",
      "The admin customer list no longer loads stores, purchases, shipments, balances and sources for every customer in the system on every page open; it now scopes relation loading to the visible page of customer ids and records timing breakdowns for later tuning.",
      "Production troubleshooting uncovered two additional live-only failure modes on `/admin/customers`: missing cursor fields in paginated relation queries and Gadget's maximum nested-filter depth, and both were repaired before the second production deploy.",
      "After the fixes, the admin customers page recovered in production, while the remaining measured relation phase shows the screen is usable but still heavier than ideal for future performance work."
    ],
    details: [
      {
        title: "Admin Customer Page Loading and Timing Visibility",
        items: [
          "The original `/admin/customers` implementation was pulling large related datasets such as stores, label purchases, shipments, balances and label sources far beyond the currently visible customer page, which made the screen feel random, slow and occasionally unresponsive even with a relatively small live customer count.",
          "That action was rewritten so customer loading, filtering and pagination happen first, and the follow-up relation queries only run for the ids shown on the current page instead of traversing unrelated records for the full customer base.",
          "Timing output was also added around the admin customer action so later production checks could distinguish whether the base customer list or the relation aggregation stage was responsible for any remaining latency."
        ]
      },
      {
        title: "Production Crash Follow-Ups and Query Repairs",
        items: [
          "The first production pass showed that the page could still fail after deploy because some paginated relation queries sorted by cursor fields that were not always selected, which left the live error logs too vague until the error serialization was tightened.",
          "After that deploy, fresh production logs exposed a second concrete root cause, `GGT_INVALID_FILTER: Filtering is limited to a maximum depth of 2`, which meant some nested Gadget filters inside the admin customer summaries had to be flattened into direct `userId`, `storeId`, `orderId` and `labelPurchaseId` driven lookups.",
          "Those query repairs were pushed through a second production deploy, and later checks confirmed that `/admin/customers` was loading again in production, with backend timings around the mid-1-second range for the full visible customer list instead of the earlier failing behavior."
        ]
      },
      {
        title: "Amazon Shopify-Source Order Matching, Verification and Release Tracking",
        items: [
          "The Amazon refresh investigation showed that new marketplace orders visible inside the connected Shopify or Salestio source were sometimes skipped by Kenpost because the import path only trusted Shopify order tags, while the real store identity could still be present only in the order note or note attributes during the first sync attempts.",
          "Store matching for Shopify-source Amazon imports was expanded so the sync logic now checks tags first and then falls back to note text plus note attributes, while the sync logs also record whether a match happened by tag, note or note attribute for later production tracing.",
          "This work was verified with targeted Amazon sync and admin customer tests, repeated successful production builds and the production repairs completed during this conversation, and it is now recorded as `v176` while the package version was advanced to `1.0.34` before the final deploy requested in this thread."
        ]
      }
    ]
  },
  {
    version: 175,
    date: "2026-04-21",
    title: "Amazon Order Timing, Regenerate Controls and Address Cleanup Were Hardened Across Customer Shipping Flows",
    summary: "This support release records the full customer-shipping thread completed in the current conversation: Amazon-source orders now use the real Shopify or Salestio sales time instead of the later sync timestamp, shipment regeneration now pauses behind an editable preflight dialog with locked product and weight rules, and the label sanitation layer was expanded so phone-like Amazon address contamination plus punctuation-heavy recipient or street input no longer degrade generated labels.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Amazon-source Shopify orders now resolve their visible purchase date from `processed_at`, Salestio note attributes or the `Ship by` window start, so the customer order list shows when the order was actually sold instead of when Kenpost synced it later.",
      "Customer regenerate flows no longer fire immediately from the action menu: both orders and shipments now open a pre-regenerate dialog where operators can edit address data, use quick German address suggestions and then continue with the existing shipment product locked in place.",
      "Address cleanup rules were tightened so Amazon phone numbers stop leaking into recipient street lines, punctuation separators such as commas or stray dots are removed from recipient names, and malformed German street endings like `Kattenstraß` are repaired before label output.",
      "The shipments page also gained multi-select PDF downloading, allowing operators to queue several label files in one action instead of opening each shipment menu one by one."
    ],
    details: [
      {
        title: "Amazon Source Order Dates and Address Recovery",
        items: [
          "The reported Amazon-source Shopify orders were showing Kenpost sync time in the customer order list instead of the original marketplace sales time, which made orders that actually arrived on April 16 or April 19 appear as if they were created only after the later Salestio import on April 20, 2026.",
          "Amazon purchase-date resolution was rewritten so the customer order list now prefers Shopify `processed_at`, then Salestio note attributes such as `Verkaufszeit`, and finally the beginning of the `Ship by` window when that is the only reliable timestamp left on the imported order.",
          "The same Amazon follow-up also hardened label address recovery: when the imported street line is phone-like and the second line contains the real street, the real street is promoted into `address1`, and if no valid street exists the phone-like line is cleared so recipient phone numbers do not get printed onto shipping labels."
        ]
      },
      {
        title: "Regenerate Dialogs and Locked Product Rules",
        items: [
          "Customer shipment regeneration no longer starts instantly from `Gönderiler > Yeniden Oluştur`; instead the flow now opens a dedicated dialog where the operator can review and edit recipient and address fields before any label is generated again.",
          "That regenerate dialog now mirrors the create-label helper experience more closely by including quick German address search suggestions, so support users can select a suggested address and let street, city, postal code and country update automatically before resubmitting the shipment.",
          "Regeneration is now intentionally restricted to the original label product and weight: the UI keeps the current product visible but disabled, the orders dialog no longer offers other products during regenerate mode, and the backend rejects any direct `labelProductId` override so a different kg or product always requires canceling the old shipment and creating a new one."
        ]
      },
      {
        title: "Shipment Download UX, Sanitization and Release Tracking",
        items: [
          "The shipments list now supports selecting multiple ready rows and triggering one `Download selected PDFs` action, which queues each chosen label as its own browser download and removes the need to open the row action menu for every shipment during bulk printing work.",
          "Recipient and street sanitation rules were expanded in the shared label path so separator punctuation such as `Pietzsch, Eberhard` or `Hartmut . Devant` is normalized before label rendering, while German street-plus-house-number normalization now repairs inputs like `Kattenstraß 66` into `Kattenstraße 66` and keeps the house number visually attached on the final label.",
          "This work was verified with the targeted Amazon Shopify-source and address-rules test suites, a successful production build, a clean `ggt problems` run, and is now recorded as `v175` while the package version was advanced to `1.0.33` before the final deploy from this conversation."
        ]
      }
    ]
  },
  {
    version: 174,
    date: "2026-04-21",
    title: "Store-Level Online Seller Label Controls Were Added and Then Hardened for Regenerated Shipments",
    summary: "This release records the full Online Seller label-prefix thread completed in the current conversation: customer stores now have a dedicated setting that controls whether `Online Seller` appears on generated labels, that setting defaults to enabled for both existing and new stores so current operations keep working unchanged, and the later production follow-up closed the regenerate edge case for older shipment records that were still falling back to the old always-on behavior.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Customer store settings now include a dedicated `Show Online Seller on labels` toggle so one store can disable the address prefix without changing the default behavior for everyone else.",
      "The Online Seller rule is no longer treated as a fixed label-generation constant, because create, regenerate, manual, bulk-manual, recovery and download flows now resolve the same store-level setting before composing label parties.",
      "A later production fix also repaired regenerated shipment labels for older orders by passing `storeId` from the customer shipments screen and adding a user-plus-platform fallback when legacy records do not carry a direct store relation."
    ],
    details: [
      {
        title: "Store Settings and Safe Default Behavior",
        items: [
          "A new store-level boolean field was introduced for the Online Seller label prefix and exposed on the customer store edit page directly below the existing marketplace tracking sync control, so the setting lives where operators already manage store behavior.",
          "That setting defaults to enabled for existing stores, newly created stores and the supported store-connection creation paths, which preserves the current live label format unless an operator intentionally turns the prefix off for one specific store.",
          "The implementation was deliberately written with safe fallbacks so missing or older store records still behave as enabled, protecting the existing production workflow instead of making label output depend on a fragile migration state."
        ]
      },
      {
        title: "Label Generation Paths Were Unified Around One Toggle",
        items: [
          "The Online Seller logic was pulled out of its earlier hard-coded label rules and routed through shared helpers that can resolve the store preference consistently across standard shipment creation and related support flows.",
          "Manual label helpers were updated so sender and customer party text omit the `Online Seller` prefix when the store setting is disabled, and the fallback naming used for manual labels now switches to more neutral `Sender` and `Customer` labels instead of forcing the previous prefix back in.",
          "This same behavior was wired into order-based shipment creation, manual bulk shipment creation, pending shipment recovery and customer download paths so the setting applies to the real operational surfaces where labels are created, re-created and fetched."
        ]
      },
      {
        title: "Regenerate Follow-Up, Verification and Release Tracking",
        items: [
          "During live production testing, one store-specific regenerate scenario still produced `Online Seller` on the label because the customer shipments page was not passing `storeId` into the regenerate request and some older order records did not have a direct store relation stored on them.",
          "That follow-up was repaired by sending `storeId` from the customer shipments regenerate action and by extending the backend resolver so it can still recover the correct store preference from the owning user and source platform when older legacy data is incomplete.",
          "The work was revalidated with the targeted manual-label test suite, a fresh production build and Gadget problem checks, then recorded as `v174` while the package version was advanced to `1.0.32` before the production deploy."
        ]
      }
    ]
  },
  {
    version: 173,
    date: "2026-04-21",
    title: "Marketplace Tracking Regenerate Flows Now Skip Already-Synced Shipments",
    summary: "This support release records the full marketplace-tracking follow-up completed in the current conversation: a live eBay shipment that had already been sent on April 13, 2026 was manually repaired after label regeneration wrongly marked it as unsent, and the regenerate or resend tracking paths were then hardened so shipments that already completed source-platform sync are no longer sent again during later label regeneration attempts.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The live eBay shipment `2707` for order `23-14464-89786` was corrected in production so its source-platform tracking state now reflects the real already-synced outcome instead of the misleading `eBay not sent` error.",
      "eBay, Amazon-source and Shopify fulfillment sync paths now treat an already-successful shipment sync as completed work, which prevents regenerate or manual resend flows from trying to push the same tracking number again.",
      "The safeguard was deployed to production immediately after the repair so later label-regeneration tests do not recreate the same false failure state for already-fulfilled marketplace orders."
    ],
    details: [
      {
        title: "Live eBay Shipment State Was Repaired",
        items: [
          "The reported shipment at `/customer/shipments/2707` was investigated against production data and confirmed to be an outbound eBay shipment for order `23-14464-89786` with tracking number `689870914215` that had originally been created on April 13, 2026.",
          "The customer's later label-regeneration test reused that same tracking number, and because the order was already fulfilled in eBay the sync layer marked the shipment as failed with the message `Order is already fulfilled in eBay, so this tracking could not be sent.` even though the tracking had already been written back successfully before.",
          "That live row was repaired manually so the shipment now stores `sourcePlatformTrackingSyncStatus = success`, the message `Tracking already exists in eBay.`, and the synced timestamp aligned back to the original shipment creation time."
        ]
      },
      {
        title: "Regenerate and Resend Paths Were Made Idempotent",
        items: [
          "A new shared tracking-sync helper was introduced so fulfillment integrations can recognize when a shipment already has a completed marketplace sync state before making any new external request.",
          "The eBay fulfillment path now exits early when `sourcePlatformTrackingSyncStatus` is already `success`, which stops regenerate or manual resend flows from reopening the same tracking sync after the shipment was already accepted earlier.",
          "The same completed-state guard was applied to the Amazon source Shopify fulfillment flow and the Shopify fulfillment regenerate path so the broader marketplace tracking layer behaves consistently instead of depending on each external platform to reject duplicate updates."
        ]
      },
      {
        title: "Verification, Release Tracking and Deploy",
        items: [
          "The change was verified with targeted tests covering the new completed-state helper together with the existing eBay fulfillment tests, while the wider TypeScript check was noted to still contain unrelated pre-existing workspace errors outside this support fix.",
          "After the live production row was corrected, the scoped fulfillment changes were pushed through Gadget and a fresh production deploy completed successfully so the release history and the runtime behavior stay aligned.",
          "This work is now recorded as `v173`, and the release note intentionally captures both the production shipment repair and the later idempotent marketplace-sync safeguard from this same conversation."
        ]
      }
    ]
  },
  {
    version: 171,
    date: "2026-04-21",
    title: "Admin Customer Credits Now Support Bulk Label Allocation with Stable Weight Ordering",
    summary: "This release records the full admin customer-credits follow-up completed in the current conversation: the old single-product manual allocation flow was replaced with a bulk allocation table on the admin customer credits tab, the backend now accepts one batched admin allocation request for multiple label products at once, and the later support pass fixed both the weight-based product ordering and the missing admin permission that caused the new allocation flow to fail in production.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Admin users can now enter quantities for multiple label products in one table and allocate them together from the customer credits screen instead of repeating the old select-and-submit flow one product at a time.",
      "The bulk allocation view now follows the expected operational order, with `Kleinpaket` first and the remaining label products sorted by increasing weight in both the allocation list and the current-balance table.",
      "The production `GGT_PERMISSION_DENIED` error was traced to missing access control for the new bulk action and repaired so admins can execute the new flow successfully."
    ],
    details: [
      {
        title: "Bulk Label Allocation Was Added to the Admin Customer Credits Screen",
        items: [
          "The earlier admin credits card relied on a single dropdown where support staff had to pick one label product, submit it, then repeat the same workflow for every additional weight class they wanted to assign to the customer.",
          "That screen was replaced with a bulk allocation table that lists all available label products directly, shows stock next to each one and lets the admin enter quantities per row before creating all requested labels in one submission.",
          "The same card keeps the existing notes and bonus-allocation controls, but the operator can now complete one customer stock assignment pass in a single action instead of a tiring sequence of repeated form submissions."
        ]
      },
      {
        title: "Batched Action Handling and Weight Sorting Were Hardened",
        items: [
          "A new admin bulk allocation action was added on the Gadget side so the credits screen can submit one batched payload containing multiple label-product quantities, while still reusing the existing allocation and balance helpers behind the scenes.",
          "After the first rollout, operational feedback showed that the visible product order felt wrong because `5 kg` was appearing between heavier entries, so the label-product ordering was rewritten to sort by real weight with `Kleinpaket` first and the remaining products ascending by kilogram.",
          "That same sorting logic was applied to both the new bulk allocation list and the `Current Balances` table so the admin customer page presents one stable mental model instead of two different product orders on the same screen."
        ]
      },
      {
        title: "Permission Repair, Verification and Release Tracking",
        items: [
          "When the new bulk allocation flow was exercised against a live customer, it returned a `[GraphQL] GGT_PERMISSION_DENIED` error even though the UI was visible, which narrowed the failure down to access control on the new action rather than the form itself.",
          "The admin role permissions were updated to allow the new bulk allocation action, the scoped changes were rebuilt and pushed through Gadget, and a fresh production deploy completed after the permission and sorting fixes were added.",
          "This work is now recorded as `v171`, the package version was advanced to `1.0.30`, and the release note intentionally captures the full admin bulk-allocation thread from this conversation, including the later production permission and ordering follow-ups."
        ]
      }
    ]
  },
  {
    version: 170,
    date: "2026-04-21",
    title: "Customer Team Management Was Added and Then Stabilized Across Shared Account Workflows",
    summary: "This release records the full customer-team-management thread completed in the current conversation: customer accounts can now add and manage internal team members from the customer portal, admin customer detail pages expose those team members directly for support visibility, and the later support passes hardened how sub-users inherit the main customer account's operational scope for orders, balances, sender-profile checks and related day-to-day shipping flows.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Customer accounts now have a dedicated `Team Management` area where they can create sub-users with unique email addresses, set passwords, reset passwords later and disable login access without deleting the account.",
      "Customer team members now see a restricted portal surface that hides billing, affiliate, buy-label and broader account-management areas while still allowing the operational screens they were meant to use.",
      "The support follow-up also hardened team-member ownership repair, admin-side team visibility and owner-scoped order, balance and sender-profile flows after live production testing exposed several sub-user edge cases."
    ],
    details: [
      {
        title: "Customer Team Management and Admin Visibility",
        items: [
          "The customer portal gained a new `Team Management` section placed in the account navigation so customers can onboard internal team members without leaving their own workspace.",
          "That flow now supports unique email validation, initial password setup, later password resets and login deactivation for team members, while intentionally avoiding destructive account deletion from the customer side.",
          "Admin customer detail pages were also extended with a dedicated `Team Members` view so support can see which sub-users belong to a customer account directly from `/admin/customers/:id` without logging in as the customer."
        ]
      },
      {
        title: "Restricted Sub-User Access and Shared Account Scope",
        items: [
          "Team-member navigation and route access were narrowed to the operational customer screens the original request called for: dashboard, stores visibility without destructive actions, orders, manual label creation, shipments, label inventory and a limited personal profile area.",
          "Sensitive or owner-only areas such as invoices, affiliate, buy-label flows and broader account-management surfaces were removed from the team-member experience so sub-users operate as staff members rather than second full customer accounts.",
          "Later support passes also repaired how team members attach back to their owning customer account, including fallback ownership recovery and owner-scoped handling for balances, sender-profile requirements and related customer-account checks that should read shared company data instead of duplicate sub-user records."
        ]
      },
      {
        title: "Stabilization Follow-Ups, Error Handling and Release Tracking",
        items: [
          "The post-rollout support work in this same thread improved customer-facing error handling, reduced raw GraphQL-style failures in team-management flows and added friendlier recovery messaging around duplicate emails, missing team links and inactive-account states.",
          "Order and store ownership handling for team members was also revisited during live production testing so sub-users could open customer screens more reliably after the first rollout revealed connection and permission edge cases.",
          "This work is now recorded as `v170`, the package version was advanced to `1.0.29`, and a fresh production deploy was requested after the release-note update so the live version history matches the work documented from this conversation."
        ]
      }
    ]
  },
  {
    version: 169,
    date: "2026-04-21",
    title: "Customer Order Credits, Unshipped Filtering and First-Load Store Access Were Stabilized",
    summary: "This support release records the full customer-orders follow-up completed in the current conversation: label credits now resolve to the correct products again after the sub-user regression, the orders list can filter down to only still-unshipped rows across supported store types, and the orders page no longer fires its first store request before the customer's accessible store scope has been validated.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Customer credit balances no longer collapse into an `Unknown` bucket that leaves the sidebar and orders page showing `0` even when usable label stock still exists.",
      "The `Only unshipped` checkbox now works on the customer orders screen beyond the earlier Amazon-specific behavior and correctly treats open processing-style states as still unshipped.",
      "The orders page now waits for the visible-store list to resolve before its first fetch, preventing the transient `You don't have permission to view this store.` error or empty first render that could previously disappear after pressing `Refresh`."
    ],
    details: [
      {
        title: "Credit Resolution Was Repaired After the Sub-User Regression",
        items: [
          "The label-credit incident in this thread was traced back to relation-style `labelProductId` values being treated like plain strings, which collapsed real product balances into one broken `Unknown` group.",
          "Customer balance actions, credit summary handling and related recovery logic were corrected so the orders page, create-label flow and sidebar credit widget now resolve against the actual label product ids again.",
          "This means customers once again see the same real remaining stock in operational screens where they need to create labels, instead of being blocked by false zero-credit states while inventory still exists."
        ]
      },
      {
        title: "Orders Filtering Was Extended for Real Customer Workflows",
        items: [
          "The customer requested the earlier Amazon-style `Only unshipped` workflow on the general orders page so already-sent orders can be hidden during daily label operations.",
          "That filter is now available directly from the orders screen and no longer depends on an Amazon-only UI condition, which means Billbee and other supported store views can use the same focused queue behavior.",
          "Backend fulfillment filtering was also hardened so `processing`, `ordered`, `confirmed`, `pending` and similar open states remain visible under the filter, while shipped, fulfilled, delivered, cancelled and returned rows are excluded."
        ]
      },
      {
        title: "First-Load Store Validation, Verification and Release Tracking",
        items: [
          "The later support follow-up showed that some users still hit a first-load permission toast or a blank orders table until they manually clicked `Refresh`, which pointed to the orders route fetching too early with an as-yet-unvalidated store selection from URL or local storage.",
          "The orders screen now waits until the selected store is confirmed inside the customer's accessible active-store list before it fires the initial load, so the page no longer opens by briefly requesting a store the current client context has not finished validating.",
          "This release was verified with targeted Node tests for credit balance handling, fulfillment-status filtering and store-selection gating plus repeated TypeScript `--noEmit` checks, the package version was advanced to `1.0.28`, and the final production deploy was completed after the first-load orders fix was added to the same support thread."
        ]
      }
    ]
  },
  {
    version: 168,
    date: "2026-04-21",
    title: "Customer Team Access, Order Detail Stability and Profile Restrictions Were Aligned",
    summary: "This support release records the full customer-portal follow-up completed in the current conversation: team members now inherit the account owner's operational data where the portal should act on the shared company account, order detail loading was repaired across both customer and team-member sessions after multiple live failure paths were uncovered, customer-facing dates were aligned to European formatting, and team-member profile plus balance access was reduced to a safer read-only surface.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Team-member credit, label-balance and sender-profile flows now resolve through the owning customer account instead of incorrectly behaving like separate standalone company accounts.",
      "The customer order detail screen no longer opens and then falls into an error state, because both the frontend action handling and the backend team-scope detail path were repaired during the same support thread.",
      "Team members now see a much more restricted account surface: profile editing, password and security management were removed, and the `Label Balances` page is no longer available to them."
    ],
    details: [
      {
        title: "Shared Account Scope for Team Members",
        items: [
          "Customer-side credit and label-related flows were reviewed end to end after support feedback showed that team members were incorrectly being treated like independent customer accounts instead of members of the same company workspace.",
          "The customer portal now resolves owner-scoped label balances, recent label purchases, sender-address requirements and related create-label checks through the main account where that shared data actually belongs.",
          "This means team members now work against the same operational credit pool and sender setup as the account owner, rather than seeing false missing-credit or missing-sender errors just because their sub-user record does not store its own duplicate copy."
        ]
      },
      {
        title: "Order Detail and Customer Orders Reliability",
        items: [
          "The order detail screen had two separate failure modes in this thread: the client detail component was reading action responses too loosely, and a later backend follow-up introduced an `isTeamMember is not defined` crash inside the `fetchStoreOrder` action itself.",
          "Both paths were repaired so the detail route now unwraps action payloads correctly on the frontend and also keeps the resolved team-member ownership state available on the server before totals are redacted for restricted users.",
          "Store-ownership resolution was also aligned more closely with the broader customer orders flow so both regular customers and team members can open supported order detail pages without falling into the generic error state after the first render."
        ]
      },
      {
        title: "Portal UX Restrictions, Locale Fixes and Release Tracking",
        items: [
          "Customer-facing date formatting was switched from the earlier US-style defaults to European ordering with `en-GB` and 24-hour time, addressing the JTL order-list feedback raised in the same support pass.",
          "Team-member profile access was reduced further after review: edit controls, sender management, security methods, password changes and plan management were removed, the dedicated security route now redirects back to the profile page, and team members no longer see or access the `Label Balances` page.",
          "This work was verified with targeted Node test runs for customer account-scope helpers and related label-purchase/order relations plus repeated TypeScript `--noEmit` checks, the package version was advanced to `1.0.27`, and the final development deploy was completed after the last order-detail and team-member navigation fixes in this thread."
        ]
      }
    ]
  },
  {
    version: 167,
    date: "2026-04-21",
    title: "Admin Amazon Platform Detail Now Resolves Customer Ownership More Reliably",
    summary: "This support release records the full admin Amazon platform-detail follow-up completed in the current conversation: the `Platforms > Amazon` screen no longer treats Amazon account or store labels as if they were customer identities, the customer column now prefers real owner records with name and email, and the ownership resolver was hardened again after a live production check showed that some inactive or unsynced Amazon stores were still losing their customer link on the server-side data path.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The admin Amazon detail table now shows real customer name and email when the owner can be resolved, instead of falling back to marketplace account labels in the customer column.",
      "Amazon store ownership is now recovered through multiple layers, including related order and shipment activity plus a direct public-store owner fallback for rows where the internal analytics read path drops the owner relation.",
      "This release also records the live production investigation itself, including repeated deploys, direct production data inspection and the follow-up repair after the first rollout only fixed one active Amazon row."
    ],
    details: [
      {
        title: "What Broke on the Admin Amazon Detail Page",
        items: [
          "The original admin Amazon platform table could list connected Amazon accounts, but the `Müşteri` column still showed Amazon-side account labels such as `walkiddy-c85a061b` instead of the actual Kenpost customer who owned the connection.",
          "The first repair in this thread removed that misleading fallback and added relation-based customer recovery through order, shipment and label activity, which immediately fixed the one row that already had synced activity behind it.",
          "A live production follow-up then showed the remaining inconsistency clearly: rows with recent synced activity could resolve to a customer, while inactive, expired or not-yet-synced Amazon stores still collapsed to `Bilinmeyen müşteri` even though the production store records themselves already had valid owner links."
        ]
      },
      {
        title: "How Ownership Resolution Was Hardened",
        items: [
          "The admin platform-insights action now keeps the existing internal analytics reads for counts and status data, but it also loads a parallel public `store` owner map and uses that as a targeted fallback when the internal store payload does not return the expected owner relation cleanly.",
          "This means the customer column no longer depends only on downstream shipment or order activity to discover ownership; inactive, errored or newly connected Amazon stores can still resolve to their Kenpost customer if the store record itself already knows the owner.",
          "Most importantly, the customer cell now only shows real customer identity data or the explicit unknown state, so the table no longer misleads support by presenting Amazon account names as if they were customer names."
        ]
      },
      {
        title: "Verification, Versioning and Deployment",
        items: [
          "This release was verified with repeated TypeScript `--noEmit` checks, direct production data inspection through Gadget CLI queries, and two production deploys during the same support thread: one after the first ownership-recovery pass and another after the production-only inactive-row follow-up was identified.",
          "The work is now recorded as `v167`, and the package version was advanced to `1.0.26` so the documented release state matches the deployed application again.",
          "This version intentionally captures the full Amazon admin ownership investigation as one support release because the root cause only became clear after the first live deploy improved active rows but still left unsynced or failed Amazon accounts without customer identity."
        ]
      }
    ]
  },
  {
    version: 166,
    date: "2026-04-14",
    title: "Order Label Review Now Preserves Optional Company Details Without Changing Shipment Safeguards",
    summary: "This support release records the customer order-label follow-up completed after the video review in the current conversation: the orders create, regenerate and bulk preview flows now expose an optional company field, source order company names are carried into the review state when available, and recipient formatting now preserves `c/o` company delivery details through the existing label pipeline without weakening the shipment, credit or DE-only safety rules already in place.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Customer order label dialogs and bulk preview rows now include an optional company field instead of forcing business recipients into the name or street lines.",
      "The orders flow now pre-fills company information from supported source-platform shipping addresses where that data is already available.",
      "Shipment creation still runs through the same existing bulk and single-label actions, so duplicate checks, Germany-only validation, Packstation blocking and credit enforcement remain unchanged."
    ],
    details: [
      {
        title: "Optional Company Input Across the Orders Flow",
        items: [
          "The `Orders` create, regenerate and bulk preview experiences were updated to show a dedicated optional company input alongside the existing recipient and address fields, matching the flexibility already available in the manual label flow.",
          "This directly addresses the customer feedback from the recorded review pass where business-recipient details were hard to see or adjust during bulk label preparation.",
          "The change stays inside the existing orders UI instead of introducing a second label path, so customer teams keep the same shipment workflow they already know."
        ]
      },
      {
        title: "Recipient Formatting and Source Mapping Were Hardened",
        items: [
          "A shared helper now splits and rebuilds recipient labels with optional `c/o` company formatting, which lets the orders screen preserve company delivery details without inventing a brand-new address schema.",
          "Source-platform shipping-address extraction was also extended to carry company names into the review defaults when those values already exist upstream, so business orders arrive in the preview in a more complete state.",
          "Most importantly, shipment creation still submits through the same existing single and bulk actions, meaning the core label engine, duplicate guards, Packstation checks, credit logic and DE-only restrictions were not rewritten for this release."
        ]
      },
      {
        title: "Verification, Versioning and Deployment",
        items: [
          "This release was verified with the shared recipient-company formatting tests, customer create-label helper tests, orders relation tests, bulk shipment download tests and a successful `yarn build`.",
          "The work is now recorded as `v166`, and the package version was advanced to `1.0.25` so the documented release state matches the deployed application.",
          "This version intentionally captures the full company-field support thread from the present conversation after the customer review step, rather than scattering the UI and formatting updates across separate partial notes."
        ]
      }
    ]
  },
  {
    version: 165,
    date: "2026-04-14",
    title: "JTL Database Mode Was Hardened with Safer Secret Handling and Correct Order Mapping",
    summary: "This release records the full JTL database-mode follow-up completed after the original dual-mode JTL rollout. The direct SQL option now keeps saved database passwords non-readable after setup, the JTL order mapping was corrected against the real MSSQL schema so customer, address, item and status data no longer collapse in the orders list, and floating-point totals from JTL database imports are now rounded and displayed cleanly in the customer order table.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Saved JTL database passwords are no longer sent back to the browser or re-rendered in the setup form after the IP connection is stored.",
      "JTL database order mapping now follows the real JTL schema more accurately, including address resolution through `tAdresse_kAdresse`, item reads from `tBestellPos` and derived payment and fulfillment states.",
      "The JTL orders screen now formats totals cleanly to two decimals and avoids the customer-facing floating-point artifacts that previously exposed values like `24.650000000000002`."
    ],
    details: [
      {
        title: "Database Password Handling Was Tightened",
        items: [
          "The JTL IP status response no longer returns the saved database password, and the UI now reloads the password field as empty even when an existing IP connection is already configured.",
          "After a JTL IP connection is saved, the password input is cleared again immediately and the visibility toggle only appears while a newly typed replacement password is present.",
          "This keeps the direct SQL mode usable for future edits without creating a recoverable plaintext-password path in the customer portal."
        ]
      },
      {
        title: "JTL MSSQL Order Mapping Was Corrected",
        items: [
          "Live MSSQL inspection against a real JTL database showed that customer and address data needed to come from `tAdresse_kAdresse`, while order lines and derived totals had to be read from `tBestellPos` using `tBestellung_kBestellung` instead of the earlier weaker assumptions.",
          "The JTL database sync helper was updated to aggregate gross, net, tax and shipping amounts from line rows, derive payment and fulfillment states from JTL order flags and timestamps, and normalize customer and address fields so the customer orders page receives the same rich shape it expects from other platforms.",
          "This eliminated the earlier blanks and placeholders in JTL order rows where customer names, item counts, totals, countries and statuses could appear missing even though the source database already contained the data."
        ]
      },
      {
        title: "Order List Formatting, Verification and Release Tracking",
        items: [
          "Both the JTL database sync layer and the customer orders UI now round monetary values to two decimals, and the rendered order table applies locale-aware formatting so imported JTL totals no longer show long floating-point tails.",
          "The item-count cell in the customer orders table was also tightened so simple JTL counts do not wrap awkwardly while still preserving the hover preview behavior for richer item lists.",
          "This release was verified with direct MSSQL queries against the live JTL dataset, a successful `yarn build`, a clean `ggt problems` run, a production deploy, and the package version was advanced to `1.0.24` so the documented release state matches the deployed application."
        ]
      }
    ]
  },
  {
    version: 164,
    date: "2026-04-14",
    title: "Amazon Bridge Onboarding Was Stabilized Across Timeout Recovery and Production Render Fixes",
    summary: "This support release records the full Amazon integration thread completed in the current conversation: production tracing first showed that Amazon bridge finalization could time out even after an authorization URL was created, so bridge requests were split into safer per-endpoint timeout windows. A later production screen recording then proved that `/customer/stores/amazon/new` was also crashing before the bridge flow even started, and the page was repaired by correcting the message-tree namespace that the customer Amazon route was reading during render.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Amazon bridge requests no longer share one short timeout; health, status, start, click and delete calls now each use safer endpoint-specific timeout defaults.",
      "The customer Amazon connection page no longer falls straight into the generic white `Application Error` card on first load, because the route now points at the message tree namespace that actually exists in production.",
      "The release also documents the production investigation sequence itself, including live bridge-state checks, production store-meta review, build verification and repeated deploys until the real customer-facing failure path was corrected."
    ],
    details: [
      {
        title: "Bridge Timeout Investigation and Hardening",
        items: [
          "Production bridge status and stored Amazon connection metadata showed a real support case where the bridge had already created an `amazonAuthUrl`, but the later confirmation step still ended in `BRIDGE_TIMEOUT`, which meant the issue was not limited to the initial start call.",
          "The app-side Amazon bridge helper was therefore updated to resolve timeout values by request type instead of using one blanket timeout for every path, with lower limits kept for health and status checks and longer limits reserved for `/jobs/start` and `/jobs/:id/click`.",
          "Regression coverage was added around the timeout resolver, and the `.env.example` plus Amazon setup notes were expanded so future production tuning can change click or start timeouts explicitly without weakening lighter bridge checks."
        ]
      },
      {
        title: "Production Render Failure on the Amazon Customer Page",
        items: [
          "After the first deploy, the correct customer screen recording showed that the Amazon route was still failing immediately on `/customer/stores/amazon/new`, which proved the remaining problem lived in route render rather than inside the later bridge requests.",
          "Thread-local code tracing then found two related message-tree issues: the route originally asked for `customer.amazonBridgePage`, and the underlying `amazonBridgePage` translations themselves were actually stored under the `admin` namespace in the page-message tree.",
          "The customer Amazon route was updated to read the real existing namespace so the page can render its copy object safely again instead of throwing during the first render and falling through to the shared root error boundary."
        ]
      },
      {
        title: "Verification, Versioning and Deployment",
        items: [
          "This thread's Amazon fixes were verified with successful local builds and targeted timeout tests before each production rollout, rather than being left as an unverified log-only theory.",
          "The work is now recorded as `v164`, the package version was advanced to `1.0.23`, and the final production deploy in this support sequence completed after the corrected Amazon page namespace fix so the live version history matches the actual repaired state.",
          "This version intentionally groups the Amazon timeout hardening and the later route-render repair together because both failures surfaced during the same customer support investigation and both had to be deployed before the integration flow could be trusted again."
        ]
      }
    ]
  },
  {
    version: 163,
    date: "2026-04-14",
    title: "Admin Customer Detail Now Tracks Connected Stores in a Dedicated Support Tab",
    summary: "This release records the admin customer-detail follow-up completed in the current thread: support teams can now see which store accounts a customer has connected directly from `/admin/customers/:id`, with a dedicated `Stores` tab that surfaces platform, store identifiers, sync footprint and connection timing without forcing a separate customer-side navigation hop.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Admin customer detail pages now include a dedicated `Stores` tab showing every connected store for the selected customer.",
      "The new admin store view surfaces platform, active state, `storeUrl`, `externalStoreId`, synced-order counts, last sync time and connection date in one place.",
      "Store totals are also reflected in the customer detail header and overview cards so support can spot multi-store accounts faster before drilling into the full table."
    ],
    details: [
      {
        title: "Customer Store Visibility for Support",
        items: [
          "The admin customer detail loader was expanded to fetch the customer's `store` records alongside the existing purchases, shipments, assigned labels and pricing data, so store visibility now comes from the same relation bundle already used by the page.",
          "This avoids the previous support gap where operators had to leave the admin customer detail view or impersonate the customer just to check which store accounts were connected.",
          "Each store row now shows the practical identifiers support actually needs during investigations, including the platform label, store URL, external store id, active status and order-cache sync metadata."
        ]
      },
      {
        title: "Admin Detail UI and Summary Updates",
        items: [
          "A new `Stores` tab was added to the admin customer detail route, and the top action area now includes a direct shortcut so operators can jump there without scrolling through the rest of the customer detail workspace.",
          "The summary card strip and overview page were updated to include connected-store counts and a quick store-footprint signal, making multi-store customers easier to recognize at a glance.",
          "The new tab stays visually aligned with the existing admin customer detail design system instead of introducing a separate management page just for store inspection."
        ]
      },
      {
        title: "Verification, Versioning and Deployment",
        items: [
          "This release was verified with a successful `yarn build` after the store-detail loader and admin UI updates were added.",
          "The work was recorded as `v163`, the package version was advanced to `1.0.22`, and a fresh production deploy was requested so the live version history matches the new admin customer-store support workflow.",
          "No schema migration or destructive data rewrite was required for this release because it only reuses existing `store` records in the admin customer detail flow."
        ]
      }
    ]
  },
  {
    version: 162,
    date: "2026-04-14",
    title: "Label Recovery, Customer Cancellations and Credit Invoice PDFs Were Hardened in One Support Release",
    summary: "This support release groups the full live follow-up completed across label generation failures, eBay refresh recovery, customer-side failed-manual-label cancellation and credit-invoice PDF corrections. Temporary storage-side failures that could leave labels half-generated are now handled more safely, customers can cancel and refund failed manual labels from the screens they actually use, and credit invoice PDFs now render the fixed service description and buyer block more reliably for both admin and customer downloads.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "eBay refresh no longer fails outright when the previous cached order snapshot cannot be read, because the sync path now falls back to a full rebuild instead of stopping on `EBAY_EXISTING_CACHE_READ_FAILED`.",
      "Failed internal or manual shipments now recover more cleanly: regenerate and download flows produce raster artifacts again, old PDF-only labels can backfill PNG and JPEG outputs, and bulk manual creation was aligned to the same artifact behavior.",
      "Customer-facing failed manual labels can now be cancelled and refunded directly from the recent manual-label list and shipment detail flow, while credit invoice PDFs now keep the full German service description and resolve buyer data from relation snapshots plus profile fallbacks."
    ],
    details: [
      {
        title: "Shipment and eBay Recovery Improvements",
        items: [
          "Production investigation showed that transient storage-side failures could break both internal label generation and incremental eBay cache reads during the same support window, so the recovery work was focused on resilience rather than treating the incidents as isolated customer mistakes.",
          "The eBay sync flow now logs the cache-read problem with clearer recovery intent and continues with a full sync fallback when the existing cached orders payload cannot be loaded, which prevents refresh from failing just because the previous snapshot is temporarily unavailable.",
          "Internal shipment download, regenerate and pending-recovery paths were aligned so raster outputs are generated again, and customers can recover older PDF-only labels without needing a separate manual intervention path."
        ]
      },
      {
        title: "Customer Cancellation and Label Credit Restoration",
        items: [
          "The failed-manual-label cancellation issue turned out to be a UI flow gap rather than a broken backend refund path, so the customer create-label screen and shipment detail screen now both expose the same cancel-and-refund action for eligible failed labels.",
          "This means customers can void failed manual labels from the screens where they first see the problem, and the linked label source is restored back into their balance instead of remaining stuck in a consumed state.",
          "Live support verification confirmed the cancel path restored multiple failed manual labels and increased the affected customer's balance step by step, so the fix was not left as a code-only assumption."
        ]
      },
      {
        title: "Credit Invoice PDF and Version Tracking",
        items: [
          "The purchase-invoice PDF layout previously truncated the required German service description with trailing dots because the description cell still used a hard character cut-off, so the PDF renderer now fits the text to the available width instead of cutting it prematurely.",
          "Admin and customer credit-invoice views were aligned to the same fixed service description, and buyer resolution for purchase invoices now uses relation snapshots and profile fallbacks so PDFs are less likely to collapse to a generic `Customer` recipient block.",
          "This work was recorded as `v162`, the package version was advanced to `1.0.21`, and fresh production deploys were completed after the invoice and release-note updates so testing reflects the documented release state."
        ]
      }
    ]
  },
  {
    version: 161,
    date: "2026-04-14",
    title: "Admin Support-Mode Return Flow Was Repaired After Production Session Tracing",
    summary: "The customer support-mode exit path was corrected after a live production follow-up showed that returning from an impersonated customer account could still crash on `/customer` with a generic application error. The support-session restore logic is now centralized, production logs confirmed why the earlier route-action approach could lose the active support session, and the customer-side `Return to admin` control was moved back onto the authenticated Gadget action path so admins can leave customer mode reliably again.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin-to-customer impersonation still starts through the dedicated support-mode action, but exiting support mode now routes back through the authenticated `stopAdminImpersonation` global action again.",
      "The stop-session restore logic was extracted into shared code so session cleanup, restored admin identity and redirect calculation stay consistent across the backend flow.",
      "Production log review confirmed the actual failure mode: the `/customer.data` action request that handled `Return to admin` could arrive without the active support-session context, causing `No active support mode session was found` and the generic error screen."
    ],
    details: [
      {
        title: "What Failed in Production",
        items: [
          "The live log trail showed support mode being started successfully for real customers, followed minutes later by repeated `No active support mode session was found` errors when the admin tried to leave the impersonated customer account.",
          "The failing requests were tied to the route-action based `/customer.data` path, where the stop action could run without the same authenticated session context that the surrounding customer GraphQL calls were using.",
          "Because that route action threw before the restore completed, admins saw the generic application error card instead of being redirected back to the admin side."
        ]
      },
      {
        title: "How the Return Flow Was Repaired",
        items: [
          "The support-session restore steps were centralized in a shared helper so the backend still restores the original admin user, clears impersonation metadata and computes the admin redirect in one place.",
          "After the production trace confirmed the route-action session gap, the customer UI was switched back from the temporary form-post route action to the authenticated Gadget `stopAdminImpersonation` global action.",
          "The customer banner button now again waits on that authenticated stop call, shows loading feedback and then redirects the browser to the returned admin destination after the session was restored successfully."
        ]
      },
      {
        title: "Verification and Release Tracking",
        items: [
          "The admin impersonation regression tests still pass, including the existing restore-path coverage and the guard case for missing support-mode metadata.",
          "A fresh production build and deploy were completed after the final support-mode fix so the live application matches the repaired return flow recorded in this version entry.",
          "This release note intentionally records the full impersonation-return thread as its own version step because the first attempted fix and the later production-root-cause correction happened inside the same support pass."
        ]
      }
    ]
  },
  {
    version: 160,
    date: "2026-04-14",
    title: "Kaufland and OpenCart Onboarding Landed with Safer Access Control and Faster Kaufland Sync Defaults",
    summary: "Kenpost's marketplace coverage expanded again with full Kaufland and OpenCart onboarding inside the customer portal, but the work did not stop at simply adding two more store cards. During live rollout, access-control grants, customer-role repair paths, Germany-only Kaufland configuration, official platform branding and Kaufland sync behavior were all tightened so the new integrations behave more like first-class portal flows instead of fragile first releases.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Customers can now connect both Kaufland and OpenCart through dedicated store-management pages, and both platforms are wired into the shared order-sync pipeline.",
      "The permission issue that initially blocked Kaufland save and test actions was fixed for the new marketplace actions, and customer-role self-healing was added to reduce repeat access problems.",
      "Kaufland sync now supports `Last 7 Days`, `Last 30 Days` and `All`, with the default changed to 30 days so large stores no longer start with a full-history import by default."
    ],
    details: [
      {
        title: "New Marketplace Connections",
        items: [
          "Kaufland and OpenCart were added across the customer store list, new-connection pages, platform registry, store detail navigation and order-detail routing so both platforms now sit inside the same operational flow as the existing marketplaces.",
          "Both integrations write normalized order payloads into the same S3 gzip cache path pattern used elsewhere, which means downstream order listing and shipment creation continue to read a consistent shape.",
          "Admin-facing platform visibility was also expanded so the new integrations show up in the broader system instead of existing only as hidden customer-side experiments."
        ]
      },
      {
        title: "Access and Reliability Hardening",
        items: [
          "The first live Kaufland rollout exposed missing grants for the new save, test and disconnect actions; those access-control permissions were then added for signed-in, customer and admin roles so the same denial no longer blocks Kaufland or OpenCart.",
          "Customer-account creation and sign-in paths were strengthened so missing `customer` roles can be repaired automatically and future onboarding inconsistencies are less likely to break marketplace actions.",
          "OpenCart was intentionally kept on an admin-login connection model for now after re-checking the platform's API story, because the current real-world order-sync needs are more reliable through the admin routes than through a narrow API-key assumption."
        ]
      },
      {
        title: "Kaufland Operational Improvements",
        items: [
          "Kaufland was locked to the German marketplace only, removing the unnecessary storefront choice and simplifying the setup flow around the real supported business case.",
          "Official Kaufland and OpenCart logos replaced the temporary assets so the new integrations match the expected platform branding in the marketplace list.",
          "Most importantly for large Kaufland stores, the sync path now uses Kaufland's official `ts_created_from_iso` order-unit filter and exposes `7d / 30d / all` controls in the orders screen, with the manual store page defaulting to a 30-day import to avoid repeated full-history waits on every refresh."
        ]
      }
    ]
  },
  {
    version: 159,
    date: "2026-04-08",
    title: "JTL Store Setup Now Offers Both Platform and Direct IP Connection Modes",
    summary: "JTL setup no longer depends on a single connection style. The customer store screen now offers a tabbed choice between the existing platform connector flow and a new IP-based SQL connection mode, so teams can keep the generated JTL connector path for licensed customers while also supporting direct JTL database reads for customers who prefer host-based access.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The JTL connection page now contains `Platform Connection` and `IP Connection` tabs inside the same store-management flow.",
      "The new IP mode stores SQL host, optional port, database name, username and password, then reads JTL orders directly from Microsoft SQL Server.",
      "Direct MSSQL validation was completed against a real JTL database before deployment, confirming that products and orders were visible through the new connection path."
    ],
    details: [
      {
        title: "Dual-Mode JTL Setup",
        items: [
          "The JTL customer setup screen now shares store name and import range across both connection styles, while each tab exposes the credentials and actions that belong only to that mode.",
          "Platform mode keeps the generated Kenpost connector URL, password and store-code workflow, while the new IP mode presents SQL host, port, database and login fields in a clearer operational layout.",
          "Status cards, test feedback and disconnect actions now all reflect which JTL connection mode is currently active."
        ]
      },
      {
        title: "Direct SQL Order Sync",
        items: [
          "A new JTL MSSQL helper now opens encrypted SQL Server connections, discovers relevant JTL tables dynamically and pulls orders directly from `tBestellung` and related rows when IP mode is selected.",
          "The IP sync path writes the same normalized JSON and gzip payloads to S3 that the rest of the order pipeline expects, so downstream Kenpost behavior stays consistent.",
          "The customer-requested Python MSSQL examples were used as a reference for server, database, username, password and TLS connection expectations while implementing this second mode."
        ]
      },
      {
        title: "Validation and Release Tracking",
        items: [
          "Before deployment, the app passed `ggt problems`, a full production build and direct MSSQL connectivity checks against a live JTL SQL dataset.",
          "The live database verification confirmed that tens of thousands of products and orders were readable, and that recent orders were visible through the same SQL connection style now exposed in the UI.",
          "This support pass was recorded as `v159` so the new direct-IP JTL option remains visible in version history instead of being buried inside the earlier connector-only work."
        ]
      }
    ]
  },
  {
    version: 158,
    date: "2026-04-08",
    title: "JTL Connector Compatibility and Diagnostic Coverage Were Expanded for Wawi Testing",
    summary: "The JTL connector path was hardened across multiple Wawi test rounds so Kenpost now answers more of the connector handshake that JTL expects during setup. Authentication cookies, connector identity metadata, feature discovery, global data pulls and structured error payloads were all tightened to make platform-mode diagnostics far clearer when customer-side Wawi behavior differs across environments.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The JTL connector endpoint now covers a broader RPC handshake, including identify, auth, features and global-data calls used during Wawi setup.",
      "Response payloads were aligned more closely with JTL expectations, including cookie handling and error-shape fields needed for deserialization.",
      "Production-side logging and targeted smoke tests now make it much easier to see whether a setup issue is Kenpost-side, network-side or specific to the tested JTL client."
    ],
    details: [
      {
        title: "Broader JTL RPC Coverage",
        items: [
          "The connector route now handles the key JTL RPC phases that surfaced during live Wawi testing, rather than only exposing a minimal endpoint probe.",
          "Unsupported-method failures that previously blocked Wawi setup were reduced by adding coverage for the discovered calls and by returning data in the shapes JTL was actually expecting.",
          "This expanded compatibility layer keeps the platform connection mode viable for customers who can or want to use the standard connector route."
        ]
      },
      {
        title: "Safer Response Shaping",
        items: [
          "Connector responses now preserve more JTL-like structure for session handling, JSON-RPC error bodies and related metadata so deserialization failures are easier to avoid.",
          "Session cookies and probe responses were adjusted so the JTL client has a better chance of continuing the setup flow instead of stopping after the first authentication hop.",
          "The work also reduced ambiguity around misleading Wawi popup text by giving support much better server-side visibility into what JTL actually called."
        ]
      },
      {
        title: "Operational Debugging Support",
        items: [
          "Repeated live connector tests from multiple customer environments were correlated with production logs so the team can now distinguish handshake issues from downstream JTL licensing or client-state behavior much faster.",
          "These diagnostics remain valuable even when a given customer ultimately chooses the new IP mode, because the platform option still exists in the same store setup screen.",
          "This release keeps the connector diagnostics as its own step in history instead of hiding the work inside the later IP-mode rollout."
        ]
      }
    ]
  },
  {
    version: 157,
    date: "2026-04-08",
    title: "JTL Store Management Became a Proper Guided Workflow Inside the Customer Portal",
    summary: "JTL setup inside the customer store area was reshaped from a rough connection form into a fuller managed workflow. Shared JTL status loading, test actions, refresh handling, disconnect behavior, import-range controls and clearer multilingual guidance were added so customers can manage JTL more like the other marketplace connections instead of treating it as a one-off experimental screen.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The JTL store screen now behaves like a managed connection page with status, test, refresh and disconnect actions instead of a bare credentials form.",
      "Import range handling for JTL now follows the same `7 days / 30 days / all` operational pattern used elsewhere in the platform.",
      "English, Turkish and German page messaging were expanded so the new JTL setup states and instructions are understandable across the supported customer locales."
    ],
    details: [
      {
        title: "Managed JTL Connection Lifecycle",
        items: [
          "JTL status loading, save/update flows, testing and disconnect operations now use a more complete connection-management pattern rather than leaving the customer on a thin static setup page.",
          "The store detail and sync-side metadata now remember the active JTL mode, recent test state and related sync hints so the operational state is easier to understand later.",
          "This makes JTL feel much closer to the established Shopify, WooCommerce and other marketplace setup flows already present in the portal."
        ]
      },
      {
        title: "Shared Sync and Range Controls",
        items: [
          "The JTL setup now shares a proper import-range selector, and the sync pipeline stores more explicit JTL metadata about how the latest payload was prepared.",
          "Whether the customer uses platform mode or IP mode, the selected date range is now part of the same JTL setup story instead of being an afterthought.",
          "This helps support teams reason about why a given JTL test or sync saw zero orders without having to inspect the database or payload manually."
        ]
      },
      {
        title: "Clearer Customer Guidance",
        items: [
          "The central page-message tree was expanded so the new JTL workflow has localized labels, setup notes, tab names, hints and result summaries in the main supported languages.",
          "The improved UI wording now explains more clearly which values are generated by Kenpost and which values the customer must actually provide themselves.",
          "This release note captures the guided-workflow foundation separately so the later connector-compatibility and direct-IP releases are easier to follow in the version timeline."
        ]
      }
    ]
  },
  {
    version: 156,
    date: "2026-04-07",
    title: "Customer Profile Security Controls Now Navigate Reliably and Feel Much More Compact",
    summary: "The customer profile page had become too stretched after the first security-method rollout, and the `Manage Security Methods` action could still feel unreliable because some customers were not actually reaching the dedicated security screen. The profile layout is now tighter again, email verification can be toggled directly from the profile summary, and the dedicated security page uses a cleaner standalone design with stronger direct navigation.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The profile page is more compact again because the security summary is shorter and the plan area now uses a denser control layout.",
      "The `Manage Security Methods` action now uses direct browser navigation to the standalone `/customer/profile/security` screen.",
      "Customers can also enable or disable e-mail verification directly from the profile summary without needing to leave the page."
    ],
    details: [
      {
        title: "Profile Layout Cleanup",
        items: [
          "The security block on the main profile page was reduced to a concise operational summary instead of a long method stack that pushed the page too far downward.",
          "Plan management was compressed into a smaller selector-driven card so the page keeps the same capability with less vertical noise.",
          "This gives the profile screen a more professional balance between account data, sender address and sign-in protection."
        ]
      },
      {
        title: "More Reliable Security Management",
        items: [
          "The profile screen now includes a live e-mail verification switch for quick action, which means customers are no longer blocked if they only need to turn the method off quickly.",
          "The `Manage Security Methods` call-to-action now uses direct href navigation rather than relying on the in-app route transition that had been reported as unreliable.",
          "The standalone security workspace was also redesigned into a clearer two-column layout with a strong e-mail method card and compact future-method summaries."
        ]
      }
    ]
  },
  {
    version: 155,
    date: "2026-04-07",
    title: "Admins Can Now Export Customer Labels Directly and Track External Label Handoffs",
    summary: "Label inventory handoff work no longer depends on customer login sessions alone. Admins can now export a customer's labels directly from the admin inventory area, while both admin and customer views now track whether labels were externally exported or externally used so teams can avoid re-downloading the wrong stock and can safely filter to labels that have not been handed off yet.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admins can now run the same customer label export workflow directly from the admin label inventory screen without impersonating the customer account.",
      "Label inventory rows now track external export and external use state, and both admin and customer screens can filter those labels separately.",
      "Labels are only marked as externally exported after the archive was actually built and stored successfully, preventing false downloaded flags on failed export attempts."
    ],
    details: [
      {
        title: "Admin Customer Export Workflow",
        items: [
          "The admin label inventory screen now supports customer-scoped export actions so support teams can select a customer, review that customer's labels, export filtered or selected rows, and copy stable download links without logging into the customer account.",
          "The same export history, progress visibility and stop controls that exist for customer inventory exports are now available for admin-operated customer exports as well.",
          "This gives operations a direct rescue path when a customer cannot complete the download themselves or should not have to share account access."
        ]
      },
      {
        title: "External Export and Usage Tracking",
        items: [
          "Label sources now store external export counters, export timestamps, external use timestamps and an admin note so handoffs outside the normal shipment flow are visible on the inventory record itself.",
          "Both the customer and admin inventory views now show badges and filters for externally exported and externally used labels, making it much easier to isolate stock that has not yet been handed off.",
          "Customer-facing inventory screens also show warning cues when the visible result set already contains labels that were exported or marked as externally used."
        ]
      },
      {
        title: "Safer Marking Rules",
        items: [
          "External export status is written only after the ZIP archive was successfully finalized and stored, not when the job first enters the queue.",
          "Admins can explicitly mark selected labels as externally used, which also converts eligible labels into consumed stock while skipping shipment-linked rows that were already used inside Kenpost.",
          "The balance sync path was hardened for older label records as well so manual external-use operations keep customer credit counts aligned more reliably."
        ]
      }
    ]
  },
  {
    version: 154,
    date: "2026-04-07",
    title: "Central Translation Coverage Expanded Across Admin and Customer Workflows",
    summary: "This release records the full translation sweep completed across the latest admin and customer pages. Missing page-level language trees were added into the central i18n structure, mixed hardcoded UI copy was moved out of route files, and the highest-risk operational screens now follow the shared release-and-translation pattern more consistently.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The central page-message file was extended for newly added admin and customer screens instead of leaving translations scattered inside route files.",
      "Admin invoice, label upload and related operational screens now use shared translation trees for headings, actions, dialogs, badges and toast feedback.",
      "Customer shipment detail, order detail and multiple store-connection flows now have stronger translation coverage, including the previously missing Amazon bridge and label-related pages."
    ],
    details: [
      {
        title: "Admin Translation Sweep",
        items: [
          "Admin invoice management screens were aligned with the shared page-message structure so table labels, edit states and credit-invoice actions no longer depend on stray hardcoded text.",
          "The label PDF upload workflow now reads its title, queue labels, duplicate states, summaries and toast messages from the central i18n tree instead of carrying mixed inline copy.",
          "This also closes the operational gap around newer admin pages like label product inventory and related invoice-side follow-up work, where translation coverage had fallen behind the feature rollout."
        ]
      },
      {
        title: "Customer Translation Coverage",
        items: [
          "Customer shipment detail and order detail flows were tightened so user-facing status labels, action buttons and feedback messages now follow the same translation structure as the rest of the portal.",
          "Billbee, Shopify, eBay, WooCommerce and especially the Amazon bridge connection flow now have dedicated message trees for setup instructions, warnings, dialogs and connection-state messaging.",
          "This makes the newer customer pages much more reliable for multilingual use instead of leaving important operational screens partially untranslated."
        ]
      },
      {
        title: "Release Tracking and Deployment",
        items: [
          "The release notes timeline was advanced to `v154` so this translation support pass is visible in version history instead of being hidden inside earlier feature notes.",
          "The application was rebuilt successfully after the translation refactor to confirm the new message-tree wiring did not break route compilation.",
          "A fresh production deploy was completed after the release-note update so the live app and the version history now describe the same state."
        ]
      }
    ]
  },
  {
    version: 153,
    date: "2026-04-07",
    title: "Customer Profile Security Moved into a Real Standalone Workspace",
    summary: "The profile redesign had improved the visuals, but the security-management entry point still felt unreliable because the `/customer/profile/security` screen was nested under the profile route in a way that could leave customers stuck on the same page. The profile layout is now tighter again, and sign-in protection management now opens in a true standalone security workspace while the profile page stays compact and easier to scan.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The customer profile page is more balanced again and no longer stretches awkwardly because of an oversized right-hand security stack.",
      "The `Manage Security Methods` action now leads into a real standalone `/customer/profile/security` page instead of a fragile nested route setup.",
      "The profile screen keeps only a short, professional security summary while the detailed email/SMS/app method controls live in the dedicated security workspace."
    ],
    details: [
      {
        title: "Cleaner Profile Layout",
        items: [
          "The plan card was moved into the main profile column so the page uses vertical space more naturally instead of leaving a large blank area beside the sender and security blocks.",
          "The right rail now stays intentionally short with a sender-address card, a concise security summary, and a small future-readiness note instead of a long method stack.",
          "The hero section was also tightened so the quick actions and status chips still feel prominent without making the page top-heavy."
        ]
      },
      {
        title: "Reliable Security Navigation",
        items: [
          "The security management screen now uses a non-nested standalone route file so `/customer/profile/security` behaves like a real destination and no longer silently falls back to the profile screen.",
          "Both the main CTA and the compact security summary now point into that dedicated workspace using direct route navigation.",
          "This makes it much clearer where customers should go to enable or disable email verification, while preserving room for future SMS and authenticator methods."
        ]
      }
    ]
  },
  {
    version: 152,
    date: "2026-04-06",
    title: "Credit Invoice PDFs Now Use the New Fixed Logistics Service Description",
    summary: "Purchase invoice PDFs now show the updated fixed service description requested for the invoice line item. The old wording and the appended label-product suffix were removed so these invoices present one consistent logistics-service description across the whole system.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Credit invoice PDFs now show `Versand- und Logistikdienstleistung (digitale Versandabwicklung) inkl. Versandetiketten` as the fixed line-item description.",
      "The previous shorter wording was replaced everywhere this shared credit-invoice description is generated.",
      "The product-name suffix is no longer appended to that fixed line, keeping the invoice wording consistent."
    ],
    details: [
      {
        title: "Updated Shared Invoice Description",
        items: [
          "The shared credit-invoice presentation helper now returns the newly requested logistics-service wording as the stable service description.",
          "The previous `(product name)` suffix behavior was removed so the generated line stays fixed and legally cleaner across all purchase invoices.",
          "Automated tests were updated at the same time to keep future regressions from bringing back the older invoice wording."
        ]
      }
    ]
  },
  {
    version: 151,
    date: "2026-04-06",
    title: "Invoice PDFs Now Keep Recipient Details Leaner and Cleaner",
    summary: "Purchase invoice PDFs were improved again so the recipient section stays focused on the customer identity without exposing unnecessary e-mail details, and the single-line invoice layout no longer wastes space on a position column. Customer VAT information still appears automatically when it exists on the customer profile, but it is now omitted completely when it does not exist.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Buyer e-mail is no longer printed in the recipient block of purchase invoice PDFs.",
      "Customer VAT information still appears when available, but the tax line stays hidden when the customer profile has no VAT/tax ID.",
      "The line-item table no longer shows a position number column for single-line credit invoices."
    ],
    details: [
      {
        title: "Cleaner Recipient Presentation",
        items: [
          "The recipient block now focuses on the customer's name, postal address and VAT/tax identifier when one exists.",
          "Customer e-mail addresses were removed from the visible invoice body because they are not required on these PDFs.",
          "If a customer has no VAT/tax identifier saved on their profile, the PDF now simply skips that line instead of showing an empty placeholder."
        ]
      },
      {
        title: "Simplified Invoice Line Layout",
        items: [
          "The line-items table was tightened to remove the old position number column because these credit invoices currently render only a single product row.",
          "The description column was widened accordingly so the content feels less cramped.",
          "This makes the invoice body look more intentional and less like a generic multi-line template."
        ]
      }
    ]
  },
  {
    version: 150,
    date: "2026-04-06",
    title: "Invoice PDFs Now Show the Customer More Clearly and No Longer Mention a 5-Day Payment Deadline",
    summary: "Customer-facing invoice PDFs could still feel anonymous in some purchase invoice flows because the buyer block sometimes collapsed down to a generic `Customer` label. Credit invoice buyer resolution is now relation-safe, the PDF layout now renders a clearer recipient section with the customer email, and the old five-day payment reminder was removed from the footer note area.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Credit invoice PDFs now render a clear `Rechnungsempfänger` section instead of falling back to a vague `Customer` line in broken relation cases.",
      "Buyer e-mail is now included in the visible recipient block so it is easier to identify which customer the invoice belongs to.",
      "The old `pay within 5 days` reminder was removed from the PDF note section."
    ],
    details: [
      {
        title: "Safer Buyer Resolution for Purchase Invoices",
        items: [
          "The purchase invoice download action now normalizes relation-style `customerId` and `createdById` values before resolving user profiles, avoiding the old `[object Object]` lookup failure.",
          "If the purchase-level customer link is incomplete, the invoice flow now falls back to the assigned label owner from the source relation before falling back again to the creator profile.",
          "This makes older or partially repaired credit purchases much more likely to produce an invoice with the correct customer details."
        ]
      },
      {
        title: "Cleaner PDF Presentation",
        items: [
          "The PDF template now labels the buyer block explicitly as `Rechnungsempfänger`, making it much clearer who the invoice was issued to.",
          "The recipient block now includes the customer e-mail alongside the existing address and tax information when available.",
          "The informational note under the line items was simplified so the previous five-day payment wording no longer appears."
        ]
      }
    ]
  },
  {
    version: 149,
    date: "2026-04-04",
    title: "Email Two-Factor Errors Now Show Clear Customer Messages",
    summary: "The sign-in email verification screen was still leaking raw GraphQL-style error text when a customer entered the wrong code. The screen now recognizes wrapped backend error codes as well, so customers see a normal invalid-code or expired-code message instead of an internal-looking technical error.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Wrong email verification codes now show a normal customer-facing message instead of raw `GGT_UNKNOWN` backend text.",
      "Expired-code, session-expired, and resend-failed cases now follow the same safer message mapping even when GraphQL wraps the error code.",
      "The sign-in verification page feels more trustworthy because internal system wording is no longer exposed to customers in common failure cases."
    ],
    details: [
      {
        title: "Cleaner Verification Feedback",
        items: [
          "The email two-factor screen now checks both exact backend error codes and GraphQL-wrapped variants of those codes before deciding what to show.",
          "This covers the real production case where a wrong code came back as a raw `GGT_UNKNOWN: SIGN_IN_EMAIL_TWO_FACTOR_CODE_INVALID` string.",
          "Customers now get the intended translated invalid-code or expired-code message instead of a technical backend response."
        ]
      }
    ]
  },
  {
    version: 148,
    date: "2026-04-03",
    title: "Admins Can Now Disable Customer Email Two-Factor from the Customer Detail Screen",
    summary: "Support teams sometimes need to recover customer access when a user enabled sign-in email verification on the wrong mailbox or can no longer receive verification emails. Admins can now disable customer email two-factor directly from the customer detail page, and the rescue flow clears any pending sign-in verification codes so the customer can immediately fall back to normal email-and-password sign-in.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admins can now disable a customer's email two-factor protection directly from the admin customer detail page without editing unrelated profile fields.",
      "The rescue action clears any pending email verification codes so customers are not left blocked by stale sign-in challenges.",
      "This gives support a fast recovery path when customers enabled email verification on the wrong inbox or mail delivery is failing."
    ],
    details: [
      {
        title: "Admin Recovery Controls",
        items: [
          "A new recovery action was added to the Access Recovery area on the admin customer detail screen specifically for sign-in email two-factor issues.",
          "The action is confirmation-gated so admins can clearly see they are disabling customer sign-in protection before the change is applied.",
          "The customer detail screen refreshes its 2FA status immediately after the action completes."
        ]
      },
      {
        title: "Safe Disable Behavior",
        items: [
          "Disabling customer email two-factor now also clears any pending verification code hash, expiry and sent-at timestamp.",
          "This prevents customers from getting stuck with an old challenge state after support turns the feature off for recovery.",
          "If the customer already has email two-factor disabled, the action exits cleanly without making unnecessary updates."
        ]
      }
    ]
  },
  {
    version: 147,
    date: "2026-04-03",
    title: "Email Two-Factor Sign-In Recovery and Auth Error Cleanup",
    summary: "Customers could reach the email verification step after entering the correct password, but both code verification and resending a code were failing with a raw `GGT_PERMISSION_DENIED` message. The unauthenticated permission chain for those auth actions is now restored, and the sign-in and email verification screens now translate raw backend failures into clearer customer-facing messages instead of exposing internal Gadget errors.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Unauthenticated customers can now verify sign-in email codes and request a fresh code without hitting a permission-denied error.",
      "Wrong-password and invalid-credentials scenarios now resolve to a cleaner, user-friendly sign-in error instead of leaking raw backend wording.",
      "The email verification screen now falls back to a generic verification error instead of exposing internal permission text if the backend ever misbehaves again."
    ],
    details: [
      {
        title: "Permission Repair",
        items: [
          "The global auth actions used by the sign-in email verification page were added to the unauthenticated permission role so customers on the pre-login 2FA screen can legally call them.",
          "This closes the exact production failure where `Verify and sign in` and `Send a new code` both returned `GGT_PERMISSION_DENIED` even though the customer had already passed the password step.",
          "The UI permission audit now passes with the email two-factor route included in the unauthenticated surface."
        ]
      },
      {
        title: "Clearer Sign-In Feedback",
        items: [
          "Sign-in now translates common invalid-credential backend messages into a clearer \"email or password is incorrect\" style response.",
          "This gives customers a more understandable reaction when they intentionally or accidentally enter the wrong password.",
          "Generic sign-in failures now resolve to a safer fallback message instead of leaving the raw backend message unchanged."
        ]
      },
      {
        title: "Safer Email Verification UX",
        items: [
          "The email two-factor screen still sends a six-digit code by email after the correct password is entered for customers who enabled this protection.",
          "If the code is wrong, expired, or cannot be checked, the page now shows purpose-built messages instead of raw Gadget permission text.",
          "The resend button follows the same cleaned-up error mapping so support and customers can distinguish a normal invalid code from a temporary backend issue."
        ]
      }
    ]
  },
  {
    version: 146,
    date: "2026-04-03",
    title: "Customer Profile Security Controls Became Directly Usable Again",
    summary: "The customer profile redesign looked cleaner, but in production the `Manage Security Methods` action still felt broken and customers could not reliably reach the dedicated security page to disable email verification. The profile screen now includes a live inline email verification toggle and uses a browser-native security link, while the overall right-hand layout was tightened so the page feels more professional without excessive vertical drift.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Customers can now enable or disable email verification directly from the main profile page without depending on a second screen.",
      "The `Manage Security Methods` action now uses a direct browser navigation link to `/customer/profile/security` instead of relying on a JavaScript-only transition.",
      "The profile page layout was rebalanced again so sender address, security, and plan details stack more cleanly without stretching the page awkwardly."
    ],
    details: [
      {
        title: "Working Security Controls",
        items: [
          "The profile page security card now contains a live email verification switch, so customers can immediately turn sign-in verification on or off in the place where they discovered the setting.",
          "This removes the operational blocker where email verification could be enabled but not easily disabled when the separate management entry point failed to move the customer to the security page.",
          "Success and failure feedback now comes directly from the same email two-factor update action used elsewhere, keeping the behavior consistent."
        ]
      },
      {
        title: "Reliable Navigation",
        items: [
          "The security management CTA now uses a plain browser href to the dedicated `/customer/profile/security` screen, which is more resilient than the previous in-page navigation attempt.",
          "Customers who want the fuller security workspace can still move into the dedicated screen for the roadmap-style overview of email, SMS, and authenticator methods.",
          "This preserves the separate security page while making the main profile screen self-sufficient for the current real-world task."
        ]
      },
      {
        title: "Profile Page Layout Cleanup",
        items: [
          "The main profile layout was tightened so the right-side utility column no longer makes the overall page feel dragged downward.",
          "Sender address, sign-in protection, and plan information now form a more balanced vertical stack beside the account information card.",
          "The result is a cleaner, more professional profile page that keeps important actions visible without wasting space."
        ]
      }
    ]
  },
  {
    version: 145,
    date: "2026-04-03",
    title: "Customer Security Management CTA Now Opens the Dedicated Settings Screen Reliably",
    summary: "The profile redesign introduced a clearer dedicated security area, but the main profile CTA still was not opening that screen reliably in production. Security management actions now use an explicit page transition so customers can always reach the dedicated sign-in protection screen and manage email verification without the click silently doing nothing.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The `Manage Security Methods` action on the customer profile page now always opens the dedicated `/customer/profile/security` screen reliably.",
      "Customers can now consistently reach the page where email verification can be enabled or disabled.",
      "This fix was recorded as `v145` so support can distinguish the navigation repair from the earlier profile layout redesign."
    ],
    details: [
      {
        title: "Reliable Security Navigation",
        items: [
          "The profile page security CTA no longer depends on the previous in-place navigation behavior that was silently failing for some customers.",
          "The action now performs a direct page transition into the dedicated security management screen so the customer always lands in the correct workspace.",
          "The same reliable navigation behavior was applied to both the top-level profile action area and the compact security summary card."
        ]
      },
      {
        title: "Operational Impact",
        items: [
          "Customers who already have email verification enabled can now reach the screen where the method can be turned off again.",
          "Customers who want to enable or review sign-in protection no longer get stuck on the profile page with a non-responsive CTA.",
          "This repair keeps the two-screen profile and security architecture while removing the broken entry-point behavior that made it feel unfinished in production."
        ]
      }
    ]
  },
  {
    version: 144,
    date: "2026-04-03",
    title: "Customer Profile and Security Management Were Reshaped into a Cleaner Two-Screen Flow",
    summary: "The customer profile page was rebalanced again after the first security redesign still felt too tall and visually awkward in production. The profile screen now keeps a tighter account overview with clearer actions, while sign-in protection methods live in a more distinct dedicated security page where email verification can be managed cleanly and future SMS/app methods are presented as roadmap cards instead of stretching the main profile layout.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The customer profile page now uses a denser overview layout with a stronger top summary, tighter card spacing and a more balanced two-column composition.",
      "Security management is now much more obviously a separate flow, with dedicated navigation from the profile page into a standalone security screen.",
      "The security screen itself was redesigned away from stacked accordion-heavy content into a cleaner live-email plus future-method roadmap layout."
    ],
    details: [
      {
        title: "Profile Page Layout Cleanup",
        items: [
          "The main profile screen now opens with a more structured overview card that combines identity, completion progress, current plan and security state without wasting vertical space.",
          "Account information remains easy to edit, but sender address and plan management were reorganized into a tighter card layout so the page feels more professional and less visually stretched.",
          "An accidental leftover hidden block from the previous redesign was removed so the page no longer carries unnecessary structural baggage."
        ]
      },
      {
        title: "Dedicated Security Flow",
        items: [
          "The profile page now uses explicit navigation links for security management instead of relying on a less obvious in-place experience.",
          "Customers now move into a dedicated security management page when they choose to manage sign-in protection, making the transition more understandable and intentional.",
          "This keeps the profile page focused on account data while making security feel like its own management workspace."
        ]
      },
      {
        title: "Professional Security Screen Refresh",
        items: [
          "The security page now leads with a distinct header and status overview so customers immediately understand they are in a separate sign-in protection area.",
          "Email verification remains the active configurable method, while SMS verification and authenticator app support are shown as clearly marked coming-soon options in cleaner roadmap cards.",
          "This makes the page easier to scan and reduces the visual heaviness that came from the previous stacked method presentation."
        ]
      }
    ]
  },
  {
    version: 143,
    date: "2026-04-03",
    title: "Customer Profile Security Was Moved into a Dedicated Management Screen",
    summary: "The customer profile page became too tall and visually heavy after the new multi-method sign-in protection UI was added directly into the main profile layout. Security management now lives in its own dedicated screen, while the profile page keeps only a compact summary and a clear entry point to manage sign-in methods. The customer orders table was also adjusted so store names no longer appear empty when the source store label is missing in row data.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The customer profile page is compact again because the detailed sign-in protection controls now open on a separate `/customer/profile/security` screen.",
      "The dedicated security page presents email verification as the live method and keeps SMS and authenticator app options visible as future-ready sections without stretching the main profile layout.",
      "Customer order rows no longer show an empty store column when the source store name is missing; the selected store now fills that gap as a safe fallback."
    ],
    details: [
      {
        title: "Dedicated Security Management Screen",
        items: [
          "The profile page now shows a short security summary instead of embedding the full three-method configuration stack inside the main two-column profile grid.",
          "A new customer security route was added so sign-in verification methods can be managed in a focused screen without making the profile overview feel unbalanced or overly long.",
          "Email verification remains fully manageable there, while SMS and authenticator app sections stay visible as clearly marked future options."
        ]
      },
      {
        title: "Profile Layout Balance",
        items: [
          "The profile overview grid was adjusted so the right-hand side no longer forces the main account-information card to stretch vertically just because a neighboring card grows taller.",
          "The security card inside the main profile now acts as a concise status block with a single CTA that moves the customer into the dedicated management flow.",
          "This keeps the main profile page easier to scan while still preserving discoverability for account-security settings."
        ]
      },
      {
        title: "Customer Orders Store Fallback",
        items: [
          "Some order rows were receiving a blank `sourceStoreName`, which made the Store column render as `—` even though the page was already scoped to a known store.",
          "The orders screen now falls back to the currently selected store when row-level store display data is missing, so single-store views continue to show a useful store label.",
          "The same fallback behavior was also aligned with related export and preview handling to reduce repeat inconsistencies."
        ]
      }
    ]
  },
  {
    version: 142,
    date: "2026-04-03",
    title: "Customer Sign-In No Longer Breaks on Email Two-Factor Session Fields",
    summary: "The sign-in outage was caused by the email two-factor flow writing a custom pending-user field into the Gadget session payload, which the live session schema no longer accepted. Sign-in now carries pending two-factor state through a signed challenge token instead of mutating the session with unsupported fields, so normal customer logins work again and optional email two-factor continues to function.",
    confidence: "verified",
    audience: ["customer", "system", "admin"],
    highlights: [
      "Customer logins no longer fail with `InternalSessionInput` errors caused by the deprecated `pendingSignInEmailTwoFactorUserId_v1` session field.",
      "Optional email two-factor still works, but the pending sign-in context is now passed through a signed challenge token instead of an unsupported custom session property.",
      "This hotfix was recorded as `v142` so support can clearly identify the production build that restored customer access."
    ],
    details: [
      {
        title: "Sign-In Outage Fix",
        items: [
          "The previous flow always tried to clear or set a custom two-factor session field before finishing sign-in, even for customers who were not using two-factor authentication.",
          "Because the live Gadget session schema no longer exposed that field, the GraphQL session mutation failed before the normal `user` session link could be saved.",
          "The repair removes that unsupported dependency from the sign-in path entirely, which restores plain email/password login immediately."
        ]
      },
      {
        title: "Two-Factor Challenge Flow",
        items: [
          "When a customer has email two-factor enabled, sign-in now returns a signed challenge token that identifies the pending user and expiry without writing extra fields into the session.",
          "The verification and resend actions now validate that challenge token server-side before checking or issuing codes, preserving the optional two-factor guard without reviving the broken session behavior.",
          "The email-two-factor page now reads its pending state from the challenge in the URL and refreshes that challenge safely after resend."
        ]
      },
      {
        title: "Regression Coverage",
        items: [
          "Challenge token helpers now have dedicated tests for round-trip parsing, tamper rejection and expiry handling.",
          "API and web typechecks plus a full production build were rerun before deployment.",
          "This keeps the emergency sign-in repair visible in the release history instead of being hidden inside a later unrelated deployment."
        ]
      }
    ]
  },
  {
    version: 141,
    date: "2026-04-03",
    title: "Carrier Log Viewer Now Shows a Query Timeline Instead of a Raw File List",
    summary: "The admin carrier log sheet was reshaped into a clearer operational timeline so support teams can see when a tracking query ran, when it finished and what result came back without digging through raw S3 file names first. The latest detected outcome now stays visible at the top, matched runs read like a query history, and raw JSON remains available only as a secondary debugging view.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The right-hand carrier log sheet now leads with the latest detected result and a readable query history instead of centering the UI around request file names and storage details.",
      "Matched runs are now shown as timeline steps with request time, finish time, run state and key counters, which makes it much easier to understand what happened during each carrier-check pass.",
      "This design follow-up was recorded as `v141` so the live timeline-style log viewer remains distinguishable from the earlier storage-read and scroll-safety fixes deployed the same day."
    ],
    details: [
      {
        title: "Readable Query Activity",
        items: [
          "The matched carrier-check runs section now behaves like an operational activity stream instead of a flat request-id list.",
          "Each run surfaces when the tracking was queried, whether the run finished cleanly and the most useful counters from that pass, such as matched labels, completed labels, carrier-open counts and errors.",
          "This gives support a much faster way to answer the real question of what happened to a label without mentally reconstructing the sequence from raw file metadata."
        ]
      },
      {
        title: "Worker Timeline First, Raw JSON Second",
        items: [
          "The latest detected result now stays summarized at the top so the current outcome is visible before anyone opens the deeper timeline.",
          "Worker events are grouped into a dedicated timeline view that reads as a chronological log stream, while the raw S3 payloads were moved into a secondary technical tab for debugging only.",
          "That keeps the interface useful for day-to-day operations without removing access to the underlying JSON when deeper investigation is still needed."
        ]
      },
      {
        title: "Versioned Production Follow-Up",
        items: [
          "This entry intentionally records the timeline-oriented carrier log redesign as a fresh version instead of silently overwriting the earlier carrier-log repairs from the same day.",
          "Keeping the redesign in `v141` makes it clear which production build replaced the overly technical file-centric viewer with the more readable query timeline.",
          "A new production deployment was completed again after adding this release note so the live version badge and release history stay aligned with the current UI."
        ]
      }
    ]
  },
  {
    version: 140,
    date: "2026-04-03",
    title: "Platform Detail Customer Names Now Resolve from Account Ownership Instead of Falling Back to Unknown",
    summary: "Admin platform detail pages now recover customer identity more reliably when a store row arrives without a hydrated `user` relation. The analytics job now uses the store's `userId` to look up the owning customer account, includes first and last name fields in its display-name logic, and falls back to the account/store name only when a customer record still cannot be reconstructed.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Connected account rows on platform detail pages no longer default to `Unknown customer` just because the nested customer relation was missing in one query shape.",
      "Customer display names now consider company name, first/last name and email before falling back, which makes the admin table and top-customer summary much more recognizable.",
      "This repair was recorded as `v140` so support can distinguish the customer-identity follow-up from the earlier platform-metric fixes deployed the same day."
    ],
    details: [
      {
        title: "Customer Resolution by User ID",
        items: [
          "The platform insight calculation now builds a customer lookup map from the full admin customer list and uses each store's `userId` to recover the owner when the direct nested `store.user` payload is absent.",
          "That means existing connected marketplace accounts can still show the correct customer even when the analytics query receives a partial relation shape from one of the underlying models.",
          "The same recovered customer object is used for the detail table rows and the derived top-customer summary so both views stay aligned."
        ]
      },
      {
        title: "Better Display Name Fallbacks",
        items: [
          "Customer naming now prefers company name first, then a combined first and last name, then email, instead of skipping straight from company name to email or ID.",
          "If a customer record still cannot be reconstructed, the platform analytics row now falls back to the connected account or store name so admins still get a meaningful label instead of a generic unknown state.",
          "This makes live platform drilldowns easier to audit when imported or older accounts have thinner profile data than newer customer records."
        ]
      },
      {
        title: "Versioned Production Repair",
        items: [
          "This entry intentionally captures the customer-name follow-up as its own support deployment instead of silently rewriting the earlier platform analytics versions.",
          "Keeping the fix in `v140` makes it clear which live build replaced the remaining `Unknown customer` rows reported from production.",
          "The version badge and release history therefore remain aligned with the actual support sequence from the same day."
        ]
      }
    ]
  },
  {
    version: 139,
    date: "2026-04-03",
    title: "Customers Can Now Opt Into Email Two-Factor Sign-In",
    summary: "Customer accounts can now enable an optional email-based two-factor step from the profile page. When enabled, password sign-in pauses until the user enters a fresh 6-digit code delivered by email, and admin customer detail pages now surface whether that protection is active.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Customer profile pages now include a dedicated security card where each customer can turn email two-factor authentication on or off without making it mandatory for everyone.",
      "The sign-in flow now supports a second verification step for protected customer accounts, including code delivery, resend support and a dedicated verification screen.",
      "Admin customer detail pages now show the two-factor status immediately, so support teams can see whether an account has the extra sign-in guard enabled."
    ],
    details: [
      {
        title: "Optional Customer Security Upgrade",
        items: [
          "A new email two-factor toggle was added to the customer profile page so customers can opt into stronger sign-in protection themselves.",
          "The feature is intentionally optional: customers who do not enable it continue to sign in with the existing email and password flow.",
          "The setting is stored directly on the customer user record so it survives normal sessions and remains visible across the app."
        ]
      },
      {
        title: "Two-Step Email Verification Flow",
        items: [
          "After a correct password is entered for a protected customer account, the app now issues a fresh 6-digit verification code and emails it with a Kenpost-branded template.",
          "The sign-in session stays pending until the user enters the code on the new verification screen, where they can also request a new email if needed.",
          "Verification challenges are time-limited and cleared after successful use, which keeps the email step lightweight while still tightening account access."
        ]
      },
      {
        title: "Admin Visibility and Support Context",
        items: [
          "Customer detail pages in admin now show whether email two-factor authentication is enabled or disabled right in the account overview area.",
          "That gives support and operations staff quick context when a customer reports sign-in friction or asks why an extra code is required.",
          "This release note records the customer-facing security rollout as its own support version so the admin Versions page stays aligned with the live change."
        ]
      }
    ]
  },
  {
    version: 138,
    date: "2026-04-03",
    title: "Recent Admin and Store-Setup Screens Now Read from Central Language Files",
    summary: "The language sweep moved the most recently added admin and customer setup screens out of scattered in-file locale blocks and into shared translation files. Platform analytics, admin settings pages, the carrier log sheet and several customer store-connection flows now resolve their UI copy through the central i18n layer, making later translation maintenance much safer.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The admin Platforms overview and detail pages now read their titles, summaries, CTA text and empty states from shared language files instead of hardcoded English strings.",
      "Admin Settings, Profile, Error Logs and Versions were moved onto the same translation path, so the release-history hub and related settings screens are now covered by centralized i18n too.",
      "Customer store setup flows for the selector, JTL, AboutYou and Otto now pull setup instructions and field copy from the centralized message tree, and the i18n audit tool now ignores dedicated translation files correctly."
    ],
    details: [
      {
        title: "Central Page Message Layer",
        items: [
          "A new page-level translation source was added so larger screen-specific copy blocks can live beside the main message tree without being duplicated inline inside route components.",
          "The i18n helper layer now resolves both flat string keys and structured page objects from the shared translation sources, which makes complex setup instructions and card copy reusable without hardcoded locale objects.",
          "This keeps future support work from silently adding another isolated `copyByLocale` block each time a new admin tool or setup page is introduced."
        ]
      },
      {
        title: "Screens Brought into Shared i18n",
        items: [
          "Admin Platforms overview/detail, the carrier log viewer sheet, the admin settings hub and its Profile/Error Logs/Versions pages now use shared translation data.",
          "Customer-facing store setup coverage was extended to the store selector plus JTL, AboutYou and Otto connection screens, including instructional content and field helper text.",
          "Platform-specific descriptions shown in these newer screens are now also localized from the same shared source instead of falling back to English config descriptions."
        ]
      },
      {
        title: "Audit and Maintainability",
        items: [
          "The translation audit script was updated so dedicated translation source files are excluded from heuristic leftover scans.",
          "This reduces false positives when auditing the UI for untranslated strings and makes the sweep easier to repeat after future releases.",
          "The release history was bumped to `v138` so this i18n cleanup remains visible as its own production deployment step."
        ]
      }
    ]
  },
  {
    version: 136,
    date: "2026-04-03",
    title: "Manual Platform Analytics Now Treat Every Customer as a Default Account",
    summary: "The admin Platforms overview now handles the Manual card differently from marketplace stores: instead of looking for explicit store connections, it treats every customer account as having a default manual label account. Manual usage therefore reflects all customer accounts, while manual order and label totals are derived from real `sourcePlatform=manual` orders and the labels created from those manual shipments.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The Manual platform card and detail page now list every customer account instead of staying empty when no dedicated manual store record exists.",
      "Manual order totals now come from real `manual` orders created through the create-label flow, and manual label totals come from the linked manual shipment and label records.",
      "The release history was bumped again so the manual-platform analytics follow-up remains visible as its own deployed repair."
    ],
    details: [
      {
        title: "Default Manual Accounts",
        items: [
          "The platform insight job now synthesizes manual accounts from customer records, because the manual create-label flow is available by default for every customer rather than depending on a normal store connection.",
          "That means the Manual detail page now shows all customers in its account table, even when a customer never created a dedicated manual store entry.",
          "The synthetic manual rows are clearly marked as default accounts instead of pretending they came from marketplace connection data."
        ]
      },
      {
        title: "Manual Usage Metrics",
        items: [
          "Manual synced-order totals now come from orders whose `sourcePlatform` is `manual`, matching the actual create-label workflow.",
          "Manual label totals are attributed from the manual shipments and linked label-source consumption records, so create-label output is reflected in the same analytics surface.",
          "Because every customer has access to the manual flow by default, the manual usage rate now reports full adoption instead of behaving like a normal optional store platform."
        ]
      },
      {
        title: "Versioned Follow-Up",
        items: [
          "This version intentionally follows the earlier platform analytics repairs from the same day instead of silently rewriting them.",
          "Keeping the manual-account adjustment in `v136` makes it easy to identify the deployment that corrected the still-empty Manual platform card.",
          "The live version badge and release-notes page therefore stay aligned with the actual support sequence."
        ]
      }
    ]
  },
  {
    version: 135,
    date: "2026-04-03",
    title: "Admin Platform Detail Pages Now Derive Label Counts from Real Shipment and Label Links",
    summary: "The platform analytics follow-up now fixes the last misleading metric on the detail pages: label totals no longer stay at zero just because the analytics job relied too heavily on bare relation IDs. The calculation now resolves store ownership through shipment, order and label-source links together, so marketplace detail screens reflect real label usage alongside the already-correct synced order counts.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Per-platform detail cards and account rows now show real label totals instead of falling back to zero when relation IDs are missing or sparse in one part of the analytics query.",
      "The platform insight job now joins shipment, order and labelSource data together, which makes label attribution resilient for printed shipments, consumed labels and void-tracking patterns.",
      "This support pass was recorded as a fresh version so the live repair for platform label analytics remains separate from the earlier permission and route fixes."
    ],
    details: [
      {
        title: "Robust Label Attribution",
        items: [
          "The platform analytics calculation no longer assumes that `shipment.orderId` and `order.storeId` will always be enough on their own to recover a store relationship reliably.",
          "It now reads the related order and store objects directly, keeps tracking-based fallbacks for void-style tracking numbers and uses linked `labelSource` records when they provide the strongest ownership signal.",
          "That means connected marketplace accounts can now surface the labels they really generated even when the underlying records were populated through slightly different relation shapes."
        ]
      },
      {
        title: "More Accurate Detail Rows",
        items: [
          "Each connected account row on `/admin/platforms/:platform` now derives its label totals from the same strengthened mapping used by the summary cards above it.",
          "Linked label sources are counted first, then unmatched shipments are used as a fallback so historical labels are not lost just because a source record is missing or was recycled.",
          "The last label activity timestamp is also taken from the best available signal, preferring consumed or assigned label times before falling back to shipment creation time."
        ]
      },
      {
        title: "Versioned Production Follow-Up",
        items: [
          "This version intentionally captures the platform-label analytics repair as its own release step after the earlier route, permission and usability fixes from the same day.",
          "Keeping this as `v135` makes it easier to identify which production build corrected the still-zero label counts reported from live platform detail pages.",
          "The release history therefore stays aligned with the actual support sequence instead of silently folding this analytics correction into the previous entry."
        ]
      }
    ]
  },
  {
    version: 134,
    date: "2026-04-03",
    title: "Admin Platform Analytics Now Loads Through a Privileged Endpoint and Carrier Logs Show a Usable Snapshot",
    summary: "The earlier admin Platforms rollout was repaired again after production still showed permission errors and zero values on some accounts. Platform analytics now load through a dedicated authenticated REST endpoint instead of the blocked route-loader path, while the carrier log viewer was widened, made scroll-safe, and upgraded to show the newest worker result summary before the full timeline.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The Platforms overview and per-platform drilldown now fetch data from a dedicated admin-only `/api/platform-insights` endpoint, removing the permission-denied path that still left some production users with empty metrics.",
      "The shared platform insight calculation was split away from session-bound action execution, so both the admin action and the REST endpoint now reuse the same internal analytics logic without depending on a browser GraphQL permission context.",
      "The carrier log viewer now opens in a wider, scroll-safe sheet and surfaces the latest detected result, worker message, request ID and file path up front instead of forcing admins to infer the outcome from basic counters alone."
    ],
    details: [
      {
        title: "Platform Analytics Repair",
        items: [
          "The admin Platforms screens no longer depend on a route loader that indirectly re-ran the analytics action with a less privileged API context.",
          "Both pages now call a dedicated server route that validates the signed-in admin, executes the internal insight calculation on the server and returns the overview or selected-platform payload directly.",
          "That closes the remaining production gap where `/admin/platforms` or `/admin/platforms/shopify` could still show `GGT_PERMISSION_DENIED` and fallback zeros even though the route structure itself had already been repaired."
        ]
      },
      {
        title: "Reusable Server Logic",
        items: [
          "The core marketplace insight aggregation was refactored into a shared server helper so the calculation can be reused safely outside the action trigger path.",
          "The original admin action still exists for internal reuse, but authentication is now separated cleanly from the actual insight computation.",
          "This makes the metrics path more predictable and avoids tying production admin pages to whichever session shape the browser GraphQL client happened to provide."
        ]
      },
      {
        title: "Carrier Log Viewer Usability",
        items: [
          "The right-hand carrier log sheet was widened and given a safer scroll container so long log sessions no longer feel cut off or frozen on desktop.",
          "A new top summary block now highlights the newest detected worker outcome, extracted snapshot fields and the underlying log file path before the operator opens the detailed timeline or raw JSON tabs.",
          "This version records both the platform repair and the log-viewer usability pass together because they were completed and deployed in the same support iteration."
        ]
      }
    ]
  },
  {
    version: 133,
    date: "2026-04-03",
    title: "Admin Platform Analytics Now Loads Server-Side with a Working Detail Drilldown",
    summary: "The new admin Platforms analytics screen was repaired after the first rollout surfaced two production issues: the browser-side analytics request hit a `GGT_PERMISSION_DENIED` wall and the direct per-platform route did not resolve reliably. The page now loads its analytics on the server, the detail drilldown opens correctly, and the release history records the repaired rollout as its own version.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Platform totals and per-platform usage cards now load through server route loaders instead of a client GraphQL action, which removes the production permission failure that previously forced the screen to show zeros.",
      "The Platforms route was restructured into a real layout plus index and detail routes, so direct links such as `/admin/platforms/shopify` now open the intended drilldown page.",
      "The repaired rollout was deployed again after build and validation so the live admin screen and version history stay aligned."
    ],
    details: [
      {
        title: "Server-Side Data Loading",
        items: [
          "Both the overview screen and the per-platform detail screen now fetch analytics during route loading on the server, reusing the same admin-only insight calculation without depending on browser GraphQL permissions.",
          "This change fixes the production state where the cards rendered fallback zeros because the browser request failed before the analytics action could run.",
          "Manual refresh on both screens now revalidates the route loader instead of reissuing the blocked client-side action call."
        ]
      },
      {
        title: "Reliable Route Structure",
        items: [
          "The Platforms area now follows the same route pattern already used by working nested admin sections, with a dedicated layout route, an index route for `/admin/platforms`, and a child detail route for `/admin/platforms/:platform`.",
          "That fixes direct navigation into platform detail URLs and keeps the drilldown reachable both from the platform cards and from pasted or bookmarked links.",
          "The detail screen continues to show customer, account, synced-order, label and status information for each connected marketplace account."
        ]
      },
      {
        title: "Validation and Follow-Through",
        items: [
          "The repaired route and loader flow was revalidated with a fresh production build and a Gadget problem scan before redeployment.",
          "A new version entry was added instead of silently overwriting the previous rollout note, so the repaired production pass remains easy to identify in version history.",
          "This version records the fix that made the earlier platform analytics rollout actually usable in production."
        ]
      }
    ]
  },
  {
    version: 132,
    date: "2026-04-03",
    title: "Carrier Log Viewer Now Resolves Flat S3 Log Files Reliably",
    summary: "The admin label-check log viewer was corrected so labels with existing per-tracking S3 JSON logs no longer appear empty just because the worker stored them under a flat `tracking/logs/<tracking>.json` key or because the runtime resolved S3 credentials through config-backed storage settings.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The carrier log viewer now checks exact flat S3 log keys such as `tracking/logs/<tracking>.json` before falling back to older prefix-based scans.",
      "Carrier-log reads now also use the app's config-backed storage credentials instead of depending only on `process.env`, which makes production log access much more reliable.",
      "The fix was deployed to production in the same support thread after targeted regression tests confirmed the flat-key lookup path."
    ],
    details: [
      {
        title: "Flat Log Key Support",
        items: [
          "The label-check log lookup flow now targets exact per-label log files directly, including the flat object pattern used by the current worker output instead of assuming every log lives inside a dated request folder tree.",
          "This prevents existing logs from being missed when the only real file is something like `tracking/logs/702813532387.json`.",
          "Older request-specific and date-based prefix scans still remain as fallback paths for legacy or partially migrated log layouts."
        ]
      },
      {
        title: "Production S3 Read Reliability",
        items: [
          "The admin carrier-log action now passes app config into the log reader so S3 object reads can resolve credentials and bucket settings through the same config-backed storage helpers already used elsewhere in the system.",
          "That closes the gap where an existing S3 object could still fail to appear in the UI because the log reader depended only on process environment variables at runtime.",
          "Exact-log reads and related query-file inspections now share that safer config-aware access path."
        ]
      },
      {
        title: "Validation and Rollout",
        items: [
          "Targeted tracking-log tests were rerun after the flat-key and config-backed read changes, and the updated regression set passed locally before release preparation.",
          "The version history was bumped to `v132` in the same support pass so the fix is now recorded explicitly instead of being folded into the older initial log-viewer release.",
          "Production deployment was completed again after adding this version entry so the live app and release-notes page stay aligned."
        ]
      }
    ]
  },
  {
    version: 131,
    date: "2026-04-03",
    title: "Admin Platform Analytics Now Shows Connected Accounts, Usage Share and Per-Customer Detail",
    summary: "The admin Platforms screen was upgraded from a static availability toggle page into a real marketplace analytics view. Admins can now see how many stores are connected per platform, how many orders were synced, how many labels were created, which platform is used most, and can open a dedicated detail screen listing the customers and accounts behind those numbers.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The admin Platforms overview now shows live marketplace metrics per platform, including connected store count, synced orders, label volume and usage share.",
      "Each platform card now links to a dedicated detail page that lists which customer connected which account and how many orders and labels that account produced.",
      "The existing admin availability toggle was kept in place, so operational enable or disable control still lives alongside the new analytics view."
    ],
    details: [
      {
        title: "Platform Overview Analytics",
        items: [
          "The former static admin Platforms page was redesigned into a dashboard-style overview that combines platform availability controls with real operational metrics.",
          "Each marketplace card now exposes connected-store totals, inactive-store counts, synced-order volume, created-label totals and a usage-share progress indicator so the most-used platforms are visible at a glance.",
          "A new top-level summary row also highlights the most-used platform across the system together with total connected stores, synced orders and labels created."
        ]
      },
      {
        title: "Per-Platform Detail Drilldown",
        items: [
          "A dedicated admin route was added for individual platforms, making each card clickable and opening a detail table for that marketplace.",
          "The detail screen shows which customer connected which store or account, the connection identifier, pulled-order volume, created-label volume, account status and recent activity timestamps.",
          "Amazon and eBay connection state are resolved more carefully than simple store rows, so their admin detail state can reflect connected, pending, expired, reconnect-required or error conditions instead of only active or inactive store flags."
        ]
      },
      {
        title: "Validation and Release",
        items: [
          "The new analytics action and both admin routes were validated with successful production builds and a clean Gadget problem scan before release preparation.",
          "Because the working directory also contained unrelated local changes, the production rollout was executed from a clean Gadget sync workspace that copied only the platform-analytics files needed for this release.",
          "This version entry intentionally records the completed admin platform analytics rollout as its own release step so the new marketplace visibility work remains easy to find in version history."
        ]
      }
    ]
  },
  {
    version: 130,
    date: "2026-04-03",
    title: "Customer Pricing Screens Now Sort by Base Price with a Cleaner Customer Selector",
    summary: "The admin customer-pricing workflow was tightened in the same support pass: label products now appear in stable base-price order on both pricing entry points, customer IDs remain visible for support work, and the left-hand customer selector was redesigned so the added identity metadata no longer feels cramped.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The pricing grid now orders label products by ascending base price in both the customer detail pricing tab and the bulk Customer Prices page.",
      "Customer IDs are now visible directly inside the left-hand selector cards, but in a dedicated chip instead of being squeezed onto the same line as the email address.",
      "The customer selector column and card layout were rebalanced with more width, spacing and cleaner hierarchy so support can scan customers faster without the panel feeling crowded."
    ],
    details: [
      {
        title: "Pricing Order Consistency",
        items: [
          "The shared customer-pricing editor now sorts its product rows by `basePrice` on the client, which keeps both admin pricing entry points aligned even when the source product query arrives in name order.",
          "Equal-price products still fall back to a stable name and carrier comparison so the grid does not visually jump around between reloads.",
          "A short-lived attempt to request server-side sorting by `basePrice` was rolled back after it triggered an internal server error on the production route, leaving the safer client-side ordering as the permanent path."
        ]
      },
      {
        title: "Customer Selector Readability",
        items: [
          "The left-hand customer cards were widened and given roomier padding, which reduces the crowding that appeared after customer IDs were added for support visibility.",
          "Email remains on its own line, while the customer ID now sits in a compact dedicated badge and any secondary person label appears as a separate pill instead of competing with the main contact line.",
          "Selection styling was also softened and clarified so the active customer stays obvious without making the entire list feel visually heavy."
        ]
      },
      {
        title: "Validation and Release",
        items: [
          "The pricing and selector changes were validated with TypeScript no-emit checks, successful production builds and clean Gadget problem scans before rollout.",
          "The UI updates were deployed to production during the same support thread so the live admin screen now reflects the sorted pricing grid and the cleaner selector layout.",
          "This version entry intentionally records the finished pricing-screen polish as its own step in history instead of burying it inside broader admin cleanup notes."
        ]
      }
    ]
  },
  {
    version: 129,
    date: "2026-04-03",
    title: "Broader Admin and Customer Localization Coverage with a Cleaner Language Switcher",
    summary: "A fresh localization sweep connected many more admin and customer screens to locale-aware copy, including operational tables, dialogs, filters, onboarding surfaces and profile flows that still showed hardcoded English. The shared language switcher was also simplified by removing its redundant static label, keeping the header cleaner while EN, TR and DE switching behavior stays the same.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin and customer pages that still mixed hardcoded English into translated flows now read their headings, filters, tables, dialogs, validation messages and empty states from locale-aware copy blocks.",
      "Admin label-source and label-check tooling, including detail, log and history side panels, now follow the active interface language more consistently during support work.",
      "The shared language switcher no longer renders a separate static language label beside the EN, TR and DE toggle, which cleans up every shell that uses the shared header control."
    ],
    details: [
      {
        title: "Expanded Locale Coverage",
        items: [
          "Customer-facing pages such as profile, onboarding, balances, affiliate, buy-label, shipment, label-inventory, order-management and manual-label creation flows were revisited so visible UI copy no longer falls back to scattered hardcoded English strings.",
          "Admin-facing pages including customer operations, shipments, commissions, settings, canceled-label and missing-label workflows were aligned to the same route-local locale pattern, covering summaries, filter controls, action buttons, tables and status messaging.",
          "Additional store setup surfaces, including the store chooser and several marketplace onboarding pages, were also updated so the broader account-setup flow behaves more predictably across supported languages."
        ]
      },
      {
        title: "Support and Settings Polish",
        items: [
          "The admin label-source audit flow and the label-check operation screens now localize their side sheets, history dialogs, log viewers and user-facing queue feedback instead of mixing translated page chrome with English internals.",
          "Settings-related admin and customer profile surfaces were tightened so account details, password actions, empty values and helper text now follow the selected locale consistently.",
          "The header-level language switcher was simplified by removing the extra static label, leaving the compact segmented control as the only visible affordance."
        ]
      },
      {
        title: "Validation and Follow-Through",
        items: [
          "The release was prepared from a full UI rescan driven by live screenshots and route-by-route audits instead of only patching the first reported pages.",
          "TypeScript no-emit validation was rerun after the localization pass so the new copy structures and helper wiring stayed type-safe.",
          "This release represents another broad localization maintenance pass; a smaller long-tail of connector-specific store setup screens remains available for follow-up cleanup."
        ]
      }
    ]
  },
  {
    version: 128,
    date: "2026-04-03",
    title: "Invoices Now Show the Customer Identity and Optional VAT ID from Profile",
    summary: "Invoice generation was corrected so the billed customer is now clearly identified from the customer profile instead of falling back to incomplete order-recipient data. Customers and admins can now save an optional tax or VAT identifier on the customer profile, and invoices print that value automatically whenever it exists.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Customer invoices now render the buyer name and address from the actual customer profile snapshot, so PDFs no longer leave the recipient ambiguous.",
      "An optional customer tax or VAT ID field was added to both the customer profile and the admin customer edit flow.",
      "Generated invoice PDFs now print the saved tax or VAT ID in the buyer block whenever the customer has provided one."
    ],
    details: [
      {
        title: "Buyer Snapshot and PDF Accuracy",
        items: [
          "The invoice buyer resolution flow now prefers the saved customer profile and buyer snapshot before older order-recipient data, which fixes PDFs that previously showed a generic or incomplete customer block.",
          "Store-order invoice generation now persists a real buyer snapshot from the customer profile at creation time, so later downloads continue to show the correct billed party.",
          "Credit-purchase and manual-admin invoice flows were aligned to use the same buyer snapshot shape so invoice rendering stays consistent across document types."
        ]
      },
      {
        title: "Customer Tax ID Support",
        items: [
          "A new optional customer `taxId` profile field was added to the user model and normalized for cleaner storage, including common EU formats such as `DE...` VAT identifiers.",
          "Customers can now maintain this field directly from their own profile, and admins can also view and edit it from the customer detail screen when supporting account setup.",
          "When a tax or VAT ID exists, the invoice PDF now prints it in the buyer section so finance and compliance workflows have the required identifier on the document."
        ]
      },
      {
        title: "Validation and Release",
        items: [
          "Targeted regression coverage was added for buyer snapshot resolution, tax-ID normalization and admin customer-profile updates so the new invoice behavior stays locked in.",
          "The work passed focused automated tests and a full TypeScript no-emit check before release preparation.",
          "This release note was added in the same support pass as the production deployment request for the invoice buyer and VAT-ID improvements."
        ]
      }
    ]
  },
  {
    version: 127,
    date: "2026-04-03",
    title: "Admin Label Inventory Views Now Show Correct Multi-Status Counts with Cleaner Layouts",
    summary: "Admin label inventory visibility was expanded across both Dashboard and Label Products so teams can switch between all, available, allocatable, assigned, consumed, void and check-error counts without losing count accuracy. The product table now exposes the raw status totals explicitly, the zero-count regression caused by relation-shaped product ids was fixed, and both admin screens were tightened visually so the inventory area no longer pushes the main content too far down the page.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Dashboard and Label Products now share a tabbed per-product inventory panel that lets operators inspect the same label pool through all, available, allocatable, assigned, consumed, void and check-error views.",
      "The Label Products table now distinguishes raw available stock from actually allocatable stock instead of hiding carrier-check-error labels inside the main available number.",
      "A production-facing regression that could zero out every product count when `labelProductId` arrived as a relation object instead of a plain string was fixed, and both admin screens were visually compacted to avoid oversized empty vertical space."
    ],
    details: [
      {
        title: "Clearer Admin Inventory Breakdown",
        items: [
          "The admin label inventory experience now surfaces separate totals for all labels, raw available labels, allocatable labels, assigned labels, consumed labels, void labels and available labels blocked by carrier-check errors.",
          "This means support teams can answer customer questions with the exact number they mean instead of mixing up raw availability with the smaller operationally assignable pool.",
          "The same status tabs are now available in both the admin dashboard and the dedicated Label Products screen so the two views stay aligned."
        ]
      },
      {
        title: "Count Accuracy and Regression Fix",
        items: [
          "The new inventory summary flow was hardened to normalize label-product identifiers before aggregation, which fixed the zero-count regression seen when Gadget returned nested relation objects instead of direct string ids.",
          "The Label Products table still keeps the allocatable stock logic visible, but raw available counts now include carrier-check-error labels and expose those blocked labels separately instead of silently subtracting them.",
          "Targeted regression coverage was expanded around the inventory summarizer and dashboard summary path so this mismatch stays locked down."
        ]
      },
      {
        title: "Layout Cleanup",
        items: [
          "Both admin inventory screens were tightened visually by reducing oversized section spacing and switching the new inventory cards into a denser grid presentation.",
          "This keeps the main action areas and tables closer to the fold while preserving the new stock insight panel.",
          "The release was verified with targeted inventory and dashboard tests, successful production builds and the production deployment completed for this support thread."
        ]
      }
    ]
  },
  {
    version: 126,
    date: "2026-04-03",
    title: "Historical Credit Purchases Now Recover Product Names and Pricing End to End",
    summary: "Legacy customer label purchases no longer get stuck with blank product names or zero-valued credit invoices after customer-specific pricing is entered later. Historical repricing now runs automatically from the pricing save flow, admin and customer purchase views read safer product fallbacks, and generated credit-invoice PDFs now identify the purchased label product explicitly.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Saving customer-specific label prices now automatically reprices matching historical purchase rows in the backend instead of depending on a separate frontend-only repair step.",
      "Admin customer detail views, customer balance history and customer credit invoice lists now keep showing the purchased label product even when a nested `labelProduct` relation arrives sparsely.",
      "Credit-invoice PDFs now include the purchased product name in the line title, so invoice documents no longer hide which label product was actually bought."
    ],
    details: [
      {
        title: "Automatic Historical Repricing",
        items: [
          "The customer-pricing save path was hardened so historical label purchases are repriced automatically as part of the same backend flow that stores customer-specific prices.",
          "This removes the earlier gap where support could save the correct custom price but older purchase rows still remained at `0.00` until a second repair path happened to run.",
          "A production audit and backfill were completed for the already affected legacy rows, and regression coverage was added so the repricing behavior stays locked in."
        ]
      },
      {
        title: "Product Name Recovery Across Screens",
        items: [
          "Admin customer-detail purchase history and credit-invoice tables now fall back to explicit serialized product fields when the nested label-product snapshot is incomplete, so support can still see which label was sold.",
          "The same safer fallback pattern now also reaches customer-facing recent purchase history and customer credit-invoice lists, not just the admin views used during investigation.",
          "This keeps old manual allocations and legacy purchase rows understandable even when Gadget returns the relation object inconsistently."
        ]
      },
      {
        title: "Invoice Document Clarity",
        items: [
          "Generated credit-purchase PDFs now append the resolved label product name to the invoice line description instead of using only the generic logistics service label.",
          "That means invoice documents, admin invoice management and customer downloads all stay aligned on which concrete product was purchased.",
          "The release was revalidated through typecheck, targeted purchase and invoice tests, Gadget problem scanning and a fresh production deploy."
        ]
      }
    ]
  },
  {
    version: 125,
    date: "2026-04-03",
    title: "English-First UI Cleanup and Safer Purchase Snapshot Recovery",
    summary: "The platform was cleaned up after a broader system audit: mixed-language customer and admin screens were pushed back toward an English-first baseline, and stale purchase snapshots no longer override the real product and pricing information derived from linked label sources.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Customer and admin screens that still mixed Turkish or German copy into the main English product flow were cleaned up without changing the underlying workflows.",
      "Admin customer detail purchases and credit invoices now resolve product names and customer-specific pricing more safely when old purchase snapshots contain stale product data.",
      "A fresh health sweep was completed with passing app typechecks, build verification, problem scanning and targeted operational tests."
    ],
    details: [
      {
        title: "Language Consistency",
        items: [
          "The most visible mixed-language admin and customer pages were normalized toward English-first copy so the product no longer feels partially localized by accident.",
          "User-facing action responses and validation messages in critical shipping and store-connection paths were also aligned so backend responses do not reintroduce random Turkish text into the interface.",
          "The remaining audit leftovers are now largely limited to tests, seed data, parser keywords and intentionally language-specific PDF or carrier text."
        ]
      },
      {
        title: "Purchase and Invoice Accuracy",
        items: [
          "Old label-purchase rows that still carried stale inline product snapshots are now resolved against their linked label-source relations first, which keeps the real product identity from being masked by outdated purchase metadata.",
          "This prevents admin purchase history and credit invoice views from falling back to blank product names or zero pricing when the actual linked label source already proves which product was allocated.",
          "Customer-specific pricing therefore stays visible more reliably in both admin-facing purchase history and credit-invoice flows."
        ]
      },
      {
        title: "System Audit",
        items: [
          "A broader project scan was rerun across backend typecheck, web typecheck, production build, Gadget problem scanning and critical workflow tests to make sure the recent changes did not leave hidden breakage behind.",
          "The system completed that sweep cleanly, and the stale purchase-resolution edge case was additionally locked down with dedicated regression coverage.",
          "This release represents a maintenance and stability pass rather than a single isolated feature."
        ]
      }
    ]
  },
  {
    version: 124,
    date: "2026-04-03",
    title: "Per-Label S3 Carrier Logs in Admin Label Checks",
    summary: "Admin label checks now expose the per-label S3 JSON log history directly inside the UI, so operators can inspect the exact carrier-query timeline for a label without leaving Kenpost.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Each label row on the admin label-check screen now opens a log viewer that reads the matching S3 JSON log files and renders them in a right-side panel.",
      "The new log view distinguishes between a label that has never been queried and a label whose query log exists but has no matching result yet.",
      "Raw JSON remains available in the same panel so support teams can compare the rendered timeline against the source payload when debugging carrier-check results."
    ],
    details: [
      {
        title: "S3 Log Visibility",
        items: [
          "The admin label-check workflow now searches the per-label log folder structure in S3, resolves the latest matching JSON files and presents them as a readable event stream.",
          "If no S3 log file exists yet, the panel now shows that state explicitly instead of implying a broken label-check result.",
          "This makes carrier-check support work much faster because request history, status transitions and underlying JSON are available from the same operational screen."
        ]
      }
    ]
  },
  {
    version: 123,
    date: "2026-04-03",
    title: "Single Label Checks Now Queue Through S3 with Live Result Popups",
    summary: "Manual single-label checks no longer bypass the S3 workflow. The label-check screen now writes one-label JSON requests to S3, shows a live progress popup and displays the returned result in place when the fast background worker finishes.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Single `Check` or `Recheck` actions now write a one-record query file into S3 instead of using a direct synchronous carrier-check path.",
      "A dedicated popup shows the label as queued, waits for the expected fast result and then renders the returned status, messages and timestamps inside the same UI.",
      "If the response takes too long, the popup falls back to a busy-system message while the background job continues safely."
    ],
    details: [
      {
        title: "Single-Label Queue Flow",
        items: [
          "The admin label-check page now uses the same S3-driven query model for one-label checks that it already uses for larger filtered or bulk runs, which keeps the system behavior consistent.",
          "The new popup gives operators immediate visual feedback that the single-label request was written, picked up and processed, instead of leaving the user to guess whether anything happened.",
          "Because the response is still persisted back into the normal carrier-check fields, the table stays aligned with the popup result after the request finishes."
        ]
      }
    ]
  },
  {
    version: 122,
    date: "2026-04-03",
    title: "Automatic Six-Hour Label Check Export Scheduling",
    summary: "Label-check exports can now be generated automatically every six hours for the three main inventory states, reducing the need for repeated manual bulk queueing and keeping S3-backed result files continuously refreshed.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "A new scheduled action now prepares S3 query files every six hours for unassigned available labels, assigned unused labels and assigned consumed labels.",
      "The automatic scheduler reuses the same background queue and polling path as the manual filtered runs, so the export format stays identical.",
      "Runs that have no matching labels are skipped cleanly instead of producing noisy empty-state failures."
    ],
    details: [
      {
        title: "Scheduled Label Exports",
        items: [
          "The system now has a dedicated background scheduler that creates the three recurring label-check export slices required by the S3-driven carrier-check pipeline.",
          "This keeps the manifest and result lifecycle compatible with the existing downstream API while reducing operator effort on the admin label-check screen.",
          "The scheduling helper was built on top of the same filtered-queue infrastructure already used manually, so future label-check changes only need to be maintained in one place."
        ]
      }
    ]
  },
  {
    version: 121,
    date: "2026-04-03",
    title: "Consumed Labels Stay Informational During Carrier Rechecks",
    summary: "Carrier rechecks for labels that Kenpost has already consumed are now treated as informational status updates instead of faulty or void candidates, which prevents false negative inventory transitions while still exposing transit or delivery visibility.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Carrier responses such as `used`, `transit` or `delivered` no longer mark internally consumed labels as faulty or void.",
      "Consumed labels still keep their updated carrier-check status and detail text so operations can inspect the transport stage without harming inventory integrity.",
      "The label-check UI and history logic were aligned to reflect this distinction clearly."
    ],
    details: [
      {
        title: "Consumed Label Safety",
        items: [
          "The carrier-check interpreter now branches on consumed labels separately from available or merely assigned labels, so a successful in-system shipment is no longer treated like an external misuse event.",
          "This preserves the operational value of post-shipment carrier visibility while preventing already-used customer labels from being pushed into the faulty-label handling path.",
          "The admin label-check list and history output now stay consistent with this safer consumed-label interpretation."
        ]
      }
    ]
  },
  {
    version: 120,
    date: "2026-04-03",
    title: "Oldest-First Label Allocation with Faulty Stock Exclusions",
    summary: "Customer credit allocation and label purchasing now consume stock from the oldest eligible labels first and permanently exclude unusable inventory such as void labels or labels with carrier check errors.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The allocation pool now skips labels that are already void or have a `check error` carrier-check state.",
      "Eligible stock is consumed in ascending creation order so older labels are assigned before newer labels.",
      "Available-stock calculations were aligned with the same rule set to prevent the UI from advertising stock that the allocator will later reject."
    ],
    details: [
      {
        title: "Safer Inventory Allocation",
        items: [
          "Label assignment now filters out unusable inventory before selection, preventing customers from receiving labels that are already known to be broken or void.",
          "The allocator sorts on the oldest available label first so stale stock is reduced naturally instead of leaving old labels stranded while fresh ones are consumed.",
          "Purchase-time stock checks and admin allocation screens now rely on the same eligible-stock helpers, which keeps inventory reporting and actual assignment behavior in sync."
        ]
      }
    ]
  },
  {
    version: 119,
    date: "2026-04-03",
    title: "Admin Customer Overview Layout Refresh",
    summary: "The admin customer detail Overview tab was redesigned to reduce dead space, rebalance the content grid and make the account summary, recovery actions and sender details feel like one coherent operational dashboard.",
    confidence: "verified",
    audience: ["admin"],
    highlights: [
      "The Overview tab now uses a denser, better-balanced layout with less empty white space and fewer visual gaps between operational cards.",
      "Account summary, access recovery and sender profile sections were regrouped into a more structured overview composition.",
      "The earlier oversized empty right column was replaced with more compact status and action groupings."
    ],
    details: [
      {
        title: "Customer Detail Presentation",
        items: [
          "The admin customer detail screen now presents the Overview tab as a tighter dashboard instead of a stack of loosely related cards separated by large blank regions.",
          "Important support actions such as email verification, password recovery and sender-profile review now sit in a layout that is easier to scan without scrolling through excessive whitespace.",
          "This makes the customer detail landing view feel closer to the stronger visual language already used elsewhere in the admin interface."
        ]
      }
    ]
  },
  {
    version: 118,
    date: "2026-04-03",
    title: "Admin Customer Detail Tabs Now Persist Across Refresh",
    summary: "Admin customer detail tabs now stay on the currently selected section after a page refresh, making deep customer support work faster and easier to share through direct links.",
    confidence: "verified",
    audience: ["admin"],
    highlights: [
      "The selected admin customer detail tab is now stored in the URL query string.",
      "Refreshing the customer detail page keeps the operator on the same section instead of jumping back to Overview.",
      "Direct links to a specific customer sub-tab can now be shared with teammates."
    ],
    details: [
      {
        title: "State Persistence",
        items: [
          "The admin customer detail route now resolves its active tab from the URL, so the view can be refreshed safely during investigation work.",
          "Support teams can copy a tab-specific customer detail link and open the same Invoices, Purchases or Pricing view immediately.",
          "This small navigation change removes repeated tab switching during admin customer support sessions."
        ]
      }
    ]
  },
  {
    version: 117,
    date: "2026-04-03",
    title: "Admin Customer List Now Shows Customer IDs",
    summary: "The admin customers index now exposes the internal customer ID directly in the listing, helping support and operations teams jump to the correct account faster when an ID is referenced in logs, invoices or customer detail URLs.",
    confidence: "verified",
    audience: ["admin"],
    highlights: [
      "A dedicated ID column was added to the admin customers list.",
      "Customer IDs are now readable at a glance without opening the detail screen or inspecting the URL.",
      "The new column makes support conversations and operational backfills easier when an account is identified by numeric ID."
    ],
    details: [
      {
        title: "Customer List Improvements",
        items: [
          "The admin customers table now renders the customer ID as a distinct field instead of hiding it behind the detail route.",
          "This reduces lookup friction when the team is cross-referencing customer records against internal tooling, exports or direct admin URLs.",
          "The ID field was added without disrupting the rest of the customer summary layout."
        ]
      }
    ]
  },
  {
    version: 116,
    date: "2026-04-03",
    title: "Customer Pricing Page Rebuilt Around a Searchable Customer Workspace",
    summary: "The main customer-pricing screen no longer forces operators to work through a single dropdown. It now provides a searchable customer list and a focused editing workspace for the selected account.",
    confidence: "verified",
    audience: ["admin"],
    highlights: [
      "Customer selection moved from a cramped dropdown into a dedicated searchable customer list.",
      "The selected customer now stays visible while the pricing grid is edited in a larger workspace.",
      "The new layout makes high-volume pricing work much easier for support teams that frequently switch between customers."
    ],
    details: [
      {
        title: "Pricing Workspace UX",
        items: [
          "The customer-pricing screen now shows a persistent list of customers with integrated search instead of hiding the full selection set inside one dropdown.",
          "Once a customer is selected, the editing area clearly reflects which account is active so there is less risk of making changes against the wrong customer.",
          "This redesign keeps the actual price-editing behavior intact while making the selection and navigation workflow much more manageable."
        ]
      }
    ]
  },
  {
    version: 115,
    date: "2026-04-03",
    title: "Tracking Links Open in New Tabs Across the App",
    summary: "Tracking-number links now open in a new browser tab consistently across the Kenpost interface, preventing operators from losing their current context every time they inspect a shipment or label.",
    confidence: "verified",
    audience: ["admin", "customer"],
    highlights: [
      "The shared tracking-link component now opens tracking destinations in a new tab.",
      "Both the tracking number and its adjacent arrow icon follow the new-tab behavior.",
      "The change propagates automatically across every screen that already uses the shared tracking-link component."
    ],
    details: [
      {
        title: "Navigation Flow",
        items: [
          "Shipment and label tracking links no longer replace the current application page when an operator or customer opens a tracking target.",
          "Because the behavior was fixed in the shared component, the change now applies system-wide without having to update each table one by one.",
          "This removes a frequent source of navigation friction for teams reviewing many tracking numbers in sequence."
        ]
      }
    ]
  },
  {
    version: 114,
    date: "2026-04-03",
    title: "Customer Dashboard Refresh and Orders Filter Cleanup",
    summary: "The customer portal received a broader visual refresh: the dashboard now presents a richer, more colorful operational summary, and the orders page no longer lets long store or label names collide inside the filter controls.",
    confidence: "verified",
    audience: ["customer"],
    highlights: [
      "The customer dashboard was redesigned with a stronger hero area, richer metric cards and a more polished quick-action layout.",
      "Recent orders, shipments and balance signals now read more like a modern operational workspace instead of a sparse landing page.",
      "The customer orders filter controls now truncate long selected values cleanly instead of letting text overlap or spill across the dropdown trigger."
    ],
    details: [
      {
        title: "Customer Portal Presentation",
        items: [
          "The main customer dashboard now uses a fuller visual treatment that better reflects account health, recent activity and common navigation actions.",
          "The redesign keeps the same core data sources but presents them in a denser, more intentional composition with improved mobile and desktop spacing.",
          "The customer orders screen also received a related usability fix so long store names and label-product names no longer create stacked or overlapping text in the filter bar."
        ]
      }
    ]
  },
  {
    version: 113,
    date: "2026-04-03",
    title: "Optional Customer Mobile Phone Support",
    summary: "Customer accounts can now store an optional mobile phone number from registration onward, while keeping the existing signup and profile flows backward-compatible for older accounts.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Signup now accepts an optional mobile phone number without making the field mandatory.",
      "Customers can edit their phone number in the customer profile area and admins can update it from the customer detail flow.",
      "Existing users are not forced through any migration or validation breakage if they do not yet have a stored phone number."
    ],
    details: [
      {
        title: "Profile Data Expansion",
        items: [
          "The user model and shared customer profile handling were extended to store an optional mobile phone number for new or updated customers.",
          "The field was added carefully so current registrations and existing customer accounts continue to work even if no phone number is present.",
          "This gives support teams another contact path when urgent shipment or billing questions need follow-up."
        ]
      }
    ]
  },
  {
    version: 112,
    date: "2026-04-03",
    title: "Permission Audit and Missing Grant Hardening",
    summary: "A broader permission audit was added to reduce `GGT_PERMISSION_DENIED` failures, and the missing admin grants uncovered during that audit were wired into the access-control layer before release.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The access-control configuration now includes the missing admin grants that were previously triggering avoidable permission errors in operational screens.",
      "A dedicated UI permission audit test was added so future screens and actions are less likely to ship with missing grants.",
      "The audit covered both model permissions and global admin actions used in the current support workflow."
    ],
    details: [
      {
        title: "Access Control Reliability",
        items: [
          "The permission map was expanded to cover the admin actions and model operations that were still missing from the live grant set.",
          "A reusable audit test now checks the screens and actions relied on by the current admin workflow, making it easier to catch new permission gaps earlier in development.",
          "This reduces the frequency of disruptive runtime permission errors in high-touch operational areas."
        ]
      }
    ]
  },
  {
    version: 111,
    date: "2026-04-03",
    title: "Customer Detail and Pricing Workflow Enhancements",
    summary: "The admin customer-detail workflow received a set of quality-of-life upgrades, including a more usable overview of pricing-related work and supporting navigation improvements that make customer management faster during support sessions.",
    confidence: "verified",
    audience: ["admin"],
    highlights: [
      "Customer-specific pricing management now sits alongside other improved customer-detail workflows introduced in this support cycle.",
      "The customer-detail area is easier to navigate between pricing, purchase, invoice and operational follow-up work.",
      "These smaller customer-detail improvements now appear explicitly in version history instead of being buried inside larger operational releases."
    ],
    details: [
      {
        title: "Customer Detail Usability",
        items: [
          "This release reserves a dedicated version step in the history for the customer-detail quality improvements that were completed alongside the newer pricing and support tools.",
          "The goal is to keep the release timeline faithful to the actual operator-facing changes instead of compressing every detail-page improvement into one oversized summary entry.",
          "The detailed pricing workspace, tab persistence and overview cleanup releases that follow now build on this same customer-detail improvement track."
        ]
      }
    ]
  },
  {
    version: 110,
    date: "2026-04-02",
    title: "Label Check Auto-Refresh, Customer Filters and Missing Labels PDF Export",
    summary: "This release consolidates the full support work completed across the current conversation: admin label checks now support customer-based filtering and full filtered S3 queueing, the carrier-check history popup shows request IDs and stays in sync while pending runs remain active, background polling stops automatically when there is nothing left to check, and the Missing Labels page can export customer-free PDF reports for problematic labels.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin label checks now support customer-based filtering and send the full filtered result set into the S3-backed carrier-check flow instead of limiting the run to the currently rendered page.",
      "The carrier-check history popup now shows the request ID for each run, uses a pending-aware status model and auto-refreshes while work is still in flight so manual refresh is no longer required for active runs.",
      "Background tracking polling now stops on its own once there are no pending labels left, which avoids wasting system resources on repeated refresh requests after a run is finished.",
      "The Missing Labels page now includes a PDF export action that generates a customer-free report of problematic or used labels for sharing outside the system."
    ],
    details: [
      {
        title: "Label Check Workflow and S3 Queueing",
        items: [
          "The admin label-check screen now lets operators filter by customer and still send the full matched result set into the background carrier-check queue, so large filtered runs are no longer capped by the page size shown in the table.",
          "The queued run keeps its request ID visible throughout the history popup so the UI can be matched back to the S3 query and result snapshot that produced the report.",
          "Run status handling was adjusted so pending labels keep the run in an active state instead of showing it as completed too early."
        ]
      },
      {
        title: "History Popup and Auto-Refresh",
        items: [
          "The carrier-check history popup now refreshes itself while there are still pending runs, which keeps the displayed totals and status badges aligned with the background S3 polling flow.",
          "Manual refresh remains available for on-demand updates, but the popup no longer depends on user action to advance active runs.",
          "The history cards now present the request ID prominently so support teams can see which background run a report belongs to at a glance."
        ]
      },
      {
        title: "Missing Labels PDF Export",
        items: [
          "The Missing Labels page now provides a PDF download action for the current faulty-label view, making it easier to hand off a report to a third party.",
          "The generated PDF deliberately omits customer-identifying fields so the report can be shared externally without exposing private customer data.",
          "The export follows the active page filters so the PDF reflects the same operational slice that the user is reviewing in the interface."
        ]
      },
      {
        title: "Verification and Rollout",
        items: [
          "The related label-check, history, background polling and PDF export changes were verified through the local build and deployed after the workflow and reporting fixes were completed."
        ]
      }
    ]
  },
  {
    version: 109,
    date: "2026-04-02",
    title: "Customer Pricing Recovery, Manual Label UX Cleanup and Credit Invoice Repair",
    summary: "This release collects the full support work completed across the current conversation: admin customer pricing now loads, saves and reopens correctly from both the bulk pricing page and the customer detail screen, customer manual label creation gained clearer reset and corporate-recipient handling, and customer credit invoices or balance history now recover missing product and pricing context while the generated PDF invoices follow the new naming, VAT and numbering rules.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin customer-specific pricing was rebuilt so prices can be edited from the customer detail screen, persisted reliably, reopened without permission or render-loop failures and grouped under Billing instead of Labels.",
      "Customer manual label creation now supports clearer blocked-submit guidance, a visible form reset path, compact history actions and corporate-recipient formatting with the shared `c/o` rule.",
      "Customer balances, credit invoice lists, admin purchase views and purchase PDFs now recover legacy purchase context, resolve customer-specific prices correctly and stop defaulting to blank product names or `0.00` totals.",
      "Credit purchase PDFs now use the new invoice numbering style, a fixed service description and 19% VAT added on top of the net amount."
    ],
    details: [
      {
        title: "Admin Customer Pricing and Billing Recovery",
        items: [
          "Customer-specific label pricing was unified behind the shared pricing editor so the admin bulk pricing page and the customer detail Pricing tab now use the same save, load and reset behavior.",
          "Several follow-up failures from the live thread were fixed in the pricing flow, including null-price removal handling, missing action params, permission errors on reload, render loops on refresh, page-size crashes and duplicate-create conflicts when a row should have updated an existing customer price.",
          "The `Customer Prices` entry was also moved from the Labels navigation group into Billing to better match how operators think about customer-specific pricing."
        ]
      },
      {
        title: "Manual Label Workflow Improvements",
        items: [
          "The customer manual label screen now explains why the create button is disabled, gives the user a clear reset action for the single-label form and keeps the create flow aligned with the sender-profile and address requirements already active in the portal.",
          "Corporate-recipient handling was added to the manual label form with the same shared `c/o` formatting rule already used elsewhere, so company deliveries stay label-safe without inventing a second formatting path.",
          "Recent manual label actions were compacted into a shipment-style dropdown so PDF, PNG, JPEG, ZIP and regenerate controls no longer clutter each row."
        ]
      },
      {
        title: "Credit Purchases, Customer Balances and Invoice PDF Fixes",
        items: [
          "Customer invoice and balance reads were moved onto more resilient action-based loaders so credit history can still load even when one datasource is slow or partially unavailable.",
          "Legacy manual credit purchases now recover their linked label product and resolved customer price through label-source fallbacks, which fixes the earlier blank product names and `0.00` totals seen in both customer and admin purchase history.",
          "Credit purchase PDFs now use the fixed service description `Versand- und Logistikservice inkl. Versandetiketten`, apply 19% VAT on top of the net amount and format invoice numbers in the new `MAK<year>-<sequence>` style."
        ]
      },
      {
        title: "Verification and Rollout",
        items: [
          "This release was verified throughout the support thread with targeted pricing, invoice and helper tests, repeated successful TypeScript and production builds, and multiple production deployments after the save-path, permission, PDF and customer-read follow-up fixes were completed."
        ]
      }
    ]
  },
  {
    version: 108,
    date: "2026-04-02",
    title: "Label Workflow Hardening, Customer Profile Gate and Orders Recovery",
    summary: "This release captures the full thread of work completed in the current conversation: the legacy Track123 and whereParcel carrier-check paths were removed from the active label-check flow in favor of the S3-based label usage check, admin customer email verification now stays reflected after refresh, newly created customers with incomplete sender profiles are automatically redirected to finish their company address before creating labels while older customers remain grandfathered, and the customer orders page now reliably recognizes created shipments so label downloads and regenerate actions stay visible after creation.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The legacy Track123 and whereParcel carrier-check integrations were removed from the live label-check path, leaving the S3-based label usage flow as the supported mechanism.",
      "Admin customer email verification now persists on refresh from the detail screen, so verified emails no longer fall back to an unverified display state after reload.",
      "New customers must complete their sender/company address before label creation, and incomplete new profiles are redirected to `/customer/profile` automatically while existing customers keep their current behavior.",
      "The customer orders table now resolves created shipments from the shipment's linked order data first, so labels remain visible with download and regenerate controls instead of looking like no label exists."
    ],
    details: [
      {
        title: "Legacy Label-Check Cleanup",
        items: [
          "The active label-check flow was cleaned up to remove the deprecated Track123 and whereParcel integration paths, while keeping the S3-backed label usage and status workflow in place.",
          "This keeps the supported inventory and usage checks focused on the live S3 pipeline instead of the legacy external integration layer."
        ]
      },
      {
        title: "Customer Verification and Sender Profile Gate",
        items: [
          "Admin-side email verification now updates the customer detail snapshot immediately, so the verified state survives a browser refresh.",
          "Label creation for newly created customers now requires a complete sender profile, and missing sender fields automatically route the user to the profile page before any label is generated.",
          "Older customers are grandfathered by creation time, so the new sender-profile requirement does not interrupt existing accounts that already had labels or incomplete sender data."
        ]
      },
      {
        title: "Orders Page Shipment Recovery",
        items: [
          "The customer orders table now prefers shipment-linked order identifiers from the shipment payload itself and then falls back to the internal order-id map, which prevents created labels from disappearing when the relation cache is thin or stale.",
          "The page now shows the expected download and regenerate controls after label creation instead of leaving the row in a false `Create Label` state."
        ]
      },
      {
        title: "Verification",
        items: [
          "The change set was verified with the sender-profile and shipment-order helper tests, a successful production build, and the existing manual-label, DHL carrier-check and admin label-carrier-check suites."
        ]
      }
    ]
  },
  {
    version: 107,
    date: "2026-03-31",
    title: "Shopify Connection Check, eBay Tracking Controls and OTTO Internalization",
    summary: "This release captures the integration work completed during the current thread: Shopify stores now have a dedicated connection test so customers can verify their token before waiting for the next sync, eBay tracking write-back now keeps shipment-level sync state with default-on behavior and manual resend controls, and OTTO order sync was pulled in-house so Kenpost now fetches, compresses and stores the cache directly in S3 instead of posting to the legacy Cloudflare worker.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Shopify store detail pages now include a dedicated connection check action for Shopify-only stores, making token failures visible before the next sync attempt.",
      "eBay tracking write-back now persists shipment-level sync status, defaults to enabled for new stores, was enabled for existing eBay stores, and can be retried manually from shipment screens.",
      "OTTO order sync now runs inside Kenpost instead of calling the old external worker, with the same gzip-backed S3 cache flow handled by the app itself.",
      "The existing development Otto store was backfilled successfully after the internal sync path was landed, confirming the new in-app OTTO flow end to end."
    ],
    details: [
      {
        title: "eBay Tracking Controls",
        items: [
          "eBay shipments now keep a tracking sync record so Kenpost can tell whether a tracking number was already sent, is pending, failed or was skipped because sync is disabled.",
          "The new manual resend flow on shipment views lets operators push unsent tracking data to eBay even when automatic sync is turned off, which is useful for late fixes and backfills.",
          "New eBay stores now default to tracking sync enabled, and the existing eBay stores were normalized to that same setting so customers do not need to re-enable it manually."
        ]
      },
      {
        title: "Shopify Token Health Check",
        items: [
          "Shopify store detail screens now expose a dedicated `Check Token` action so customers can test whether the saved credentials still work before the next order refresh runs.",
          "The check is shown only for Shopify stores and returns a clearer reconnect message when the token is expired or invalid, instead of leaving the user to guess why new orders are not appearing.",
          "This makes the most common support issue for the affected Shopify store immediately visible from the customer portal."
        ]
      },
      {
        title: "OTTO Internal Sync and Backfill",
        items: [
          "OTTO order sync was moved off the old Cloudflare worker path and now runs directly inside Kenpost, including token exchange, pagination, gzip generation and S3 upload.",
          "The internal OTTO helper uses the same cache shape as the previous worker output, so downstream order readers continue to work without any data-format change.",
          "The existing development Otto store was backfilled successfully through the internal path, producing a refreshed S3 cache and confirming that the new sync flow works end to end."
        ]
      },
      {
        title: "Verification and Rollout",
        items: [
          "The eBay tracking flow was exercised on live shipment data and the manual resend path was confirmed to mark tracking as synced in the shipment metadata.",
          "The Shopify connection test path was added and verified on the store detail page so support can quickly distinguish a bad token from a simple sync delay.",
          "The OTTO backfill was run in development only, the temporary maintenance action was removed afterward, and no production Otto store existed at the time of the backfill."
        ]
      }
    ]
  },
  {
    version: 106,
    date: "2026-03-31",
    title: "Amazon Salestio Bridge Launch, Shopify-Sourced Amazon Orders and Admin Customer List Recovery",
    summary: "This release replaces Kenpost's earlier direct Amazon connection path with the new Salestio bridge workflow, hardens the customer-facing Amazon authorization experience with timed confirmation, modal guidance and safer operational masking, adds a separate Shopify source pipeline for Amazon orders written into the shared gzip-backed cache flow, propagates Amazon disconnect and delete actions back to the bridge server, and repairs the admin customers page so customer and credit management can load again without cursor-pagination failures.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Amazon connections now run through the external Salestio bridge instead of direct Amazon API credentials, including start, authorization and final confirmation handling from the customer portal.",
      "The Amazon onboarding flow now generates unique bridge site names automatically, opens the Amazon authorization page in a new tab, guides the user through confirmation in a modal and enforces the shortened 2-minute completion window.",
      "Amazon order imports now come from a dedicated Shopify source store, filtered by Amazon store name in Shopify order tags, then written into the same gzip-backed JSON cache pattern used by the rest of the marketplace order system.",
      "Deleting or disconnecting an Amazon store now also calls the bridge server to remove or deactivate the remote account, which keeps Kenpost and the automation environment in sync instead of leaving orphaned bridge records behind.",
      "The admin `/admin/customers` page no longer crashes on `GGT_INVALID_CURSOR` while loading credit and customer summaries, restoring customer visibility for support and credit assignment work."
    ],
    details: [
      {
        title: "Amazon Bridge Connection Workflow",
        items: [
          "Kenpost now uses the documented two-step Amazon Salestio bridge flow, including bridge health checks, job start, external Amazon authorization, final Shopify-side confirmation click and clearer result-state handling for active, submitted, expired and retryable outcomes.",
          "The customer Amazon connection screen was rebuilt around that bridge contract: internal server details are masked from customers, the authorization link opens automatically in a new tab, confirmation happens through a foreground modal instead of a tiny inline action, and the UI now removes stale countdown or pending-job panels as soon as the account becomes active.",
          "Operational safeguards were added around bridge state, including unique generated site names, safer status messaging, restart behavior for expired authorization links, timeout countdown visibility, a one-time post-success explanation that order import may take up to roughly an hour, and cleaner handling when the bridge cannot confirm a fully active state immediately."
        ]
      },
      {
        title: "Amazon Orders via Dedicated Shopify Source",
        items: [
          "Amazon orders are now pulled from a separate Shopify source store that exists only for Amazon ingestion, keeping this workflow fully separate from Kenpost's normal customer Shopify marketplace connections.",
          "The sync reads Shopify orders, filters them by the Amazon store name written into the order tag set, converts matching rows into Kenpost's Amazon order cache shape and persists them into the shared gzip-backed JSON cache path so the normal customer orders UI can consume them without a separate live fetch path.",
          "The required Shopify source credentials were added as dedicated environment variables for both development and production, and Amazon connection completion now triggers the first sync automatically so newly connected stores can begin receiving cached order data without a second manual step."
        ]
      },
      {
        title: "Admin and Data Reliability Follow-Ups",
        items: [
          "Amazon bridge delete and disconnect operations now propagate to the bridge server before Kenpost removes or passivates the local store, reducing the risk of stale remote accounts remaining attached after support cleanup.",
          "The admin customers data action was reworked to resolve counts and credit summaries through safer relation-aware lookups and stable cursor pagination, including the `createdAt + id` sort combination required by Gadget's cursor expectations for high-volume `labelSource` queries.",
          "This release was verified with repeated production builds, clean `ggt problems` runs, live bridge and Shopify smoke checks during the thread, targeted production deployments for the follow-up fixes, and log-based verification of the admin-customer pagination issue."
        ]
      }
    ]
  },
  {
    version: 105,
    date: "2026-03-29",
    title: "Admin Access Recovery, Hidden Stock Limits and Customer Management Cleanup",
    summary: "This release groups the customer-account support work completed in the current thread: admins can now manually recover blocked customer access by verifying emails and setting temporary passwords, the customer buy-labels page still enforces stock limits without exposing live stock counts, the admin customer invoice tab now respects Gadget's page-size limit, customer credit summaries were rebuilt to handle both `labelBalance` rows and legacy assigned-label records correctly, and obsolete onboarding warnings were removed from customer management screens.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin customer detail pages now include direct access-recovery actions so support can mark a customer's email as verified or set a temporary password without relying on blocked mail delivery.",
      "The customer `/customer/buy-labels` page no longer reveals internal stock quantities, while still stopping purchases above the real available stock with a generic limit message.",
      "Admin customer detail invoices no longer fail with `GGT_INVALID_PAGE_SIZE`, because the line-query pagination now stays within Gadget's maximum `250` row cap.",
      "Customer credit totals and balance summaries now combine `labelBalance` availability with legacy assigned-label fallback logic, fixing the earlier zero or impossible credit counts on admin customer screens."
    ],
    details: [
      {
        title: "Admin Recovery and Support Controls",
        items: [
          "Support operators can now recover customer access directly from the admin customer detail page by manually marking an email as verified or setting a replacement password when verification and reset emails never arrive.",
          "These actions stay admin-only and are intentionally scoped to customer accounts so normal customer sign-up, sign-in, verification and reset flows continue to work without broader auth-model changes."
        ]
      },
      {
        title: "Customer and Admin UI Corrections",
        items: [
          "The buy-labels purchase cards now hide raw stock counts from customers, but still block oversized purchase attempts with a generic `You can't buy this quantity right now.` style message instead of leaking internal inventory numbers.",
          "Admin customer pages were cleaned up so obsolete onboarding states such as `Needs onboarding` and `Incomplete` no longer appear now that onboarding is not part of the active customer flow."
        ]
      },
      {
        title: "Data Integrity and Verification",
        items: [
          "Admin customer credit rollups and per-customer balance views now read both `labelBalance` records and legacy assigned or consumed label-source evidence, which prevents valid customer accounts from incorrectly showing zero available or zero used credits.",
          "This release was verified with focused access-recovery and credit-summary tests, successful TypeScript checks, and production deployments after each follow-up fix in the thread."
        ]
      }
    ]
  },
  {
    version: 104,
    date: "2026-03-28",
    title: "Customer Shipment Refunds, Orphan Recovery and Void-State Cleanup",
    summary: "This release brings customer-side shipment cancellation much closer to admin-grade recovery: customers can now cancel and refund eligible shipments from their own shipments page, legacy rows with incomplete ownership data are handled more safely, orphaned shipment records can reconstruct their missing label link from stored shipment artifacts, and refunded or void rows now present clearer visual states without misleading restore actions.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Customers can now use `Cancel & Refund` directly from `/customer/shipments`, with the returned label credit going back into the same customer's stock.",
      "Legacy shipment rows with missing `user`, `order` or `labelSource` ownership links now resolve through safer fallback logic instead of failing with false ownership rejections.",
      "Older orphan shipments whose linked `labelSource` record disappeared can now rebuild that link from the shipment's stored PDF artifact before the refund is applied.",
      "Void and refunded rows now use clearer visual treatment and no longer keep showing an active `Restore Credit` action after the credit was already returned."
    ],
    details: [
      {
        title: "Customer Refund Workflow",
        items: [
          "The customer shipments table now exposes the same practical refund capability that previously existed only in admin shipment operations, while keeping the customer flow limited to `refund to customer stock` instead of broader admin inventory modes.",
          "Customer ownership checks were expanded so shipment, order, store and purchase relations can all contribute to scope resolution, which prevents valid customer refunds from being blocked just because one older foreign key was left blank."
        ]
      },
      {
        title: "Legacy and Orphan Shipment Recovery",
        items: [
          "Refund logic now supports older shipment records where the linked `labelSource` no longer exists but the shipment still retains enough stored artifact metadata to safely reconstruct the missing source record.",
          "This means older printed shipments with broken links can still be voided and credited back without manual database repair, as long as the shipment artifact and ownership context remain trustworthy."
        ]
      },
      {
        title: "UI Cleanup and Verification",
        items: [
          "The customer shipment action column was compacted into a safer menu flow, refunded rows gained clearer red visual treatment, and void rows stopped surfacing misleading restore actions after the refund had already been completed.",
          "This release was verified with TypeScript checks, focused refund tests including the orphan-shipment recovery case, clean `ggt problems` output and production deployment after the fixes were completed."
        ]
      }
    ]
  },
  {
    version: 103,
    date: "2026-03-28",
    title: "WooCommerce Connection Management, API Diagnostics and Reconnect Workflow",
    summary: "This release brings WooCommerce store management much closer to the production-ready eBay experience: the Woo connect page now doubles as a live connection workspace with masked key visibility, API health diagnostics, reconnect and disconnect controls, store-specific management routing and clearer guidance when a Woo site is reachable but still misconfigured for public REST access.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "WooCommerce now has an eBay-style connection management page with `Test API`, `Reconnect WooCommerce`, `Disconnect`, `Sync now` and `View orders` actions in one place.",
      "The Woo connection page supports both new connections and existing-store management through the same route, including `storeId`-aware reopen behavior from the store detail screen.",
      "API diagnostics now surface the detected REST mode, auth status, saved key preview and a customer-readable explanation when the target Woo site responds only through legacy `rest_route` endpoints.",
      "Store detail pages now include a direct `Manage WooCommerce API` entry point so operators do not need to jump back into raw credential forms to troubleshoot an existing store."
    ],
    details: [
      {
        title: "Woo Management Workspace",
        items: [
          "The WooCommerce connection route was expanded from a simple credential form into a richer management workspace that mirrors the operational clarity already introduced for eBay.",
          "Existing Woo stores can now reopen the same page in management mode, showing last sync information, imported order count, masked API key preview and connection health details without re-entering credentials blindly."
        ]
      },
      {
        title: "API Test and Troubleshooting Clarity",
        items: [
          "The new Woo API test flow now tells the user whether the site responds through modern pretty REST routes or legacy `index.php?rest_route=...` mode, instead of failing with a generic sync error.",
          "When the Woo site itself is the blocker, the UI explains that permalink, cache or REST-auth configuration must be corrected on the store side before Kenpost can import orders reliably."
        ]
      },
      {
        title: "Verification",
        items: [
          "This release was verified with successful TypeScript checks, a production build and a development deployment after the Woo management page and diagnostics workflow were completed.",
          "The flow was also exercised against a dedicated external WooCommerce test store seeded with sample products and orders for end-to-end validation."
        ]
      }
    ]
  },
  {
    version: 102,
    date: "2026-03-28",
    title: "WooCommerce Internal Sync, S3 Order Cache and Legacy REST Compatibility Fixes",
    summary: "This release removes WooCommerce's dependency on the missing external sync worker path that was returning `404`, moves Woo order imports onto the same internal S3-backed cache pattern used by the other marketplaces, and corrects the legacy REST URL construction so Kenpost can distinguish true Woo auth failures from malformed route probing.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "WooCommerce order sync no longer depends on the broken external worker endpoint and now uses an internal sync path that writes the order cache into S3.",
      "Legacy Woo REST routes are now built correctly with query parameters appended outside `rest_route`, fixing the earlier malformed URL shape that produced misleading `No route was found matching the URL and request method` failures.",
      "Woo auth probing now distinguishes between route discovery, public REST availability and actual consumer-key authorization, which exposed the real remaining blocker on the demo store as Woo-side auth behavior rather than a Kenpost URL bug.",
      "Orders pages continue to use the shared S3 cache loading pattern, so once Woo sync succeeds the imported orders flow into the same customer order list used by the other marketplaces."
    ],
    details: [
      {
        title: "Internal Sync Instead of a Dead Worker",
        items: [
          "The Woo sync path was updated to avoid the old worker URL that returned `404`, and Kenpost now prepares Woo order data internally before persisting the result to the S3-backed orders cache.",
          "This aligns Woo more closely with the hardened eBay order pipeline and keeps order-import control inside the app instead of an external endpoint."
        ]
      },
      {
        title: "Legacy REST Route Handling",
        items: [
          "Kenpost now builds legacy Woo URLs in the correct shape, such as `/index.php?rest_route=/wc/v3/orders&per_page=1`, instead of accidentally embedding extra query parameters inside the `rest_route` value itself.",
          "Auth probing was expanded across query-string, Basic Auth and OAuth-style signing attempts so the diagnostics screen can report the real Woo access problem with much higher confidence."
        ]
      },
      {
        title: "Verification",
        items: [
          "This release was verified with backend and web TypeScript checks, a successful build, a clean `ggt problems` run and deployment to the active development environment.",
          "Live troubleshooting against the external Woo demo confirmed that the earlier `404` path was a Kenpost-side URL issue, while the remaining `401` result is now correctly reported as a Woo-side auth configuration problem."
        ]
      }
    ]
  },
  {
    version: 101,
    date: "2026-03-28",
    title: "eBay Legacy Order Address Preservation and Manual Label Completion Flow",
    summary: "This release captures the final eBay order-shipping follow-up: Kenpost now preserves the best available street data during eBay cache refreshes, automatically opens a manual address-completion flow when older eBay orders are missing street information, and avoids secondary shipment failures caused by treating an eBay username like a valid email address.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Older eBay orders that are missing `Address 1` now route the operator into a manual address-completion dialog instead of failing outright during label creation.",
      "The eBay sync merge logic now keeps previously known street, postal and city fields when a later eBay refresh returns a thinner payload, preventing good address data from being overwritten by blank values.",
      "Single-order and single-selection label creation flows were aligned so both routes can open the same recovery dialog when the shipping address is incomplete.",
      "Shipment creation no longer fails on the eBay buyer username being written into an email field that expects a real email address."
    ],
    details: [
      {
        title: "Manual Recovery for Old eBay Orders",
        items: [
          "Because eBay may suppress street-level PII on older orders, Kenpost now falls back to an address-completion popup whenever the operator tries to create a label for an order that has city, postal code and country but no usable street line.",
          "The popup includes the dedicated warning added in the earlier eBay follow-up so the user understands that the missing street can be an eBay API limitation rather than a sync regression."
        ]
      },
      {
        title: "Safer Cache Preservation and Label Creation",
        items: [
          "The eBay order merge step now preserves the best-known address fragments in S3 when subsequent sync payloads return less information than an earlier fetch.",
          "The shipment creation flow was also corrected so eBay usernames are no longer treated as guaranteed email addresses, removing an unrelated validation failure that previously blocked label creation even after the operator completed the missing address."
        ]
      },
      {
        title: "Verification",
        items: [
          "This release was verified through live customer-side eBay order imports, manual address dialog testing, label-creation retries and the usual backend/web build verification before deployment.",
          "The changes are scoped to the eBay order handling branch and do not alter the normal address or label behavior for Shopify, Billbee, Amazon, JTL or the other marketplace integrations."
        ]
      }
    ]
  },
  {
    version: 100,
    date: "2026-03-27",
    title: "Canceled Labels Client Fallback and Archive Visibility Recovery",
    summary: "This follow-up release hardens the dedicated `Canceled Labels` admin screen after production still showed an empty error state: the page now reconstructs canceled-label rows directly from shipment, label-source, order, purchase and user model reads in the browser, so voided shipment history remains visible even if the older action path fails or returns an unexpected wrapper.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The `Canceled Labels` page no longer depends solely on the original global action response path and now builds its archive view directly from live model reads as a reliability fallback.",
      "Voided shipments such as `VOID-933-*`, `VOID-934-*`, `VOID-935-*` and `VOID-936-*` can now be surfaced from the same data already present in production instead of leaving admins on an empty error screen.",
      "The page chunk changed in this release, which also helps flush stale cached archive code in browsers that were still rendering the broken canceled-label screen.",
      "A fresh production build completed successfully after the archive-page fallback was added."
    ],
    details: [
      {
        title: "Client-Side Archive Reconstruction",
        items: [
          "The page now fetches void shipments first, then resolves linked label sources, related orders, label purchases, customer identities and product names through batched model reads on the client.",
          "Archive row states such as `Restored to customer`, `Returned to stock`, `Still consumed` and `Missing linked label` are now derived locally from the current production records rather than relying on a single fragile loader response."
        ]
      },
      {
        title: "Verification",
        items: [
          "Verification for this follow-up included another successful `yarn build` after the page-level fallback loader was added."
        ]
      }
    ]
  },
  {
    version: 99,
    date: "2026-03-27",
    title: "Canceled Shipment Archive Separation and Canceled Labels Reliability Fix",
    summary: "This release finishes the admin shipment cancel follow-up by separating archived cancel records from the live `/admin/shipments` workspace, repairing the dedicated `Canceled Labels` loader so voided shipments reliably appear in their own review screen, and tightening the archive behavior so support is no longer distracted by stale cancel tombstones among active labels.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The main `/admin/shipments` page now returns only active shipment rows, so canceled `VOID-...` tombstones no longer clutter the operational queue or leave confusing recovery actions visible beside finished work.",
      "The `Labels > Canceled Labels` workspace now loads voided shipment history through the internal data layer with a narrower, safer relation selection, fixing the earlier page failure where canceled rows and summary cards stayed empty.",
      "Canceled shipment handling is now clearly split between the active operations page and the archive-style canceled-labels page, making it easier to review returned-to-customer, returned-to-stock and needs-attention cases without mixing them into the live shipment list.",
      "A fresh production build was completed after the shipment-page filtering and canceled-labels data-loader fixes were applied."
    ],
    details: [
      {
        title: "Active Shipments Only on the Main Page",
        items: [
          "The admin shipments data action now filters out `void` rows before building the live shipment table, so operational pages focus on labels that can still be downloaded, regenerated or actively managed.",
          "This also removes the earlier confusion where canceled rows still showed tombstone tracking numbers like `VOID-934-...` and looked half-actionable even though the cancel flow had already finished."
        ]
      },
      {
        title: "Reliable Canceled Labels Archive",
        items: [
          "The canceled-labels action now loads void shipments, linked label sources, orders and label products through the internal server API instead of wider public selections that were more brittle in production.",
          "The linked label lookup was simplified to fetch only the fields actually needed for placement and customer-restoration review, reducing the chance of the page failing with an empty summary and generic load error."
        ]
      },
      {
        title: "Verification",
        items: [
          "Verification for this release included a successful `yarn build` after the shipment filtering and canceled-label archive fixes were completed.",
          "The release was prepared specifically to support the current production cancel-and-return debugging thread and is intended to be deployed as one combined admin-facing cleanup."
        ]
      }
    ]
  },
  {
    version: 98,
    date: "2026-03-27",
    title: "Admin Dashboard Relation Recovery, Inventory Snapshot Cleanup and Overview Polish",
    summary: "This release records the dashboard follow-up completed after the refreshed admin overview still showed `Unknown`, `Unlinked` and misleading zero-state values: `/admin` now resolves customer, order and label-product context through nested purchase, shipment, store and label-source relations instead of depending only on sparse direct ids, inventory widgets describe true stock availability more honestly, and the overview layout was tightened so operators can trust the snapshot more quickly.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Recent purchases and recent shipments now recover customer, order and product context through nested relations such as `order.store.user`, `shipment.order`, `labelSource.labelPurchase`, `assignedToUser` and linked `labelProduct` records, reducing `Unknown`, `Unlinked` and `—` cases on the dashboard.",
      "Top product activity and inventory-derived metrics now use the richer recovered relation context, so product summaries no longer stay empty just because a direct foreign key is missing on a live row.",
      "Inventory watchlist, managed-label totals and available-label cards now present clearer zero states when no uploaded stock exists instead of implying broken math or half-linked inventory.",
      "The admin overview layout was refined with a tighter quick-actions area, clearer empty-state copy and more consistent snapshot storytelling across the KPI cards and operational panels."
    ],
    details: [
      {
        title: "Dashboard Relation Recovery",
        items: [
          "The admin summary action now resolves customer ownership, order context and label-product identity through nested dashboard sources instead of trusting only direct `customerId`, `orderId` or `labelProductId` fields.",
          "This specifically hardens purchase and shipment rows that only retain their links through store ownership, label purchases, assigned labels or other nested relation objects, which was the main reason the refreshed dashboard still showed `Unknown` and `Unlinked` values after the first dashboard rollout."
        ]
      },
      {
        title: "Inventory and Empty-State Cleanup",
        items: [
          "Managed label totals and stock watchlist messaging were adjusted so a true absence of uploaded stock is described explicitly, rather than surfacing as a suspicious wall of zeroes.",
          "Top-products and watchlist sections now better distinguish between genuinely empty activity, incomplete product links and valid operational data that was previously hidden behind overly blunt fallback text."
        ]
      },
      {
        title: "Verification",
        items: [
          "This dashboard follow-up was verified with the targeted admin summary test suite, passing TypeScript checks, a successful production build and a clean `ggt problems` run before deployment.",
          "The release note was added only after the relation-recovery and UI cleanup changes had already been deployed successfully to production on March 27, 2026."
        ]
      }
    ]
  },
  {
    version: 97,
    date: "2026-03-27",
    title: "Shipment Recovery Wording Clarified and Customer Stock Visibility Added",
    summary: "This release follows up on the shipment cancel and return recovery work with clearer admin wording: `/admin/shipments` now distinguishes canceled shipments from faulty labels, shows whether the linked label sits in customer stock or Kenpost stock, disables redundant recovery buttons once a label is already in the intended place, and adds a direct shortcut to the dedicated `Canceled Labels` workspace.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Voided shipment rows now display a human-readable `Canceled` status instead of the raw `void` storage value so operators do not confuse a canceled shipment with a faulty label.",
      "Each shipment row now shows the linked label placement, such as `Customer stock` or `Kenpost stock`, so support can immediately verify whether customer credit was actually restored.",
      "The former `Restore Credit` wording was replaced with clearer action labels like `Cancel + Return To Customer`, `Return To Customer` and `Move To Kenpost Stock`, and redundant buttons are disabled once the label is already back where it belongs.",
      "A new shortcut from the shipments page opens `Canceled Labels` directly, complementing the existing sidebar entry added in the earlier recovery follow-up."
    ],
    details: [
      {
        title: "Clearer Shipment Recovery Language",
        items: [
          "Admin confirmation dialogs now explicitly say when the customer will receive the label back for reuse and when the label will instead move back into Kenpost inventory.",
          "This removes the ambiguity around `Restore Credit`, which previously sounded like a separate manual repair step even after cancel had already restored the customer's reusable stock."
        ]
      },
      {
        title: "Visible Label Placement on Shipments",
        items: [
          "The shipments page now resolves the linked label source for each row and shows where that label currently sits after recovery.",
          "Support can now distinguish normal canceled shipments whose labels already returned to customer stock from rows that still need review or were moved back to Kenpost stock."
        ]
      },
      {
        title: "Verification",
        items: [
          "The follow-up verification for this release includes a successful production build after the admin shipment page data and UI changes were applied."
        ]
      }
    ]
  },
  {
    version: 96,
    date: "2026-03-27",
    title: "eBay 90-Day Address Warning for Manual Label Completion",
    summary: "This release adds a clearer customer-facing explanation for the older eBay order cases discovered during live label testing: when an eBay order older than 90 days opens the manual address-completion dialog because the street line is missing, the popup now explains that eBay may no longer return full street data through the API and that the missing address must be completed manually before label creation can continue.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The create-label dialog now shows a dedicated yellow warning only for older eBay orders that are still missing `Address 1`.",
      "The message explains that eBay may stop returning full street data after 90 days, so the missing address is not necessarily a Kenpost sync failure.",
      "Operators now get the explanation inside the same popup where they complete the address, instead of being forced to guess why street information is absent.",
      "Other marketplaces remain unchanged because the warning is scoped specifically to eBay and only to the incomplete legacy-order scenario."
    ],
    details: [
      {
        title: "Scoped Warning for Legacy eBay Orders",
        items: [
          "The warning appears only when the selected store is eBay, the dialog is opened for an order older than 90 days, and the initially loaded shipping address is still missing `Address 1`.",
          "This keeps the UI quiet for normal orders while giving a precise explanation for the exact edge case that repeatedly caused confusion during live eBay testing.",
          "The message is shown inside the existing address dialog so the operator can immediately continue with manual correction in the same workflow."
        ]
      },
      {
        title: "Clearer Explanation of the Data Source Limitation",
        items: [
          "The new text explicitly states that the missing street is not something Kenpost can necessarily recover from the API for older eBay orders.",
          "This reflects the production observation and official eBay Fulfillment behavior where older orders may no longer expose full street-level PII.",
          "As a result, the customer understands why manual address entry is required before shipment creation can proceed."
        ]
      },
      {
        title: "Verification and Compatibility",
        items: [
          "This release is a targeted UX clarification layered on top of the earlier eBay sync and manual-address-entry fixes, not a change to the underlying Shopify, Billbee, Amazon, JTL or WooCommerce shipment flows.",
          "Local type checks, a successful production build and deployment verification were completed after the dialog warning was added.",
          "Version numbering was advanced to `v96` because `v95` was already occupied in the release timeline and reusing it would have broken version history continuity."
        ]
      }
    ]
  },
  {
    version: 95,
    date: "2026-03-27",
    title: "Shipment Cancel Recovery Follow-Up, Boundary-Safe Label Inference and Canceled Labels Workspace",
    summary: "This release records the follow-up shipment recovery fix completed after fresh production failures on `/admin/shipments`: cancel and return flows now recover customer ownership through the order's store owner when direct shipment or order links are blank, legacy DHL `1 kg` labels no longer fail product inference on overlapping weight boundaries, and admins now have a dedicated `Canceled Labels` screen under `Labels` to review voided shipments and see where each linked label ended up.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Admin shipment cancel recovery now falls back to the order store owner, fixing fresh production cases that still ended with `Original customer could not be determined` even though the shipment originated from a customer store.",
      "Metadata-based label-product inference now resolves ambiguous boundary cases like DHL `1 kg` labels more deterministically by preferring the tightest matching active weight range instead of giving up when older products overlap.",
      "A new `Labels > Canceled Labels` admin page lists void shipments together with their linked label state, customer context and current placement so support can see whether a label returned to stock, went back to customer stock or still needs manual attention.",
      "Regression coverage was expanded for both the new store-owner fallback and the overlapping `1 kg` inference case, and the full production build completed successfully after the follow-up changes."
    ],
    details: [
      {
        title: "Original Customer Recovery",
        items: [
          "The admin shipment recovery resolver now checks `order.store.user` after the existing shipment, order and purchase ownership links, which closes a real production gap where the store owner was still known but the more direct user relations were blank on the recovery path.",
          "Cancel flow diagnostics were expanded accordingly, so future warnings now show the order store id and store owner id alongside the other attempted ownership links."
        ]
      },
      {
        title: "Boundary-Safe DHL Product Inference",
        items: [
          "The metadata inference helper now sorts overlapping weight matches by the tightest usable range instead of treating every shared-boundary result as unresolved ambiguity.",
          "This specifically hardens DHL filenames such as `DHL Paket 1 kg ...` when the active product catalog contains legacy ranges like `0-1000g`, `1-1000g` or similarly overlapping lightweight products."
        ]
      },
      {
        title: "Dedicated Canceled Labels Screen",
        items: [
          "Admins now have a separate `Canceled Labels` workspace directly under the `Labels` navigation group.",
          "The new page lists void shipments, extracts the original tracking number from `VOID-...` tombstones, resolves linked label placement and highlights rows that still have missing links or unresolved consumed state."
        ]
      },
      {
        title: "Verification",
        items: [
          "Targeted tests now cover store-owner customer fallback and overlapping `1 kg` metadata inference in addition to the earlier cancel and return recovery cases.",
          "The workspace verification for this follow-up included passing targeted Node test runs and a successful `yarn build` after the backend and admin UI changes were applied."
        ]
      }
    ]
  },
  {
    version: 94,
    date: "2026-03-27",
    title: "eBay OAuth Launch, Internal Order Sync, S3 Cache Hardening and Legacy-Order Address Recovery",
    summary: "This release records the end-to-end eBay integration completed and live-tested in the current support thread: customers can now connect eBay stores through Kenpost's own OAuth application without entering app keys, eBay order imports now run internally and persist to the same S3-backed order cache used by other marketplaces, the customer order screen gained eBay-specific sync ranges and clearer reconnect or disconnect controls, and follow-up production fixes hardened store deletion, session handling, address recovery and label creation for older eBay orders where the marketplace suppresses street-level PII after 90 days.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Customers now connect eBay through Kenpost's hosted OAuth consent flow, with server-side token exchange, encrypted token storage, reconnect, disconnect and refresh handling instead of manual App ID or Cert ID entry.",
      "eBay order synchronization now runs inside the app and writes the normalized result to S3-backed order cache storage, removing the broken dependency on the previous missing external eBay sync worker.",
      "The customer orders page now supports eBay-specific sync ranges such as `Last 1 Month`, `Last 3 Months` and `All`, where `All` intentionally maps to the last `2 years` because of eBay Fulfillment API limits.",
      "Store-management reliability was improved through repeated live fixes covering OAuth callback authorization, disconnect and delete flows, false `You must be logged in` session failures and record deletion errors caused by missing Gadget metadata.",
      "eBay label creation now handles incomplete historical addresses more honestly: when eBay no longer returns street data for older orders, the UI opens an address-completion dialog instead of failing silently or masking the real limitation."
    ],
    details: [
      {
        title: "Hosted eBay OAuth for Multi-Tenant Customers",
        items: [
          "The app now uses Kenpost's own production eBay developer application for customer sign-in, so customers no longer paste their own app credentials, user tokens or manual key material into the store setup flow.",
          "OAuth connect, callback, state validation, token exchange, refresh, encrypted persistence and reconnect-required handling were added to the existing multi-tenant store architecture so one Kenpost app can authorize many different customer-owned eBay seller accounts safely.",
          "The eBay connection UI now shows connection state, environment, account identity, last sync information and reconnect or disconnect controls while keeping client secrets strictly server-side."
        ]
      },
      {
        title: "Internal eBay Sync and S3 Cache Integration",
        items: [
          "The earlier eBay sync path depended on an external worker endpoint that returned `404`, so eBay order import now runs directly inside the app through the official eBay Fulfillment API client layer.",
          "Fetched eBay orders are normalized into the same S3-backed JSON cache pattern already used by other marketplaces, which means the customer orders screen, pagination and export flows can reuse the existing cache reader path instead of a one-off eBay pipeline.",
          "The orders screen now exposes eBay-specific date ranges, and the `All` option maps to the last two years because eBay's Fulfillment API rejects start dates outside the official two-year window."
        ]
      },
      {
        title: "Connection Management, Session Hardening and Delete Recovery",
        items: [
          "Live testing uncovered several eBay management edge cases, including OAuth callback store-authorization mismatches, disconnect actions falsely claiming the customer was not logged in and store deletions crashing on missing `__typename` metadata.",
          "These flows were repaired so the eBay management page can now distinguish between creating a new eBay connection and managing an existing store, while customer-owned delete and disconnect actions resolve session state more consistently.",
          "The release also includes cleanup around reconnect handling, new-store onboarding and store-detail navigation so customers can move between store detail, eBay management and the shared stores list without the earlier confusing state carry-over."
        ]
      },
      {
        title: "Old eBay Orders, Suppressed Street Data and Label UX",
        items: [
          "During live testing, older eBay orders showed city, postal code and country but no street line, which initially looked like an S3 persistence bug; official eBay Fulfillment documentation confirms that street-level address fields can be withheld for orders older than `90` days.",
          "The sync layer was still hardened so if Kenpost already saw a better eBay address earlier, later syncs will preserve those non-empty address fields in S3 rather than overwriting them with newer blank payloads from eBay.",
          "For orders where eBay never returns the street at all, single-order label creation now opens the existing address dialog so the operator can manually complete `Address 1` and continue instead of being blocked by an opaque backend failure."
        ]
      },
      {
        title: "Label-Creation Follow-Ups and Data Validation Fixes",
        items: [
          "Once manual address entry was introduced, additional live logs showed that some eBay buyer usernames were being written into the internal `email` field, which caused label creation to fail on email validation even after the address was corrected.",
          "The eBay shipment-creation path now treats email as optional unless the incoming value is a real email address, preventing usernames like `nermin_feyzula` from poisoning order persistence during label creation.",
          "The customer orders page also now surfaces dialog submit failures through toast feedback, so future label-creation issues no longer fail quietly during manual address entry."
        ]
      },
      {
        title: "Verification, Constraints and Scope",
        items: [
          "This release was verified through repeated live customer-flow testing on connect, callback, reconnect, sync, disconnect, delete and label-creation scenarios, together with passing TypeScript checks, production builds and clean `ggt problems` runs before deployment.",
          "The eBay-specific fixes are intentionally scoped so other marketplaces such as Shopify, Billbee, Amazon, JTL and WooCommerce keep their existing shipment and order flows unchanged unless an address-completion dialog is explicitly needed for a genuinely incomplete order.",
          "A known platform constraint remains in place: if an old eBay order is first imported only after eBay has already suppressed its street-level PII, Kenpost cannot recover that hidden street automatically and manual address completion is still required for shipment creation."
        ]
      }
    ]
  },
  {
    version: 93,
    date: "2026-03-27",
    title: "Shipment Retry Hardening, Honest Failure Visibility and Customer Totals Cleanup",
    summary: "This release records the support-thread fixes completed after live shipment and admin customer investigations: transient internal label generation failures are now retried automatically instead of failing immediately on the first temporary storage error, the admin shipments page no longer disguises real failed labels as `Preparing`, and admin customer listing/detail screens now show more complete counts while the customer list page expands from 25 to 250 rows.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Internal manual-template label generation now retries temporary storage and network failures with built-in backoff before a shipment is marked as failed.",
      "Admin shipments now surface true label-generation failures instead of showing `Preparing` whenever downloadable artifacts are missing.",
      "Admin customer list pagination was raised from `25` to `250` rows so larger customer sets can be reviewed in one page.",
      "Admin customer counters now page through full result sets instead of relying on a single batch, reducing false zero or undercount cases on stores, purchases and shipments.",
      "Customer detail style admin screens now distinguish between loaded relation rows and the real total count, so operators no longer confuse a `latest 1000 rows` slice with the whole customer history."
    ],
    details: [
      {
        title: "Automatic Retry for Temporary Label Infrastructure Errors",
        items: [
          "The internal shipment label persistence layer now classifies retryable storage and network failures such as timeouts, `503` responses, `429` throttling and the observed `Service is unable to handle request.` message as transient infrastructure errors.",
          "For those transient cases the system retries the same shipment label generation with controlled backoff at `800ms`, `2s` and `5s` before treating the operation as a real failure.",
          "This keeps one-off object-storage instability from turning immediately into a customer-facing broken shipment while still allowing permanent validation-style errors to fail fast."
        ]
      },
      {
        title: "Failure Visibility on Admin Shipments",
        items: [
          "The admin shipments page loader now includes `labelGenerationStatus` and `labelGenerationError` in its payload instead of only relying on artifact URLs and file keys.",
          "The action column now shows a failure state when label generation actually failed, rather than always rendering `Preparing` whenever a PDF is missing.",
          "This makes operational diagnosis much faster because admins can immediately tell the difference between an in-progress shipment and a shipment that needs regeneration."
        ]
      },
      {
        title: "Customer Counts and Paging Fixes",
        items: [
          "The admin customer list page now loads `250` customers per page instead of `25`, matching the requested wider overview.",
          "List-side count maps for stores, purchases and shipments now walk paginated result sets instead of counting only the first internal batch, which avoids hidden undercounts in larger customer histories.",
          "The customer detail relations action now returns explicit totals for custom prices, purchases, shipments and assigned labels alongside the currently loaded rows."
        ]
      },
      {
        title: "Customer Detail Totals vs Loaded Rows",
        items: [
          "Admin customer detail cards previously used the length of the loaded arrays as if they were full totals, even though the screen intentionally caps loaded history to the latest `1000` rows.",
          "The overview and tab headers now explain whether all rows are loaded or whether the page is currently showing only the latest slice of a larger total.",
          "This reduces confusion during support work because `Purchases`, `Shipments` and `Assigned Labels` now reflect true totals while still preserving the latest-row loading cap."
        ]
      },
      {
        title: "Verification and Rollout",
        items: [
          "Verification for this release included a successful targeted retry-classification test, successful production builds and a clean `ggt problems` check before deployment.",
          "No schema migration or destructive data change was introduced in this release; the work is confined to retry behavior, admin loaders and UI state rendering.",
          "This entry intentionally groups the shipment retry hardening and admin customer counting fixes because both were completed in the same live support thread and deployed together."
        ]
      }
    ]
  },
  {
    version: 92,
    date: "2026-03-27",
    title: "Admin Label Assignment Recovery, Customer Visibility Fixes and Direct PDF Downloads",
    summary: "This release captures the label-management and customer-account fixes completed in the current support thread: admins can now allocate up to 1000 labels in one operation without hitting the earlier 250-query ceiling, customer detail and admin inventory views now resolve assigned product and owner information more reliably even when Gadget relations arrive as raw ids, new customer accounts regain the missing access needed for the portal, and single label downloads from the customer inventory now download directly instead of opening as an inline PDF preview.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin manual allocation now supports up to `1000` labels in one operation while still respecting Gadget's internal `250` pagination limit through batched fetching and updates.",
      "Admin customer detail pages now surface up to `1000` assigned labels, purchases and shipments, with product and source information rehydrated even when linked relations are returned as raw ids instead of nested objects.",
      "Admin `Label Inventory` owner display was improved so assigned labels show the real customer name or company instead of a fallback such as `Customer 128`, and the page also gained customer filtering.",
      "Newly created customer accounts were repaired so the customer portal no longer breaks on first login due to missing self-read permissions or incomplete signed-in role state.",
      "Single PDF downloads from `/customer/label-inventory` now trigger a real browser download instead of opening the PDF in a new tab."
    ],
    details: [
      {
        title: "1000-Label Allocation Without Breaking Gadget Limits",
        items: [
          "The admin allocation and unassign flows now keep the visible single-operation limit at `1000`, but internally page through label sources in `250`-row batches so Gadget's hard pagination ceiling is not exceeded.",
          "This resolves the earlier failure case where entering `995` labels caused a `page size too big` backend error even though enough stock was available.",
          "Customer detail relation loading was expanded in the same thread so admins can review the larger assignment result on one screen instead of being cut off at the earlier 250-row view."
        ]
      },
      {
        title: "Relation Hydration and Owner Visibility Repairs",
        items: [
          "Several live support cases showed Gadget returning linked records like `labelProduct`, `labelPurchase` or `assignedToUser` as raw ids instead of fully expanded relation objects.",
          "The affected admin loaders now explicitly rehydrate those ids back into label-product, purchase and user details so screens no longer lose product names, source labels or owner names just because the relation payload shape changed.",
          "As part of that work, the admin label inventory page now shows the actual customer identity in the owner column and supports filtering assigned labels by customer."
        ]
      },
      {
        title: "Customer Account and Portal Access Fixes",
        items: [
          "New customer accounts created during admin operations were missing part of the expected signed-in access path, which could produce application errors on `/customer` for accounts that otherwise existed correctly.",
          "Customer permissions were updated so a customer can read the minimum self-user data needed by the portal, and new customer creation paths now attach the expected signed-in role state together with the customer role.",
          "This keeps the customer portal behavior aligned with older working accounts without regressing existing users who were already signing in successfully."
        ]
      },
      {
        title: "Customer Download UX Follow-Up",
        items: [
          "Single-label PDF downloads from the customer inventory page previously opened a new browser tab because the flow redirected to an inline PDF response.",
          "The page now routes individual downloads through the attachment-style proxy path and triggers a direct browser download with a stable file name, which better matches the meaning of the `Download` button.",
          "Bulk ZIP export behavior remains separate; this release note only covers the one-by-one PDF download UX adjustment."
        ]
      },
      {
        title: "Verification and Scope",
        items: [
          "Workspace verification for the changes recorded in this release included successful `yarn tsc --noEmit`, `yarn build` and `ggt problems` runs after the related admin and customer updates were applied.",
          "The release groups together the live support fixes completed across allocation limits, customer detail hydration, owner labeling, portal access and direct PDF download behavior so the operational history stays readable.",
          "This entry intentionally follows the earlier customer inventory export notes with a more support-focused stabilization release covering the later real-world admin and customer issues found after rollout."
        ]
      }
    ]
  },
  {
    version: 91,
    date: "2026-03-26",
    title: "Customer ZIP Export Restart Fix, Longer Runtime and Clearer Finalizing Status",
    summary: "This release records the follow-up hardening completed after real-world 995-label export tests: the customer inventory ZIP worker no longer silently restarts the same archive job during finalization, the export runtime window was expanded substantially, automatic queue retry for the same job was disabled, and history entries now surface the later upload phase more clearly.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Large customer inventory ZIP exports no longer automatically retry the same queued job, which previously made long-running exports look like they jumped back to the beginning after reaching the final packaging stage.",
      "The background export worker timeout was increased from `15 minutes` to `60 minutes`, giving 500 to 1000 label archives far more room to finish packaging and upload without being cut off mid-run.",
      "Customer export history now carries a dedicated manifest phase so the UI can distinguish normal collection work from the later upload step instead of showing one ambiguous finalizing state.",
      "Finalizing history cards now explain whether the server is still compressing the ZIP or has already moved into the upload-to-storage step before the public download link becomes active.",
      "The release keeps the previously delivered stable public `/dl/...` download link model intact while making the underlying large-archive job lifecycle less confusing and less likely to repeat work."
    ],
    details: [
      {
        title: "Restart Loop Removed from Long ZIP Jobs",
        items: [
          "Customer inventory exports were previously queued with an automatic retry policy, which meant a timeout or late-stage worker interruption could silently rerun the same job and make the history row appear to start over.",
          "That automatic retry path has now been removed for this queue so a large export either finishes normally or surfaces a terminal failure instead of reprocessing the full label set behind the customer's back.",
          "This specifically targets the observed support case where a 995-label ZIP reached the end of PDF processing, entered finalization and then appeared to begin the whole archive again."
        ]
      },
      {
        title: "Longer Worker Runtime for 995-Label Exports",
        items: [
          "The `exportCustomerLabelInventory` action timeout was increased from `900000` milliseconds to `3600000` milliseconds.",
          "This wider runtime window is intended to absorb the real cost of collecting hundreds of PDFs, assembling the ZIP and uploading the finished archive to storage without hitting the earlier 15-minute ceiling.",
          "By widening the runtime instead of splitting work back into visible retries, the customer now gets a truer one-job lifecycle for very large exports."
        ]
      },
      {
        title: "Manifest Phase Tracking and UI Clarity",
        items: [
          "The manifest payload for customer inventory export jobs now records a `phase`, allowing the history dialog to distinguish collection, upload and terminal states.",
          "The history popup now shows a more precise message when all PDFs are already processed but the ZIP is still being uploaded, which reduces the earlier confusion caused by `100%` progress without an active download button.",
          "This improves operator confidence during very large runs because the UI now better reflects what the server is doing after the per-PDF work has already finished."
        ]
      },
      {
        title: "Verification and Scope",
        items: [
          "Workspace verification for this release included a successful `yarn build` run and a clean `ggt problems` check after the queue, action and history updates were applied.",
          "The support fix recorded here is intentionally separated from the earlier ZIP queue rollout note because it addresses a later production-observed restart loop during finalization.",
          "This version entry focuses on the customer inventory export reliability follow-up completed after hands-on 995-label archive testing in the current support thread."
        ]
      }
    ]
  },
  {
    version: 90,
    date: "2026-03-25",
    title: "Queued Customer ZIP Export, Longer Archive Runtime and Live Progress Feedback",
    summary: "This release captures the latest customer label inventory export hardening completed in the current support thread: large ZIP exports were moved out of the direct request path into a background queue, the archive worker was given a longer execution window, PDF collection throughput was increased, and the customer portal now surfaces queued, running and downloading progress states while the archive is being prepared.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Customer `Label Inventory` ZIP export no longer waits inside one direct browser request; the page now queues a background export job and tracks it until the archive is ready.",
      "The export worker timeout was raised to 15 minutes, which removes the earlier short-request timeout bottleneck for very large archives such as 500 to 1000 labels.",
      "PDF resolution concurrency for inventory exports was increased so large batches are assembled faster before ZIP packaging begins.",
      "The customer progress dialog now distinguishes queued, waiting, retrying, running and downloading phases instead of showing one static loading state.",
      "Once the archive is ready, the customer download path uses the stored ZIP artifact instead of falling back to rebuilding the same archive synchronously."
    ],
    details: [
      {
        title: "Background Queue for Customer Inventory Exports",
        items: [
          "The `POST /api/label-inventory-export-download` route now queues the inventory export as a background action instead of generating the ZIP inline during the web request.",
          "This change removes the earlier browser-facing timeout pressure because the customer request now returns quickly with a background action handle rather than waiting for the full archive build.",
          "The queue is serialized through a dedicated export worker channel so heavy archive creation jobs do not pile onto the normal page-response path."
        ]
      },
      {
        title: "Longer Export Runtime and Faster PDF Collection",
        items: [
          "The customer inventory export action now runs with a 15-minute timeout window, which is more appropriate for high-volume ZIP generation than the previous short action-request window.",
          "Label PDF resolution concurrency was increased from `4` to `12`, helping the worker gather large label batches from storage or upstream URLs much faster.",
          "These changes specifically target the real customer case discussed in the thread where archive preparation time climbed sharply even on medium-sized export attempts."
        ]
      },
      {
        title: "Live Queue and Download Progress in Customer UI",
        items: [
          "The `Label Inventory` progress dialog now reflects the actual export lifecycle: queued, waiting for worker, retrying, building on the server, and finally downloading to the browser.",
          "Customers now see clearer feedback for long-running exports instead of a single spinner that looked stuck during larger ZIP preparation.",
          "When the stored archive is streamed back through the proxy download path, the dialog can also display byte-based progress while the ZIP is being transferred."
        ]
      },
      {
        title: "Safer Stored-Archive Delivery",
        items: [
          "Once the background worker finishes, the app now downloads the stored archive artifact instead of silently falling back to a second synchronous rebuild path.",
          "If storage cannot provide a reusable archive URL, the export now fails explicitly rather than hiding the problem behind another long-running direct generation attempt.",
          "This keeps the customer flow aligned with the intent of the queue-based export architecture and avoids reintroducing timeout risk at the final download step."
        ]
      },
      {
        title: "Verification and Scope",
        items: [
          "The work recorded here covers the queued export route, longer-running export action, faster label PDF gathering and richer customer progress feedback completed in this support thread.",
          "Workspace verification after the changes included filtered TypeScript checks across the touched customer export frontend and backend files with no new matched type errors.",
          "This release intentionally adds a focused follow-up entry after the earlier customer inventory rollout notes so the later timeout and queue hardening work remains visible in version history."
        ]
      }
    ]
  },
  {
    version: 89,
    date: "2026-03-25",
    title: "Admin Label Date Filters, 1000-Row Inventory Views and Carrier Check History Reports",
    summary: "This release collects the admin label-operations work completed in the current conversation: label inventory and label checks gained upload-date filtering, admin inventory pagination was expanded to 500 and 1000 rows per page, and background carrier-check batches now persist a full history with manifest-backed summaries and downloadable reports.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Admin `Label Inventory` now supports upload-date filtering with `Uploaded from`, `Uploaded to` and a quick `Today` shortcut so freshly uploaded labels can be isolated before sending them for external control.",
      "The same created-at filter state now flows through the shared backend query and filtered carrier-check queueing logic, so date-scoped checks only send the intended label set to the S3 processing exchange.",
      "The inventory page-size selector was expanded beyond the previous 250-row ceiling and now allows 500 or 1000 rows in one page while the backend page-size guard was raised to match.",
      "Bulk carrier-check runs now create persistent history records in the database, storing request ids, filter snapshots, S3 manifest keys, result keys and summarized outcome counts.",
      "Admins can open a new `History` dialog from `Label Checks`, review previous runs and download a manifest-derived report that lists totals and problematic tracking outcomes for that specific control batch."
    ],
    details: [
      {
        title: "Upload-Date Filtering Across Admin Label Workflows",
        items: [
          "Both `/admin/label-sources` and `/admin/label-checks` now expose upload-date filters so admins can narrow the working set by the labels' creation timestamps instead of relying only on text, product or status filters.",
          "A quick `Today` action fills the current local date bounds automatically, which makes it faster to review the labels uploaded on the same day before auditing or forwarding them for control.",
          "The backend query helpers were updated to accept `createdAtFrom` and `createdAtTo`, ensuring the visible filter state and the server-side record selection stay aligned."
        ]
      },
      {
        title: "Higher Inventory Page Sizes",
        items: [
          "The admin label inventory page-size selector now includes `500` and `1000` in addition to the earlier smaller options.",
          "This required a matching backend safeguard update because the previous hard cap of `250` would have rejected larger requests even if the dropdown exposed them.",
          "The change is intentionally scoped to the inventory tooling and keeps the existing pagination model in place, while giving operations staff a faster way to scan larger batches on one screen."
        ]
      },
      {
        title: "Persistent Carrier Check Run History",
        items: [
          "A new `labelCheckRun` model now stores background carrier-check batch metadata, including the source of the run, request id, selected filters, S3 query or manifest references and summarized result counters.",
          "Both filtered and explicitly selected bulk carrier-check flows now create these records when a batch is queued, so admins no longer lose the context of what was sent once the background worker takes over.",
          "The polling flow was extended to sync persisted runs from S3 result or manifest snapshots, which keeps the history entry updated as the external control process progresses or completes."
        ]
      },
      {
        title: "History Dialog and Downloadable Run Reports",
        items: [
          "The `Label Checks` page now includes a `History` dialog where admins can inspect recent carrier-check runs without leaving the audit workspace.",
          "Each history entry surfaces run status and grouped outcome counts such as open, confirmed used, faulty and error totals, making it easier to understand what happened in a large control batch at a glance.",
          "For deeper follow-up, admins can download a report generated from the stored manifest or result snapshot for that exact run, including the processed totals and the tracking rows that ended in problematic states."
        ]
      },
      {
        title: "Verification and Scope",
        items: [
          "The work captured here includes the date-filter rollout, the 500 and 1000 per-page expansion and the new label-check history and report-download support completed across the current conversation.",
          "Workspace verification for these changes included a successful `yarn tsc --noEmit` run after the related frontend, backend and model updates were applied.",
          "This release intentionally groups the operational label-management changes from the present support thread into one admin-focused version entry instead of scattering them across partial notes."
        ]
      }
    ]
  },
  {
    version: 88,
    date: "2026-03-25",
    title: "Customer Label Inventory Rollout, 1000-Label ZIP Export and UI Polish",
    summary: "This release consolidates the full customer label inventory thread completed across the current conversation: the customer portal gained a new `Label Inventory` section under `Shipments`, customers can now view only their assigned labels in an admin-inspired inventory screen, bulk ZIP export was added and expanded to 1000 labels with stronger storage fallbacks, and follow-up UI cleanup removed wasted space and redundant inventory summary content.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Customer navigation now includes a dedicated `Label Inventory` page directly beneath `Shipments`, giving customers a clear place to view labels assigned to their own account.",
      "The new customer inventory screen follows the admin label inventory design direction while staying customer-scoped, with summary cards, filters, paginated rows, per-label download and assigned-label visibility only.",
      "Customers can export filtered or explicitly selected labels as a single ZIP archive, and the export ceiling was raised from 500 to 1000 labels in one request.",
      "ZIP export preparation was hardened so larger archives no longer fail just because inline transfer is too big or the primary signed-URL path cannot return a usable download URL.",
      "The same thread also cleaned up customer-facing layout issues by tightening the shipments filter grid and removing the unnecessary `Products` summary card from the new inventory page."
    ],
    details: [
      {
        title: "New Customer Label Inventory Area",
        items: [
          "A new customer route and sidebar item were added so `Label Inventory` appears in the customer portal directly below `Shipments`.",
          "The page was built to reflect the stronger admin `/admin/label-sources` inventory layout, but it is adapted for customers so they only see labels assigned to themselves.",
          "The customer inventory view includes label totals, ready-to-use, consumed and void counts, along with searchable and filterable inventory rows."
        ]
      },
      {
        title: "Customer-Scoped Downloads and Export Actions",
        items: [
          "Individual label downloads on the customer inventory page were protected with ownership checks so a customer can only fetch labels assigned to their own user or customer account.",
          "Bulk inventory export supports both `Export Filtered` and `Export Selected`, packaging the chosen label PDFs into one archive instead of forcing one-by-one downloads.",
          "When some PDFs are missing, the export still completes for available items and documents skipped labels inside `00-export-report.txt` within the generated ZIP."
        ]
      },
      {
        title: "1000-Label ZIP Capacity and Resilient Delivery",
        items: [
          "The export limit was increased from 500 to 1000 labels so customers can download larger assigned-label batches in one run.",
          "Large archives first try the normal object-storage writer, then fall back to a process-env-based S3 upload path when the main storage path does not return a usable archive key.",
          "If a signed S3 URL still cannot be produced after the archive is stored, the app now falls back to a dedicated ZIP download route that can redirect to or proxy the stored archive for the customer."
        ]
      },
      {
        title: "Customer UI Cleanup and Layout Fixes",
        items: [
          "The customer shipments filter area was rebalanced so the controls fit the available grid more naturally and no longer leave a large empty block in the center of the page.",
          "The new label inventory overview was simplified by removing the unnecessary `Products` summary card after rollout feedback showed it added noise without helping the customer.",
          "These refinements were completed as part of the same support thread so the new inventory flow launches with cleaner spacing and simpler top-level inventory metrics."
        ]
      },
      {
        title: "Verification and Thread Scope",
        items: [
          "The work recorded here includes the customer menu addition, the customer inventory page, ZIP export support, the 1000-label increase, the ZIP fallback fix and the customer layout cleanup completed across the conversation.",
          "Workspace verification during the thread included TypeScript checks and production builds after the main customer inventory and ZIP export updates.",
          "This release intentionally supersedes the earlier partial thread summary by collecting the full set of completed customer inventory changes discussed in the present conversation into one versioned entry."
        ]
      }
    ]
  },
  {
    version: 87,
    date: "2026-03-25",
    title: "Customer Label Inventory, ZIP Export Resilience and Inventory UI Cleanup",
    summary: "This release records the full customer inventory thread completed in the current conversation: a new `Label Inventory` workspace was added to the customer portal, assigned labels gained customer-scoped ZIP export and secured download handling, the export limit was raised to 1000 labels with stronger S3 and proxy fallbacks, and the customer shipment or inventory layouts were tightened to remove wasted space and redundant summary cards.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Customer portal navigation now includes a dedicated `Label Inventory` entry directly under `Shipments`, opening a customer-facing inventory page modeled on the admin inventory design but scoped only to labels assigned to the signed-in customer.",
      "The new page surfaces assigned-label summary cards, search, status and product filters, per-label PDF download, checkbox selection and bulk ZIP export for either filtered results or explicit selections.",
      "Label export throughput was increased from 500 to 1000 labels per request so larger customer inventory batches can be downloaded without splitting the job manually.",
      "Bulk ZIP preparation no longer fails silently when inline export is too large and the primary signed-URL storage path cannot respond; the app now falls back through process-env S3 upload and a dedicated app-hosted ZIP download route.",
      "Customer-facing layout cleanup in the same thread removed wasted filter space on `/customer/shipments` and dropped the redundant `Products` summary card from the new inventory screen."
    ],
    details: [
      {
        title: "Customer Label Inventory Workspace",
        items: [
          "A new customer route and sidebar item were added so `Label Inventory` appears directly below `Shipments` in the customer portal navigation.",
          "The page reuses the stronger admin inventory visual pattern but is adapted for customers, showing only labels assigned to the signed-in user rather than global stock management data.",
          "Inventory rows include product, tracking, weight, assignment, consumption and creation timestamps together with customer-safe label download actions."
        ]
      },
      {
        title: "Bulk ZIP Export and Ownership Safety",
        items: [
          "Customers can now export either the currently filtered inventory set or a manually selected subset of labels as one ZIP archive from the inventory screen.",
          "Download permission checks were hardened so individual label downloads and bulk inventory exports respect customer ownership instead of trusting a raw label id.",
          "The export response can return either a direct archive URL or inline data for smaller archives, while skipped labels are documented inside `00-export-report.txt` within the ZIP."
        ]
      },
      {
        title: "1000-Label Capacity and Storage Fallbacks",
        items: [
          "The maximum customer inventory export size was increased from 500 to 1000 labels in one request to better fit real customer batch sizes.",
          "When the archive is too large for inline transfer, the backend now first tries the standard storage writer, then falls back to a process-env-driven S3 upload if the primary write path does not return a usable key.",
          "If signed URL generation is still unavailable after storage succeeds, the export now falls back to a dedicated application route that can redirect to or proxy the stored ZIP so the customer still receives a downloadable archive."
        ]
      },
      {
        title: "Customer Layout Refinements",
        items: [
          "The customer shipments filter grid was adjusted so the controls fit their available columns cleanly instead of leaving a large empty block in the middle of the page.",
          "The new inventory overview was simplified by removing the redundant `Products` summary card once it became clear that the number added little value for customers.",
          "These UI adjustments were completed in the same support thread so the new inventory feature ships with tighter spacing and a cleaner at-a-glance header."
        ]
      },
      {
        title: "Verification and Support Notes",
        items: [
          "The customer inventory route, export path and follow-up ZIP resilience fix were validated in the workspace with TypeScript checks and production builds during the thread.",
          "A later support report that showed `Export archive could not be prepared.` was traced to the no-URL return path in the export action, and the release now captures the fallback work added in response.",
          "This release entry intentionally summarizes the full customer label inventory, export and layout work completed across the present conversation rather than only one isolated patch."
        ]
      }
    ]
  },
  {
    version: 86,
    date: "2026-03-25",
    title: "Legacy Shipment Recovery, Return-To-Stock Hardening and Pagination Stability",
    summary: "This release records the full shipment support thread completed in the current conversation: old cancel and return flows were hardened for legacy label rows, admin shipment statistics were repaired end to end, oversized shipment pagination errors were removed from customer order screens, and `Return To Stock` gained a final metadata-based label-product fallback so older shipments can be recovered even after direct product links were lost.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin shipment cancel and return flows now recover older labels through relation-id fallback, purchase fallback, original `VOID-...` tracking recovery and, as a final safety net, source-metadata-based label product inference.",
      "Legacy `Return To Stock` failures that ended with `Label product could not be determined` were hardened further so missing direct product links no longer block recovery when the label file metadata still identifies the DHL product.",
      "The `/admin/shipments` statistics cards were fixed across frontend query shape, access control and backend pagination, so Today, Yesterday and rolling-window totals load reliably instead of failing or sticking at zero.",
      "A recurring `GGT_INVALID_PAGE_SIZE` problem on shipment-related customer order views was removed by replacing oversized direct queries with a paginated server-side relation loader that always respects Gadget limits.",
      "Shipment recovery logging now records much richer diagnostic context, including missing source or product links and inferred product metadata, so future support issues can be traced without guesswork."
    ],
    details: [
      {
        title: "Shipment Cancel and Return Recovery",
        items: [
          "The admin shipment inventory action was updated so label recovery no longer depends only on the newest shipment relations; it now falls back through shipment, relation-object, purchase and older `VOID-<shipmentId>-<tracking>` recovery paths before giving up.",
          "This means both `Cancel` and `Return To Stock` handle older rows more safely when the original shipment has already been voided, the source lost its `consumedByShipment` relation or one of the foreign-key fields was stored incompletely.",
          "When a shipment is recovered successfully, the action also backfills missing shipment and label-source product links so the same legacy row is less likely to fail again later."
        ]
      },
      {
        title: "Metadata-Based Label Product Inference",
        items: [
          "A final fallback was added for the oldest legacy cases where shipment, label source and purchase records have all lost direct `labelProduct` links.",
          "In those cases, the recovery action now inspects source metadata such as `originalFileName`, `savedFileName`, `savedKey`, `weightGrams` and `weightText` and matches that information against active label products.",
          "This specifically hardens old DHL label rows where names like `5 kg`, `2 kg` or `Kleinpaket` can still be inferred from stored file or weight hints even though the relational product ids are gone."
        ]
      },
      {
        title: "Admin Shipments Statistics Repair",
        items: [
          "The shipment statistics cards on `/admin/shipments` were fixed in multiple layers after a production error where the frontend queried the action result with the wrong GraphQL shape.",
          "Access control for the dedicated stats action was completed and the backend counter was updated to paginate safely across the full shipment dataset instead of relying on incomplete or order-sensitive batches.",
          "As a result, Today, Yesterday and rolling 7, 30 and 365 day counts now load correctly and no longer fail with field-query errors or remain stuck at zero while recent shipments exist."
        ]
      },
      {
        title: "Pagination and Production Stability",
        items: [
          "A separate recurring production error caused by `first: 1000` shipment queries was traced to customer order relation loading and removed by moving that lookup into a paginated backend action.",
          "That change keeps shipment, order and related label lookups within Gadget's page-size limits, so the customer order experience no longer floods production logs with `GGT_INVALID_PAGE_SIZE` failures.",
          "The release was verified during the support thread with targeted shipment-recovery tests, admin inventory resolution tests, `yarn build`, `ggt problems` and fresh deploys after each critical fix."
        ]
      },
      {
        title: "Operational Diagnostics",
        items: [
          "The shipment inventory action now logs much more detail when recovery cannot determine the original customer, linked label source or effective product.",
          "These warnings now include relation-id fallbacks, purchase ids and source metadata hints so future support investigations can identify the exact missing link instead of only seeing the generic toast shown in the UI.",
          "This release entry intentionally summarizes all shipment, statistics and pagination work completed across the present conversation rather than only one isolated patch."
        ]
      }
    ]
  },
  {
    version: 85,
    date: "2026-03-25",
    title: "DHL Proxy Audit, Dedicated Label Checks Workspace and S3-Based Bulk Carrier Processing",
    summary: "This release captures the full label-audit support thread completed in the current conversation: DHL carrier verification was migrated onto the external tracking proxy for direct checks, label auditing was split out from stock inventory into its own `Label Checks` workspace, bulk verification moved to an S3 query/result exchange with background polling, and the resulting carrier data now drives clearer open, confirmed-used, faulty and auto-void decisions for reusable labels.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Direct DHL carrier checks no longer rely on the earlier DHL public-detail flow and now use the external tracking proxy response format, including status code, German status text and carrier event interpretation.",
      "Label auditing was moved out of the crowded `Label Inventory` page into a dedicated `Label Checks` admin screen with its own filters, bulk actions, details sheet and navigation entry.",
      "Bulk label verification no longer hammers the live proxy directly from the app; instead it exports filtered or selected tracking queries to S3, then polls S3 result and manifest files in the background and writes completed answers back into Kenpost.",
      "Carrier classification was tightened so shipment movement or event history marks a reusable label as used, while true `not_found` or no-movement responses remain open; available labels that show carrier activity are auto-voided as faulty to keep them out of saleable stock.",
      "The label-check UI was simplified during rollout with compact tooltip details, better consumed-label messaging, safer filtered-bulk handling and a final runtime fix for the page-level `Application Error` caused by a missing icon import."
    ],
    details: [
      {
        title: "DHL Proxy Integration and Result Mapping",
        items: [
          "Single-label carrier checks were remapped onto the external DHL tracking proxy so direct admin rechecks now interpret `status_code`, `status_text_de`, delivery flags and returned event payloads instead of the older DHL detail scrape flow.",
          "Carrier summaries shown to admins now preserve richer provider data such as source, cached state, timestamps and German carrier status text, and the detail view can surface those values without exposing raw backend structures.",
          "Consumed labels whose carrier result confirms use are now treated as expected `Confirmed used` inventory instead of being presented as faulty stock."
        ]
      },
      {
        title: "Dedicated Label Checks Workspace",
        items: [
          "A new `Label Checks` menu entry was added directly under `Label Inventory`, giving carrier audit work its own screen while leaving inventory counts, stock filters and upload flows focused on stock management.",
          "The new workspace carries search, product, inventory-state and carrier-outcome filters plus per-row recheck, restore and detail actions, while the old inventory screen was decluttered by removing the embedded carrier-check controls.",
          "Carrier-check detail text inside the grid was later compressed behind an `i` tooltip so the table can scale better across larger pages without losing access to check explanations."
        ]
      },
      {
        title: "Bulk Checks via S3 Query and Result Exchange",
        items: [
          "Filtered and selected bulk audit actions now write a snapshot JSON query file to the S3 tracking exchange instead of immediately calling the live carrier proxy for every row.",
          "The app then schedules background polling every five minutes, reads the per-day manifest and result JSON files, applies any completed answers back to the related label sources and stops polling once the request is complete or terminal.",
          "This externalized bulk flow keeps the in-app user experience lightweight, avoids unnecessary repeated polling once a batch finishes and lets the external worker own the heavy carrier processing."
        ]
      },
      {
        title: "Carrier Safety Rules and Inventory Outcomes",
        items: [
          "Carrier result handling now treats movement signals such as shipment events or live transit-style statuses as proof that a supposedly reusable label was already used outside Kenpost.",
          "When that happens on a label that is still `available`, the app automatically moves it to `void` and counts it as faulty so it can be followed up through Missing Labels or DHL support rather than sold again.",
          "True open responses such as `not_found` or no-movement states remain available, while consumed labels that are confirmed by the carrier stay consumed and only their audit trail is refreshed."
        ]
      },
      {
        title: "Operational Hardening and Rollout Fixes",
        items: [
          "The rollout included admin permission fixes for the new queue and polling actions, filtered-bulk safeguards so a run started for `available` labels cannot later spill into other inventory states, and retry or timeout tuning around background processing.",
          "Customer-facing toast copy for background bulk checks was rewritten to describe a normal delayed audit flow without exposing S3 implementation details, while single-row `Recheck` continues to use the direct live proxy path.",
          "A production `Application Error` on `/admin/label-checks` was traced to a missing `Download` icon import in the new page and fixed in the same support thread, followed by a fresh production deploy."
        ]
      }
    ]
  },
  {
    version: 84,
    date: "2026-03-25",
    title: "Admin Shipment Statistics and Consolidated Sidebar Navigation",
    summary: "This admin release captures the full workspace improvements completed in the current conversation: `/admin/shipments` now shows dynamic label-generation totals for today, yesterday and rolling 7, 30 and 365 day windows, while the crowded left navigation was reorganized into cleaner collapsible Labels and Billing sections without changing the underlying admin pages.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The admin shipments page now surfaces live summary cards for Today, Yesterday, Last 7 Days, Last 30 Days and Last 365 Days so operations can see label volume instantly.",
      "Shipment statistics are loaded from a dedicated backend action instead of the visible table rows, so the cards are not capped by the page's `first: 250` shipment query.",
      "The statistics request respects the admin browser time zone and refreshes together with the existing page reload controls, keeping date-window totals aligned to the viewer.",
      "The left admin navigation is no longer a long flat list: label-related pages now live under a collapsible `Labels` parent menu.",
      "Plans, purchases and invoices were also consolidated into a collapsible `Billing` section, reducing menu noise while preserving each screen as its own route."
    ],
    details: [
      {
        title: "Shipment Statistics Cards",
        items: [
          "The `/admin/shipments` header area now includes five overview cards that report how many labels were created today, yesterday, in the last 7 days, in the last 30 days and in the last 365 days.",
          "These values are fetched through a separate admin action that counts shipment records by `createdAt`, which means the totals remain accurate even though the visible shipment table itself only loads a limited page of recent rows.",
          "The statistics payload also returns the resolved time zone and generation timestamp so admins can understand which day boundary the counts are using."
        ]
      },
      {
        title: "Dynamic Refresh and Time-Zone Alignment",
        items: [
          "Stats load automatically when the shipments page opens and are requested again alongside the normal page refresh flow so the summary cards stay current during active support or warehouse sessions.",
          "Client-side time-zone detection is passed to the backend action, allowing Today and Yesterday boundaries to follow the admin's actual locale rather than a fixed server default.",
          "This avoids misleading day-window counts when support or operations staff are working outside the server's default region."
        ]
      },
      {
        title: "Sidebar Navigation Cleanup",
        items: [
          "The admin shell navigation was refactored to support grouped items, replacing part of the long flat menu with collapsible parent sections that are easier to scan.",
          "All label-focused routes now live under `Labels`, including Label Products, Label Inventory, Label Checks, Missing Labels and Customer Prices.",
          "Plans, Purchases and Invoices now live under `Billing`, while still opening as their own dedicated pages rather than being merged into a single screen."
        ]
      },
      {
        title: "Interaction Behavior and Verification",
        items: [
          "Grouped navigation sections expand and collapse inline, and any group that contains the active route opens automatically so admins can still orient themselves immediately after navigation or reload.",
          "The shipment statistics and sidebar cleanup were validated in the local workspace with TypeScript checks and build verification during the support session before being recorded in this release history.",
          "This release entry intentionally summarizes the full admin shipments and admin navigation work completed across the present conversation rather than only one isolated patch."
        ]
      }
    ]
  },
  {
    version: 83,
    date: "2026-03-25",
    title: "Customer Label Recovery, Bulk Archive Regenerate and On-Demand Raster Downloads",
    summary: "This support release captures the full customer label thread completed in the current session: bulk ZIP packages were revalidated around the combined PDF, broken printable labels were repaired for Acrobat, bulk ZIP history gained true regenerate support and customer PNG or JPEG downloads returned as on-demand conversions without slowing normal PDF generation.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Bulk ZIP delivery for customer label batches was rechecked end to end, with explicit regression coverage and extra server visibility around the combined `00-all-labels.pdf` file inside the archive.",
      "Printable shipment PDFs now self-heal leftover page annotations and widget remnants even when `AcroForm` is no longer present, fixing Acrobat print-preparation failures seen on older labels.",
      "The customer Bulk ZIP History dialog can now regenerate the original shipment set instead of only downloading an already saved archive, so the same batch can be rebuilt on demand.",
      "The supporting permission path for customer bulk regenerate was completed so the new history action works in production rather than stopping at access-control gaps.",
      "Fast PDF-first label generation remains in place, while customer PNG and JPEG buttons are active again and convert labels only when requested."
    ],
    details: [
      {
        title: "Bulk ZIP Validation and Observability",
        items: [
          "Customer reports that a bulk ZIP was missing the single combined label PDF were reinvestigated against the live code path used by customer bulk downloads.",
          "The archive builder was refactored slightly so merged-PDF inclusion can be regression tested directly, and the ZIP preparation path now records clearer runtime signals around whether `00-all-labels.pdf` was added.",
          "This means later support investigations can distinguish a true packaging defect from stale downloads, environment drift or a customer opening an older archive."
        ]
      },
      {
        title: "Printable PDF Repair for Acrobat",
        items: [
          "A recurring Acrobat print failure was traced to labels whose pages still contained annotation or `FlatWidget` remnants even after the higher-level form dictionary had disappeared.",
          "The printable PDF normalizer now treats those page-level annotation leftovers as a repair trigger too, removes them safely and preserves the rendered label appearance.",
          "Regression coverage was added around this case so future label-generation changes do not silently reintroduce the same print-blocking PDF structure."
        ]
      },
      {
        title: "Bulk ZIP History Regenerate",
        items: [
          "The customer shipments Bulk ZIP History popup now includes a regenerate action that reruns bulk label generation for the exact shipment group originally stored with that archive.",
          "Instead of forcing the customer to rebuild the same selection manually, the saved shipment membership is sent back through the bulk regenerate flow and a fresh ZIP is produced from the same set.",
          "Production access control was updated alongside the UI so customer users can execute this history-based regenerate path successfully after deployment."
        ]
      },
      {
        title: "On-Demand PNG and JPEG Downloads",
        items: [
          "PDF generation stays optimized around the fast printable PDF path, rather than blocking the customer behind eager raster artifact creation every time a label is created or regenerated.",
          "At the same time, customer shipment and create-label screens once again expose active PNG and JPEG actions when a shipment can be rasterized, even if raster files were not pre-persisted on the server.",
          "When the customer explicitly requests PNG or JPEG, the app now performs raster conversion on demand instead of hiding the buttons or forcing slower default shipment generation."
        ]
      },
      {
        title: "Verification and Rollout",
        items: [
          "The session included targeted regression tests for combined bulk ZIP content, printable PDF repair and shipment raster support so the main customer label flows remain covered after each fix.",
          "Each support step was rebuilt and rolled through the shared production deployment workflow after validation rather than left as an unverified local-only patch.",
          "This release entry intentionally summarizes the full label and download support work completed across the present conversation, not just one isolated code change."
        ]
      }
    ]
  },
  {
    version: 82,
    date: "2026-03-25",
    title: "Admin Invoice Workspace, Credit Invoice Editing and Reference PDF Alignment",
    summary: "Admin invoices were expanded into a true cross-customer workspace with order and credit invoice visibility, manual invoice creation and editing, editable credit invoice values, corrected label-credit pricing and a PDF template refresh aligned to the Makpirtuk reference invoice.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The admin invoices screen now combines order invoices and credit invoices across all customers instead of leaving credit documents visible only from customer-side flows.",
      "Admins can now create manual invoices, edit generated order invoices and edit `CR-...` credit invoices directly from `/admin/invoices`, including quantity and unit-price adjustments that propagate to later customer downloads.",
      "Credit invoice pricing no longer falls back to zero for new manual label allocations when no customer-specific price exists; the system now resolves the customer price first and otherwise uses the label product base price.",
      "Invoice downloads were aligned to the Makpirtuk reference PDF more closely, including the seller block, footer columns, table geometry and overall spacing, while older order invoices are regenerated on download so the latest template is applied.",
      "The supporting admin invoice surface was hardened during rollout with GraphQL pagination fixes, cleaned editor problems and type-safe action cleanup so the release could be deployed without lingering app problems."
    ],
    details: [
      {
        title: "Admin Invoice Workspace",
        items: [
          "The `/admin/invoices` route now shows both order invoices and credit invoices in one admin-facing workspace, with document search plus document, order-status and credit-type filters.",
          "Credit invoices that previously existed only through customer pages are now visible to admins in the same invoice hub, including direct PDF access for support and finance workflows.",
          "The admin invoices page was also adjusted to respect Gadget pagination limits after rollout so the new invoice manager does not fail on oversized `first` values."
        ]
      },
      {
        title: "Manual Create and Edit Flows",
        items: [
          "Admins can now create manual invoices from the invoice workspace by selecting a customer, adding one or more invoice rows and entering quantity, unit price, tax and buyer details.",
          "System-generated order invoices can now be reopened in admin and edited rather than treated as immutable, which lets finance correct line items and totals before the customer downloads again.",
          "Credit invoices with `CR-...` numbers now expose an admin edit flow as well, so quantity and unit price overrides can be saved and later reflected in the regenerated PDF and customer invoice list."
        ]
      },
      {
        title: "Pricing and Data Corrections",
        items: [
          "An investigation into zero-value credit invoices showed that manually allocated admin credit purchases were being stored with `unitPrice` and `totalPrice` equal to zero.",
          "The label-purchase pricing path now resolves the applicable customer label price first and falls back to the label product base price when no customer-specific override exists, preventing new manual credit invoices from being created with unintended zero pricing.",
          "Credit invoice display and PDF generation now resolve saved admin overrides consistently so edited totals, quantities and notes stay aligned between admin and customer surfaces."
        ]
      },
      {
        title: "Reference PDF Template Alignment",
        items: [
          "The shared invoice PDF renderer was reworked against the provided Makpirtuk reference invoice so header spacing, buyer block placement, table proportions, totals block and footer typography match the reference much more closely.",
          "The seller profile used in generated invoices remains locked to the Makpirtuk company identity and footer banking data supplied during the support session.",
          "Order invoice downloads now regenerate the PDF at download time, which means older stored invoices also pick up the newer layout instead of continuing to serve stale pre-redesign PDF files."
        ]
      },
      {
        title: "Verification and Rollout",
        items: [
          "The support session also cleaned remaining editor and build issues around invoice-related actions so the Problems panel could be brought back to a clean state before release.",
          "Invoice template, admin invoice management and credit invoice editing changes were rebuilt and deployed through the shared production workflow after each major rollout step.",
          "This release note captures the full invoice-management support thread completed across the current session rather than a single narrow patch."
        ]
      }
    ]
  },
  {
    version: 81,
    date: "2026-03-24",
    title: "Customer Label Printing Reliability and Bulk ZIP Delivery Improvements",
    summary: "Customer-facing label delivery was reworked so newly created, regenerated and previously stored shipment PDFs are printable again, while manual and store bulk downloads now arrive in a more recoverable single-ZIP format with combined and ordered label files.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Customer PDF downloads now default back to the normal flattened printable PDF instead of the Adobe-only variant, reducing printer compatibility issues on customer devices.",
      "Label generation, regenerate and customer download flows now normalize printable PDFs and can self-heal older broken shipment PDFs at download time.",
      "Manual label creation and regenerate flows once again prepare PNG and JPEG artifacts, while regenerate no longer blocks the customer behind slow raster work before showing download options.",
      "Bulk label creation for manual and store-order flows now delivers a single ZIP reliably, including a combined `00-all-labels.pdf` file plus individually numbered label files such as `01-...`, `02-...` and onward.",
      "The customer shipments page now includes a bulk ZIP history popup so failed or missed bulk downloads can be retried later without recreating the same shipment batch."
    ],
    details: [
      {
        title: "Printable PDF Reliability",
        items: [
          "Customer shipment PDF downloads now resolve to the flattened normal PDF again, while the Adobe-safe artifact remains available internally for editing and compatibility-sensitive processing.",
          "The shared internal label generation pipeline used by store orders, manual labels, shipment regenerate and related customer downloads now strips problematic AcroForm data from printable PDFs during generation.",
          "If an older shipment still points at a previously broken PDF, the customer download action now repairs the PDF on demand and can persist the corrected printable copy back to storage for later downloads."
        ]
      },
      {
        title: "Create and Regenerate Flow Parity",
        items: [
          "Initial shipment creation, manual label creation and shipment regenerate were aligned back onto the same printable PDF path so the first download and later regenerate behave consistently.",
          "Raster artifact generation for manual labels was restored so PNG and JPEG outputs are again available alongside PDF when the label set supports them.",
          "Regenerate no longer waits on raster conversion before returning control to the customer, which reduces timeout pressure while still letting PNG and JPEG recover in the background or on demand."
        ]
      },
      {
        title: "Bulk ZIP Delivery",
        items: [
          "Manual bulk create and store-order or Billbee bulk create now both produce a single ZIP download rather than leaving customers to gather labels one by one.",
          "Each bulk ZIP now contains the original per-shipment label files as before, plus a combined `00-all-labels.pdf` file that merges all printable label pages into one document for easier printing.",
          "Individual files inside the ZIP are now prefixed in display order such as `01-...`, `02-...` and `03-...` so larger batches sort predictably in file explorers and print rooms."
        ]
      },
      {
        title: "Bulk Recovery and Customer Controls",
        items: [
          "Bulk ZIP preparation now retries readiness checks for freshly created shipment PDFs instead of failing immediately when storage metadata lags behind shipment creation.",
          "The customer shipments page now exposes a bulk ZIP history dialog that stores later bulk groups and lets customers re-download a past bulk package without rebuilding the same shipment selection.",
          "This release note captures the full customer-side label reliability work completed across the current support session, including printable PDF repair, regenerate parity, bulk ZIP consolidation and later ZIP packaging refinements."
        ]
      }
    ]
  },
  {
    version: 80,
    date: "2026-03-24",
    title: "Admin Shipment Recovery, Download Parity and Inventory Return Controls",
    summary: "Admin shipment operations were hardened around customer-style label downloads, explicit cancel versus return stock behavior and automatic recovery when PNG or JPEG artifacts are missing or stale.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin shipment downloads now follow the customer-side raster recovery path so PNG and JPEG handling behaves consistently across both surfaces.",
      "When an admin download would otherwise fail, the app can now regenerate the shipment in the background and retry the download automatically instead of stopping at the first error.",
      "Shipment inventory actions were split into distinct cancel and return-to-stock flows so support can either restore a label to the customer or reclaim it into available stock.",
      "Restore logic now resolves the original customer from multiple ownership links, fixing cases that previously ended with `Original customer could not be determined`.",
      "Regression coverage was added for cancel and return restore scenarios, and the updated admin shipment flow was revalidated through successful builds."
    ],
    details: [
      {
        title: "Admin Download and Regenerate Parity",
        items: [
          "The admin shipments page now mirrors the customer shipment download pipeline by using the same PDF proxy and raster artifact preparation helpers for PNG and JPEG delivery.",
          "Raster support detection was widened for internal, manual and output-key-backed labels so regenerate no longer leaves valid return shipments stuck without PNG or JPEG options.",
          "Admin-side regenerate now backfills raster artifacts after the shipment PDF is rebuilt, keeping later PNG, JPEG and ZIP downloads aligned with the newest shipment record."
        ]
      },
      {
        title: "Automatic Recovery on Download Failure",
        items: [
          "If an admin attempts to download a shipment artifact and the first attempt fails, the UI now suppresses the early error toast, regenerates the shipment when order context exists and retries the requested download automatically.",
          "This recovery path reduces support dead ends for shipments whose stored raster artifacts are missing, stale or incomplete at the moment of download.",
          "When a shipment no longer has the order linkage needed for regeneration, the flow still falls back to the final user-visible error instead of pretending recovery succeeded."
        ]
      },
      {
        title: "Cancel Versus Return Inventory Rules",
        items: [
          "Admin shipment actions now distinguish between canceling a shipment and restoring the label to the customer versus returning the label fully back into available system inventory.",
          "Cancel mode keeps the label assigned to the original customer so their credit is effectively restored, while return mode removes that ownership and makes the label reusable from stock again.",
          "Customer resolution for these operations now checks label-source assignment, shipment ownership, linked order ownership and purchase ownership fallbacks before deciding that the original customer is unknown."
        ]
      },
      {
        title: "Verification",
        items: [
          "Targeted tests were added for the admin cancel and return label restore action to confirm both inventory paths update balances correctly.",
          "The shipping-management application was rebuilt successfully after the admin shipment updates, with only the pre-existing non-blocking sourcemap warnings remaining.",
          "This release note captures the full set of admin shipment fixes completed during the current support session, from the first download parity issue through the later auto-regenerate recovery work."
        ]
      }
    ]
  },
  {
    version: 79,
    date: "2026-03-24",
    title: "Label Inventory Upload Investigation and Translation Audit",
    summary: "An end-to-end investigation of intermittent admin label upload complaints distinguished successful backend uploads from client-side error reports, confirmed hardcoded Turkish copy in the upload UI and separated that from browser-translated root error-boundary text.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Intermittent complaints around `/admin/label-sources/upload` were investigated from the first customer symptom through log review and UI string tracing.",
      "The upload screen was confirmed to ship hardcoded Turkish queue, status and helper text alongside English controls, explaining the mixed-language screenshots without relying only on browser translation.",
      "The generic white `Application Error` card reported by some users maps to the shared root error boundary, whose source text is English and can therefore appear translated by browser auto-translate tools.",
      "The investigated 2026-03-24 log export showed successful 200 upload responses and persisted label-source records rather than a confirmed backend upload outage.",
      "Operationally, the issue now looks more like an intermittent client-side or browser-specific runtime failure than a reproducible server-side label upload break."
    ],
    details: [
      {
        title: "What the Investigation Covered",
        items: [
          "The investigation covered the full support thread context: the customer-facing intermittent error report, the suspicion around browser translation tooling and the uploaded ndjson log export.",
          "The admin label inventory upload route was inspected directly to verify whether Turkish labels were coming from the application code or from browser-side translation.",
          "The shared root error boundary was checked separately so the translated white error card could be compared against its original English source copy."
        ]
      },
      {
        title: "Findings from Code and Logs",
        items: [
          "The upload route still contains multiple hardcoded Turkish strings for duplicate states, queue statuses and helper copy, so part of the mixed-language UI is native to the app rather than introduced by translate software.",
          "The white fallback card shown by affected users matches the shared root error boundary, whose source labels remain English and can therefore appear in Turkish only after browser-side translation.",
          "The investigated log export did not contain matching error-level upload entries, reportAppError captures or non-200 upload responses for the same route, despite showing successful upload completion records."
        ]
      },
      {
        title: "Operational Notes",
        items: [
          "During the investigated 2026-03-24 09:35Z to 10:33Z window, admin label upload requests completed successfully and persisted labelSource records normally.",
          "The only warnings found in that export were unrelated label-balance drift warnings from Shopify billing synchronization rather than upload-pipeline failures.",
          "The current diagnosis is therefore an intermittent client-side or browser-conditioned issue on the upload page, with follow-up work best aimed at copy cleanup and stronger client-side exception capture."
        ]
      }
    ]
  },
  {
    version: 78,
    date: "2026-03-18",
    title: "Shopify Purchase Auto Sync and Existing Order Reconciliation",
    summary: "Admin purchases now stay centered on Shopify order reconciliation instead of per-product mapping, with clearer sync outcomes and a page-open auto-sync loop for recent billing orders.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "The admin purchases screen now runs a background Shopify billing sync every five minutes while the page stays open.",
      "Manual Shopify repair sync continues to compare against the full accessible billing order history so older orders can still be found and repaired.",
      "Sync results now separate newly created purchase rows, refreshed existing purchases and already-processed orders instead of making everything look skipped.",
      "Purchase matching remains driven by KenPost checkout metadata and Shopify line item titles, so missing Shopify product catalog entries no longer block reconciliation.",
      "Operationally, the purchases screen is now the primary repair surface for Shopify billing imports instead of the older label-product mapping view."
    ],
    details: [
      {
        title: "Automatic Purchases Refresh",
        items: [
          "When an admin keeps /admin/purchases open, the page now performs a silent Shopify billing sync on load and then repeats it every five minutes.",
          "This keeps newly paid Shopify billing orders flowing into Purchases faster without requiring repeated manual button clicks during active operations.",
          "The automatic refresh stays scoped to the purchases workspace and does not replace the separate webhook pipeline."
        ]
      },
      {
        title: "Existing Order Repair Clarity",
        items: [
          "Sync summaries now distinguish between orders that created new purchase rows, orders that refreshed an existing purchase and orders that were already fully processed.",
          "A result such as `already processed` now means Shopify data was fetched and matched, but no brand-new purchase row was needed because the order was already present.",
          "This makes it easier for support staff to see the difference between a real sync failure and a successful repair pass over existing purchases."
        ]
      },
      {
        title: "Metadata-First Matching",
        items: [
          "Shopify purchase reconciliation continues to trust KenPost checkout metadata first, with Shopify line item titles as a fallback when repairing older orders.",
          "Because these purchases are created from KenPost-controlled order flows, the sync no longer depends on Shopify product-variant browsing to recover billing rows.",
          "Manual admin sync can therefore backfill or repair historical Shopify orders even when the Shopify product catalog does not contain a matching sellable variant record."
        ]
      }
    ]
  },
  {
    version: 77,
    date: "2026-03-17",
    title: "Shopify Billing Repair Visibility and Purchase Sync Clarification",
    summary: "Shopify billing repair was reframed around order metadata and existing purchase reconciliation so backfills no longer depend on Shopify product mapping and sync results explain what was actually refreshed.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Admin purchases sync now separates newly created purchase rows, refreshed existing purchases and orders that were already fully processed.",
      "Empty Shopify repair sync continues to scan the full accessible billing order history instead of relying on a short recent-only window.",
      "Purchase reconciliation now stays centered on KenPost checkout metadata and Shopify line item titles instead of per-product Shopify variant mapping.",
      "The purchases screen now makes it clear that Shopify data was fetched even when no brand-new purchase rows were created.",
      "Label product detail no longer needs to be the primary place for Shopify purchase matching because billing repair now happens at the purchases sync layer."
    ],
    details: [
      {
        title: "Purchases Sync Result Clarity",
        items: [
          "The Shopify billing sync summary now reports created, refreshed and already-processed counts separately instead of making repaired orders look like they were skipped or never fetched.",
          "Recent sync results in the admin purchases screen now label refreshed rows explicitly when an existing purchase was repaired from Shopify metadata.",
          "This makes it easier to tell the difference between a real sync failure and a successful backfill that found the orders were already present in Purchases."
        ]
      },
      {
        title: "Metadata-Driven Matching",
        items: [
          "Shopify billing reconciliation continues to use KenPost checkout metadata such as order-linked label product ids and quantities first, with Shopify line item titles as a repair fallback.",
          "Because these purchases are written from KenPost-controlled order creation, Shopify product variant browsing is no longer treated as the canonical mapping path for billing recovery.",
          "Older unmatched purchases can still be repaired during sync without requiring a separate per-product Shopify mapping workflow."
        ]
      },
      {
        title: "Operational Notes",
        items: [
          "Manual /admin/purchases repair sync remains independent from the real-time Shopify webhook path, so historical backfills can still run even if the webhook secret needs separate attention.",
          "When sync reports that orders were already processed, the underlying purchase rows may still have been reattached to label sources or label balances during repair.",
          "This release captures the admin-facing visibility improvements around Shopify billing repair rather than a change to customer checkout behavior."
        ]
      }
    ]
  },
  {
    version: 76,
    date: "2026-03-17",
    title: "Internal Label Delivery, Acrobat Safety and Address Fit Corrections",
    summary: "Internal shipment downloads, regenerate flows and DHL label address rendering were tightened so regenerated labels resolve the right artifact set and long German street lines fit more reliably.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Bulk customer label downloads now include the internally generated PNG and JPEG artifacts alongside the PDF in one ZIP package.",
      "Internal shipment PDF delivery now prefers the Acrobat-safe artifact across regenerate, direct download and bulk ZIP flows.",
      "Billbee store filtering on the customer orders page no longer resets the selected source store immediately after selection.",
      "German street lines on internal DHL labels now try `Straße -> Str.` first and only then reduce font size if the line still does not fit the label field width.",
      "Admin-side regenerate for customer shipments now follows the same corrected address-fit pipeline used by the customer flow."
    ],
    details: [
      {
        title: "Label Artifact Delivery",
        items: [
          "Customer bulk ZIP downloads now package the internal PDF together with any generated PNG and JPEG label artifacts so the full label set arrives in one archive.",
          "Internal shipment download actions were aligned around the Adobe-safe PDF as the canonical direct-download artifact, reducing Acrobat print failures from the older flattened variant.",
          "Regenerate updates now relink the shipment back to the fresh internal artifact set so later customer, admin and ZIP downloads resolve the newest files."
        ]
      },
      {
        title: "Address Rendering and Regenerate",
        items: [
          "Internal DHL address rendering now merges street and house-number continuation lines before deciding whether the line fits the field.",
          "The fit check was upgraded from a simple character-count heuristic to a real label-width calculation based on the actual Helvetica field width, which catches lines like `Bürgermeister-Fuchs-Straße 41a` that were visually too wide despite being under the old threshold.",
          "If the merged street line still does not fit, the generator abbreviates `Straße` or `Strasse` to `Str.` first and only shrinks the field font when abbreviation alone is not enough."
        ]
      },
      {
        title: "UI and Operational Fixes",
        items: [
          "The customer orders Billbee store dropdown now keeps the chosen source store instead of restoring the previous state from stale sync logic.",
          "Admin shipment regenerate continues to support older orphaned or partially linked rows, while now benefiting from the same corrected internal label rendering behavior.",
          "Regression tests were expanded around manual label line fitting so both max-font and shrink-required paths stay covered."
        ]
      }
    ]
  },
  {
    version: 75,
    date: "2026-03-17",
    title: "Customer Raster Artifact Recovery and PDF.js Font Delivery",
    summary: "Manual shipment downloads were hardened again around production font serving, customer-side PNG or JPEG preparation and safer regenerate follow-up behavior.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "PDF.js standard fonts are now served through a real app route in production, removing a hidden blocker that prevented customer-side PNG or JPEG preparation from starting.",
      "Customer shipment download flows were reworked to prefer the flattened normal PDF artifact before attempting PNG, JPEG or ZIP generation.",
      "PNG or JPEG preparation now uses a browser-side recovery pipeline instead of depending purely on the crash-prone server raster worker path.",
      "Manual shipment regenerate remains responsible for rebuilding the canonical PDF artifact, while follow-up raster preparation can be retried from the customer screens without re-consuming a label.",
      "Additional diagnostics were added around customer raster preparation so missing artifacts can be distinguished from download-trigger issues more quickly."
    ],
    details: [
      {
        title: "PDF.js and Browser Preparation",
        items: [
          "A dedicated `/pdfjs/standard-fonts/:font` route now serves the standard font files expected by PDF.js in production, replacing the earlier broken static path.",
          "Customer shipment download helpers now point at the live font route and use a more Safari-friendly browser download sequence for generated PNG and JPEG files.",
          "Raster preparation was moved toward browser-side recovery from the stored normal PDF artifact so standard preview generation no longer depends entirely on native worker stability in the backend sandbox."
        ]
      },
      {
        title: "Manual Shipment Artifact Flow",
        items: [
          "Manual and internal shipment download actions now prefer the KenPost-generated normal PDF when customers request preview-style artifacts instead of falling back to the Adobe-safe form PDF too early.",
          "Regenerate continues to rebuild and relink the shipment PDF artifact, including older rows that were missing direct order or owner links.",
          "ZIP download handling remains aligned with the regenerated shipment artifact set so later retries can include newly prepared raster outputs once they exist."
        ]
      },
      {
        title: "Diagnostics and Recovery Visibility",
        items: [
          "Customer shipment screens now report more specific raster-preparation failures back into the app logs, making it easier to separate font-loading problems, browser-generation failures and true missing-artifact cases.",
          "Recent work also keeps the broader preparing-state and regenerate-recovery hardening from v74 in place while the PNG or JPEG recovery path continues to be stabilized.",
          "This release captures the production hardening work around customer-visible manual label previews rather than a brand-new label engine."
        ]
      }
    ]
  },
  {
    version: 74,
    date: "2026-03-16",
    title: "Shipment Recovery, Regenerate and Download Hardening",
    summary: "Manual and internal shipment flows were hardened around stuck labels, regenerate recovery, customer shipment visibility and safer artifact delivery.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Customer and admin shipment screens were aligned around the newer regenerate and artifact-download workflow used by the manual-label workspace.",
      "Legacy manual shipments that were left pending after background crashes now have stronger recovery paths instead of staying indefinitely in a preparing state.",
      "Regenerate access, customer-scoping and orphan-order fallbacks were tightened so older shipment records can be repaired without direct data edits.",
      "Customer shipment visibility now resolves the signed-in user more defensively, preventing real shipments from disappearing behind an empty list view.",
      "Manual label artifact handling was reworked around safer PDF-first recovery, raster backfill attempts and richer diagnostics for missing PNG or JPEG outputs."
    ],
    details: [
      {
        title: "Regenerate and Pending Recovery",
        items: [
          "Shipment regenerate now carries stronger order-context fallbacks for orphaned or partially linked manual shipments, including older rows where the direct shipment-order relationship was missing.",
          "Pending internal labels gained self-heal paths so stale manual shipments can be retried, completed or surfaced as actionable failures instead of sitting forever in a preparing state.",
          "Admin and customer regenerate permissions were realigned with the actual signed-in session roles used by older customer accounts."
        ]
      },
      {
        title: "Customer and Admin Shipment Surfaces",
        items: [
          "Customer shipments was refreshed to better match the manual-label workspace, including regenerate controls and richer artifact actions across list and detail views.",
          "Customer shipment visibility now resolves session user ids more defensively, preventing valid customer shipments from dropping out of the list behind a false zero-state.",
          "Admin shipment recovery flows now target the correct customer context more consistently when support operators regenerate labels on behalf of users."
        ]
      },
      {
        title: "Artifact Delivery and Diagnostics",
        items: [
          "Manual label delivery was hardened around PDF-first fallbacks, ZIP safety and raster backfill attempts so missing preview files can be diagnosed and retried without crashing the whole app.",
          "Additional runtime tracing was added around regenerate, shipment visibility and customer download actions to make environment-specific failures easier to isolate.",
          "The release also includes ongoing work to reduce crash-prone raster-generation paths while preserving normal PDF delivery for customer-visible manual shipments."
        ]
      }
    ]
  },
  {
    version: 73,
    date: "2026-03-16",
    title: "DHL Tracking Cutover and Purchase Repair Controls",
    summary: "Carrier checks, purchase reconciliation and admin recovery tooling were realigned around DHL production tracking and safer back-office repair flows.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Admin label-source rechecks now use DHL Parcel DE tracking instead of the previous third-party fallback path.",
      "Carrier verdict handling was tightened so already consumed labels are not downgraded to open when DHL responds with incomplete public-tracking data.",
      "Admin shipments gained a cancel-and-restore flow that returns the linked label to the original customer balance instead of releasing it back into public stock.",
      "Shopify billing reconciliation moved fully to order metadata and line-item title matching, while purchases sync now reports already-processed orders explicitly.",
      "Bulk shipment downloads and manual-label creation flows were hardened with single-ZIP delivery and a faster address-entry experience."
    ],
    details: [
      {
        title: "DHL Tracking and Carrier Decisions",
        items: [
          "The DHL Parcel DE production tracking integration now uses the official API credential set and no longer relies on Track123 or variant-matching heuristics for admin rechecks.",
          "Used-versus-open decisions now prefer definitive movement evidence and keep consumed labels in a used state even when a public DHL response only says no data was found.",
          "Carrier-check detail views and list badges were updated so older third-party wording can be phased out after a fresh recheck."
        ]
      },
      {
        title: "Admin Recovery and Inventory Safety",
        items: [
          "Admin shipment cancellation now voids the shipment record but restores the linked label back to the original customer assignment instead of making it globally available.",
          "Customer balances are resynchronized during restore flows so credit counts follow the recovered label correctly.",
          "Void labels that were previously parked during support or test activity can now be restored from the admin inventory workflow with clearer intent."
        ]
      },
      {
        title: "Purchases, Bulk Downloads and Manual Labels",
        items: [
          "Shopify purchases sync now explains when orders were already processed instead of lumping them into a generic skipped count.",
          "Billing repair logic now focuses on KenPost checkout metadata and Shopify order line-item titles, replacing the older label-product Shopify variant mapping approach.",
          "Bulk label creation downloads were hardened around a single ZIP delivery path, and manual-label address entry now behaves more like checkout autocomplete without blocking creation on extra validation steps."
        ]
      }
    ]
  },
  {
    version: 72,
    date: "2026-03-14",
    title: "Root Crash Guard and Manual Label Stability",
    summary: "Root-level request handling and the manual label workspace were hardened so stray submits no longer crash the app shell.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "The root app shell now uses the React Router document layout pattern, preventing duplicate `<head>` rendering during error states.",
      "Unexpected `POST /` requests are now redirected safely instead of crashing the root route with a missing action error.",
      "The manual label screen and customer workspace buttons now declare non-submit behavior explicitly, reducing accidental form submissions.",
      "Customer-side support and navigation controls were aligned with safer button and link markup.",
      "Manual label stability was rechecked with a production build and the internal manual label generator test suite."
    ],
    details: [
      {
        title: "App Shell Safety",
        items: [
          "Root document rendering was moved into a dedicated layout so normal route rendering and error rendering no longer compete over the same `<head>` tag.",
          "The root route now handles direct POST traffic by redirecting signed-in users back into the correct app area instead of surfacing an unhandled method error.",
          "A custom root error boundary now degrades gracefully without taking down the whole customer session."
        ]
      },
      {
        title: "Manual Label Workflow",
        items: [
          "Non-submit buttons on the manual label page now use explicit `type=\"button\"` handling so auxiliary actions do not trigger stray form posts.",
          "Recent manual label actions such as refresh, regenerate and artifact downloads were tightened to behave as pure UI actions.",
          "The manual label generator regression checks and full production build both passed after the stability patch."
        ]
      }
    ]
  },
  {
    version: 71,
    date: "2026-03-14",
    title: "Internal Store Labels, Bulk Preview and Admin Recovery",
    summary: "Store-order fulfillment, bulk label creation and admin recovery tooling were aligned around the internal label engine and safer operational controls.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Store orders, bulk order labels and regenerate flows now use the same internal label generation path as manual labels instead of the external PDF worker.",
      "Bulk order creation now keeps selections across pages, opens a preview step, supports per-order address edits and lets operators choose the correct label product before confirming.",
      "Bulk downloads were aligned around a single ZIP flow, while admin and customer shipment screens gained the newer PDF, PNG, JPEG and ZIP download handling for internal labels.",
      "Admin shipments and purchases gained recovery tools so wrongly assigned labels can be refreshed, reselected and regenerated on behalf of customers.",
      "Shopify label-product mapping now supports live variant search and exact manual matching, reducing accidental product mismatches in billing sync."
    ],
    details: [
      {
        title: "Internal Fulfillment",
        items: [
          "The internal DHL template engine was extended beyond manual labels to cover store-order create, bulk create and regenerate operations.",
          "Template selection now follows the consumed label source itself, preventing Kleinpaket purchases from rendering on the wrong heavier DHL template.",
          "Recent manual labels, customer shipment downloads and admin shipment downloads now understand the internal artifact set more consistently."
        ]
      },
      {
        title: "Bulk and Customer Workflow",
        items: [
          "Order selections now survive pagination, store filters are remembered and bulk creation no longer silently processes only the currently visible page.",
          "A new bulk preview step gives users one last review pass for addresses and per-shipment product choice before labels are created.",
          "Shipment page-size controls were expanded and bulk output now favors one ZIP package instead of fragmented downloads."
        ]
      },
      {
        title: "Admin Recovery and Mapping",
        items: [
          "Admin shipments now survive orphaned order records, offer safer regenerate controls and support customer-owned label reselection during recovery.",
          "Admin purchases gained a refresh operation to realign purchase-linked labels before regenerating previously misbuilt shipments.",
          "Label products can now be linked to Shopify variants through live search, and billing reconciliation now relies on explicit Shopify ids or SKU mappings instead of title guesses."
        ]
      }
    ]
  },
  {
    version: 70,
    date: "2026-03-13",
    title: "Internal Manual Label Generation and Recent Label Hub",
    summary: "Manual labels now render fully inside Kenpost, store multiple artifacts in S3 and can be reviewed directly from the create-label screen.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Manual label creation no longer depends on the external PDF API and is now generated internally from the DHL template.",
      "Each manual label run now produces PNG, JPEG, Adobe-safe PDF and rasterized normal PDF artifacts for storage and download.",
      "Manual regenerate flows were aligned with the same internal generation path without changing normal order label generation.",
      "The create-label page now shows the latest 50 manual labels with direct PDF and ZIP download actions.",
      "Germany-only and Packstation or Postfiliale blocking rules remain active in the manual flow."
    ],
    details: [
      {
        title: "Manual Label Engine",
        items: [
          "Manual labels now update the PDF template directly, rebuild form appearance streams and keep the sensitive routing barcode logic inside the backend.",
          "Routing text, routing code and Refnr enrichment are produced server-side so manual shipments no longer rely on an external label worker.",
          "The internal flow stores generated files under the manual shipment S3 prefix and returns canonical artifact locations for later downloads."
        ]
      },
      {
        title: "Customer Workflow",
        items: [
          "The create manual label page now refreshes in place after successful single or bulk creation instead of forcing users away from the screen.",
          "A new recent manual labels section lists the newest 50 manual shipments only, with order, recipient, tracking, status and download controls.",
          "Operators can stay on the same page, create another label and immediately re-download the latest manual outputs from below."
        ]
      }
    ]
  },
  {
    version: 69,
    date: "2026-03-13",
    title: "Diagnostics and Recovery Hardening",
    summary: "Carrier checks, Shopify billing recovery and customer administration were tightened to fail safer and recover missed flows more reliably.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "DHL carrier checks now run through a conservative staged diagnostic pipeline instead of trusting generic HTML responses.",
      "Automatic voiding only happens for high-confidence used labels backed by definitive JSON or rendered tracking evidence.",
      "Shopify payment confirmation now fetches the full billing order payload so missed webhooks can still allocate credits correctly.",
      "Admin customer management now includes older or imported customer accounts even if they are missing an explicit customer role.",
      "Password validation now fails earlier with clearer guidance before weak sign-up attempts reach record save."
    ],
    details: [
      {
        title: "Carrier Check Safety",
        items: [
          "DHL tracking now records request variants, confidence, retryability and response source so inconclusive checks stay auditable.",
          "Toolbar error pages, SPA shells and generic HTML are treated as inconclusive errors instead of being guessed as clean or used.",
          "High-confidence auto-voiding is restricted to confirmed used labels from tracking JSON or rendered tracking evidence."
        ]
      },
      {
        title: "Billing Recovery",
        items: [
          "Customer-side Shopify payment confirmation now reloads the full order metadata needed for Kenpost note parsing and allocation.",
          "When a paid Shopify order still cannot be allocated, the system now emits clearer warnings for follow-up instead of silently stalling."
        ]
      },
      {
        title: "Customer Operations",
        items: [
          "Admin customer listings now include legacy non-admin user accounts that were missing the explicit customer role in older data.",
          "Customer activate and deactivate controls were aligned with the same account detection logic to avoid management dead ends."
        ]
      }
    ]
  },
  {
    version: 68,
    date: "2026-03-12",
    title: "Inventory Safety, Internal Uploads and Release Center",
    summary: "Label inventory, Shopify billing, internal PDF uploads and version tracking were hardened in one release.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "PDF label uploads moved inside the app and no longer depend on an external worker for this page.",
      "Upload queue was sped up with safer parallel workers, bounded retries and adaptive backoff.",
      "Duplicate labels are skipped before S3 overwrite and sold or consumed labels are protected from public updates.",
      "Admin label inventory and missing-label workflows were rebuilt with carrier checks, exports and safer stats.",
      "A professional release notes page replaced the raw deployment counter."
    ],
    details: [
      {
        title: "Label Inventory",
        items: [
          "Admin label inventory now shows accurate totals, filters, page state and fallback product or owner labels.",
          "Manual carrier check support was added for DHL so suspicious labels can be marked faulty and moved into the missing-label workflow.",
          "Faulty labels can now be exported from the missing-label screen as CSV or Excel plus grouped ZIP downloads."
        ]
      },
      {
        title: "PDF Upload Pipeline",
        items: [
          "The upload route now persists label records server-side, reducing one extra client database round-trip per file.",
          "The queue uses a continuous worker pool instead of fixed chunks, so fast files are not blocked by the slowest item in a chunk.",
          "Network and transient server failures trigger backoff instead of hammering the backend."
        ]
      },
      {
        title: "Shopify and Purchases",
        items: [
          "Shopify billing reconciliation and duplicate purchase protection were tightened.",
          "Admin purchases now link back to Shopify order pages when billing metadata is available."
        ]
      }
    ]
  },
  {
    version: 67,
    date: "2026-03-11",
    title: "Label Inventory Control Pass",
    summary: "Operational control screens around label sources were consolidated and made safer for support use.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Admin label sources was rebuilt around a dedicated action-backed data source.",
      "Label inventory stats stopped using broken cursor pagination.",
      "Missing labels became a real workflow instead of a placeholder page."
    ],
    details: [
      {
        title: "Admin Pages",
        items: [
          "Inventory cards, product filters, status filters and pagination were aligned with real label source counts.",
          "Carrier check metadata was stored on label sources so operators can audit what was checked and why it was flagged."
        ]
      },
      {
        title: "Data Safety",
        items: [
          "Public updates on sold, assigned or consumed labels were blocked.",
          "Download handling was moved to a same-origin route to avoid redirect or CORS issues."
        ]
      }
    ]
  },
  {
    version: 66,
    date: "2026-03-11",
    title: "Shopify Billing Recovery",
    summary: "Shopify-paid orders can now be recovered even if the webhook flow is missed.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Manual Shopify billing sync was added to admin purchases.",
      "Webhook handling was clarified between early create events and paid events.",
      "Duplicate credits are blocked by Shopify order id."
    ],
    details: [
      {
        title: "Fallbacks",
        items: [
          "Admins can reconcile a specific Shopify billing order or scan the history to recover missed purchases.",
          "Customers returning to the buy-labels flow can now trigger a confirm fallback if the webhook was missed."
        ]
      },
      {
        title: "Auditability",
        items: [
          "Purchases now carry Shopify metadata and can open the source order in Shopify admin.",
          "Credit allocation remains idempotent for the same billing order."
        ]
      }
    ]
  },
  {
    version: 65,
    date: "2026-03-10",
    title: "Purchases Workspace Refresh",
    summary: "Admin purchases was upgraded from a raw table into an operations workspace.",
    confidence: "verified",
    audience: ["admin"],
    highlights: [
      "Customer and type filters were added.",
      "Search and pagination were introduced.",
      "Relation fallbacks for customer, label product and creator were improved."
    ],
    details: [
      {
        title: "Operations",
        items: [
          "The page no longer tries to fetch oversized result sets directly from GraphQL.",
          "Action-backed loading protects the page against GraphQL page-size errors and permission drift."
        ]
      }
    ]
  },
  {
    version: 64,
    date: "2026-03-09",
    title: "Shipment Data Repair Release",
    summary: "Shipment list quality was improved with stronger fallbacks for order, recipient and product data.",
    confidence: "verified",
    audience: ["admin", "customer"],
    highlights: [
      "Customer shipments gained safer relation fallbacks for orphan records.",
      "Product names can now be resolved from cached store order payloads.",
      "Recipient address hover support was added to shipment views."
    ],
    details: [
      {
        title: "Customer Experience",
        items: [
          "Shipment rows now resolve better against order cache data when direct product links are missing.",
          "Address previews were aligned with the orders page behavior."
        ]
      }
    ]
  },
  {
    version: 63,
    date: "2026-03-08",
    title: "Internal Shopify Order Sync",
    summary: "Shopify order sync was moved into the app while keeping S3 order cache compatibility.",
    confidence: "verified",
    audience: ["customer", "system"],
    highlights: [
      "Shopify sync now runs internally instead of through the external sync worker.",
      "Range-based sync was added for one month, three months or full history.",
      "The existing S3 cache contract remained unchanged."
    ],
    details: [
      {
        title: "Store Sync",
        items: [
          "Shopify REST pagination, retries and metadata writing are now handled inside the app.",
          "Customer and store detail screens gained Shopify-specific sync range controls."
        ]
      }
    ]
  },
  {
    version: 62,
    date: "2026-03-06",
    title: "Customer Label Purchase Flow",
    summary: "Label buying and customer balances were aligned with real inventory assignment.",
    confidence: "reconstructed",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Buy labels and credit assignment flows were connected to label source inventory.",
      "Balances now reflect assigned and consumed sources rather than only abstract counters.",
      "Invoice and purchase records were tied more closely to customer activity."
    ],
    details: [
      {
        title: "Commerce",
        items: [
          "Customer purchase records, label balances and allocation history were brought into one coherent flow.",
          "Admin adjustments and unassign flows remained available for corrections."
        ]
      }
    ]
  },
  {
    version: 61,
    date: "2026-03-03",
    title: "Shipment Generation Hardening",
    summary: "Shipment creation and regeneration flows were made more defensive.",
    confidence: "reconstructed",
    audience: ["customer", "system"],
    highlights: [
      "Regeneration keeps the same tracking context while refreshing label artifacts.",
      "Label consumption and shipment updates were made more consistent.",
      "Manual shipment paths were aligned with the standard order-based shipment flow."
    ],
    details: [
      {
        title: "Operational Safety",
        items: [
          "Shipment and label source linking was hardened so re-renders and downloads can find the correct label again.",
          "Recovery paths were improved for manual and regenerated labels."
        ]
      }
    ]
  },
  {
    version: 60,
    date: "2026-02-28",
    title: "Store Order Detail Expansion",
    summary: "Platform-specific order detail pages were expanded across the supported marketplaces.",
    confidence: "reconstructed",
    audience: ["customer"],
    highlights: [
      "Dedicated order detail routes exist for Shopify, Amazon, Billbee, Otto, AboutYou, eBay, JTL and WooCommerce.",
      "Customer order workflows gained platform-specific rendering and shipment actions."
    ],
    details: [
      {
        title: "Marketplace Support",
        items: [
          "The portal now contains route-level handling for each connected platform instead of one generic order detail experience.",
          "This made room for platform-specific fields and shipment actions."
        ]
      }
    ]
  },
  {
    version: 59,
    date: "2026-02-25",
    title: "Admin Customer Operations",
    summary: "Customer detail became the main operational cockpit for label credits and history.",
    confidence: "reconstructed",
    audience: ["admin"],
    highlights: [
      "Customer detail pages gained purchases, shipments, balances and allocation history.",
      "Grant credits, edit prices and operational reviews were consolidated."
    ],
    details: [
      {
        title: "Operations",
        items: [
          "Customer-specific label pricing and allocation tools were brought next to the live account record.",
          "Assigned label stock can be reviewed directly from the customer screen."
        ]
      }
    ]
  },
  {
    version: 58,
    date: "2026-02-21",
    title: "Invoice and PDF Workflow",
    summary: "Invoice generation, numbering and downloadable PDFs became a first-class flow.",
    confidence: "reconstructed",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Invoice generation from store orders was wired into the app.",
      "Invoice number sequencing and PDF retrieval were standardized."
    ],
    details: [
      {
        title: "Billing",
        items: [
          "Customers can now generate and revisit invoices from order detail flows.",
          "Admins gained central invoice visibility for audit purposes."
        ]
      }
    ]
  },
  {
    version: 57,
    date: "2026-02-17",
    title: "Affiliate and Commission Tracking",
    summary: "Referral and commission features were added around invoice activity.",
    confidence: "reconstructed",
    audience: ["customer", "admin"],
    highlights: [
      "Affiliate dashboard and referral signup tracking were added.",
      "Commission events can be reviewed and marked paid from admin."
    ],
    details: [
      {
        title: "Growth",
        items: [
          "Referral code handling and affiliate event review were integrated into the portal.",
          "Commission lifecycle tracking now exists in admin operations."
        ]
      }
    ]
  },
  {
    version: 56,
    date: "2026-02-13",
    title: "Admin Label Product and Pricing Pass",
    summary: "Label products, stock counts and customer-specific pricing became manageable from admin.",
    confidence: "reconstructed",
    audience: ["admin"],
    highlights: [
      "Label product management screens were introduced.",
      "Customer price overrides and stock-aware admin flows were added."
    ],
    details: [
      {
        title: "Pricing",
        items: [
          "Custom customer prices and label product catalog management were added to support operational flexibility.",
          "Stock-aware views were introduced to keep allocation and inventory aligned."
        ]
      }
    ]
  },
  {
    version: 55,
    date: "2026-02-10",
    title: "Platform Connectivity Expansion",
    summary: "Marketplace onboarding routes and credential handling expanded across the supported platforms.",
    confidence: "reconstructed",
    audience: ["customer", "system"],
    highlights: [
      "Store creation routes were added for Shopify, Amazon, Billbee, Otto, AboutYou, eBay, JTL, WooCommerce and manual stores.",
      "Credential and sync metadata patterns were standardized."
    ],
    details: [
      {
        title: "Integrations",
        items: [
          "Store onboarding now follows a platform-specific setup pattern instead of one monolithic form.",
          "Credential fallbacks and sync metadata fields were aligned."
        ]
      }
    ]
  },
  {
    version: 54,
    date: "2026-02-05",
    title: "Customer Portal Core",
    summary: "The customer portal matured into a real daily workspace for stores, orders, shipments and invoices.",
    confidence: "reconstructed",
    audience: ["customer"],
    highlights: [
      "Customer navigation expanded across stores, orders, shipments, invoices and profile.",
      "Label creation, store sync and billing surfaced in one portal."
    ],
    details: [
      {
        title: "Portal Experience",
        items: [
          "Account, sender address and navigation structure were shaped for daily logistics use.",
          "Customer-facing pages were separated from admin operations."
        ]
      }
    ]
  },
  {
    version: 53,
    date: "2026-01-31",
    title: "Admin Command Center",
    summary: "Admin navigation and operational areas were formalized into a central command center layout.",
    confidence: "reconstructed",
    audience: ["admin"],
    highlights: [
      "Dashboard, customers, inventory, purchases, orders, shipments and invoices were grouped under a unified admin shell.",
      "Operational menus and page-level actions became more consistent."
    ],
    details: [
      {
        title: "Operations",
        items: [
          "The admin area evolved from a handful of screens into a broad operations console.",
          "This laid the foundation for later stock, billing and error-management work."
        ]
      }
    ]
  },
  {
    version: 52,
    date: "2026-01-26",
    title: "Authentication and Onboarding",
    summary: "Authentication, sign-up, sign-in and onboarding flows were established.",
    confidence: "reconstructed",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Sign-in, sign-up, password reset and email verification routes were added.",
      "Initial onboarding and profile completion flows appeared."
    ],
    details: [
      {
        title: "Access",
        items: [
          "The app gained its base auth shell and role-aware routing.",
          "Customer onboarding set the stage for sender profile and store setup."
        ]
      }
    ]
  },
  {
    version: 51,
    date: "2026-01-20",
    title: "Project Foundation",
    summary: "The initial app structure, route shells and domain areas were laid down.",
    confidence: "reconstructed",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The React Router, Gadget and UI component structure was established.",
      "Core domains for stores, orders, shipments, invoices, labels and commissions were defined."
    ],
    details: [
      {
        title: "Foundation",
        items: [
          "Shared UI primitives and route structure were introduced.",
          "The codebase layout made room for both customer and admin workflows from the start."
        ]
      }
    ]
  }
];

export const latestRelease = releaseNotes[0];

export function getCurrentProductVersionLabel(): string {
  return `v${latestRelease.version}`;
}
